import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { usePermissions } from "../../context/PermissionsContext";
import { PERMISSIONS } from "../../constants/permissions";
import llmService from "../../services/llmService";
import aiGenerationService from "../../services/aiGenerationService";
import agentService from "../../services/agentService";
import customToolsService from "../../services/customToolsService";
import composioService from "../../services/composioService";
import kgDatasetService from "../../services/kgDatasetService";
import { unwrapList as unwrapKgList } from "../KnowledgeGraph/kgUtils";
import AgentTestPlayground from "../shared/AgentTestPlayground";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  Button,
  Typography,
  IconButton,
  Chip,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
  Tooltip,
  Popover,
  Snackbar,
  Alert,
  AlertTitle,
  CircularProgress,
  Menu,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import {
  Settings,
  AutoAwesome,
  Rocket,
  HelpOutline,
  OpenInFull,
  Code,
  MoreVert,
  Remove,
  Cancel,
  ArrowBack,
  Edit,
  History,
  RestoreRounded,
  PublishRounded,
  UnpublishedRounded,
  Info,
  CalendarMonth,
  Bolt,
} from "@mui/icons-material";
import {
  Book,
  Brain,
  CloudCog,
  Crosshair,
  Database,
  FileText,
  Gauge,
  History as LucideHistory,
  Info as LucideInfo,
  Link2,
  Lock as LucideLock,
  MessageCircleQuestion,
  Mic,
  Quote,
  Radio,
  ScanSearch,
  Search as LucideSearch,
  SearchCheck,
  Settings as LucideSettings,
  Network as LucideNetwork,
  ShieldCheck,
  Shuffle,
  UserRoundCheck,
  UsersRound,
  Zap,
} from "lucide-react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import AddToolDialog from "./AddToolDialog";
import StructuredOutputDialog from "./StructuredOutputDialog";
import KnowledgeBaseDialog from "./KnowledgeBaseDialog";
import KnowledgeBaseD3Dialog from "./KnowledgeBaseD3Dialog";
import KnowledgeGraphDialog from "./KnowledgeGraphDialog";
import MemoryDialog from "./MemoryDialog";
import LiveSourcesDialog from "./LiveSourcesDialog";
import LearningDialog from "./LearningDialog";
import LearningRecordsDialog from "./LearningRecordsDialog";
import ResponsibleAIDialog from "./ResponsibleAIDialog";
// import ContextDialog from "./ContextDialog";
import AgentChainingDialog from "./AgentChainingDialog";
import AgentTeamDialog from "./AgentTeamDialog";
import AgentAPIDialog from "./AgentAPIDialog";
import VersionHistoryPanel from "./VersionHistoryPanel";
import PublishConfirmDialog from "./PublishConfirmDialog";
import AgentConflictDialog, { AgentConflictBanner } from "./AgentConflictDialog";
import UnpublishConfirmDialog from "./UnpublishConfirmDialog";
import PublishAsTemplateConfirmDialog from "./PublishAsTemplateConfirmDialog";
import DeleteTemplateConfirmDialog from "./DeleteTemplateConfirmDialog";
import CreateWithoutKnowledgeConfirmDialog from "./CreateWithoutKnowledgeConfirmDialog";
import Dim1RulebookConfirmDialog from "./Dim1RulebookConfirmDialog";
import ResourceShareDialog from "../shared/ResourceShareDialog";
import ManageSharingDialog from "../shared/ManageSharingDialog";
import { MyDim3Dialog, MyDim3Status } from "../shared/MyDim3";
import resourceSharingService from "../../services/resourceSharingService";
import useMyDim3 from "../../hooks/useMyDim3";
import { SHARED_KB_COPY } from "../../constants/sharedKnowledgeCopy";
import styles from "./AgentCreate.module.scss";

const McpToolDisplay = ({ tool, isDarkMode }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchActions = async () => {
      setLoading(true);
      try {
        const customToolsRaw = await customToolsService.getCustomTools();
        const ownedCustomTools = Array.isArray(customToolsRaw) ? customToolsRaw : (customToolsRaw.tools || []);
        const sharedCustomTools = !Array.isArray(customToolsRaw) && Array.isArray(customToolsRaw.shared)
          ? customToolsRaw.shared.map((item) => ({ ...(item.resource || {}), id: item.resource_id ?? item.resource?.id }))
          : [];
        const customTools = [...ownedCustomTools, ...sharedCustomTools];

        const toolDef = customTools.find(toolDef =>
          toolDef.tool_key === tool.appId ||
          toolDef.name === tool.appId ||
          toolDef.id === tool.appId ||
          toolDef.tool_key === tool.appName ||
          toolDef.name === tool.appName
        );

        if (toolDef && toolDef.id) {
          const fullDef = await customToolsService.getCustomToolById(toolDef.id);

          let resolvedActions = fullDef?.actions || [];
          // If actions were not saved, fetch them dynamically from the MCP server
          if (resolvedActions.length === 0 && fullDef?.api_type === 'MCP') {
            try {
              const payload = {};
              if (fullDef.mcp_config?.transport === 'http') {
                payload.mcp_server_url = fullDef.mcp_server_url;
                if (fullDef.api_key) payload.api_key = fullDef.api_key;
                if (fullDef.mcp_config.http_headers) {
                  payload.mcp_config = { transport: 'http', http_headers: fullDef.mcp_config.http_headers };
                }
              } else if (fullDef.mcp_config?.transport === 'stdio') {
                payload.mcp_config = fullDef.mcp_config;
              }
              const liveTools = await customToolsService.fetchMcpTools(payload);

              if (liveTools && Array.isArray(liveTools)) {
                resolvedActions = liveTools.map(t => {
                  let params = t.parameters || [];
                  if (!Array.isArray(params) && typeof params === 'object' && params.properties) {
                    const properties = params.properties;
                    const requiredList = Array.isArray(params.required) ? params.required : [];
                    params = Object.keys(properties).map(key => ({
                      name: key,
                      type: properties[key].type || 'string',
                      description: properties[key].description || '',
                      required: requiredList.includes(key)
                    }));
                  }
                  return { ...t, parameters: Array.isArray(params) ? params : [] };
                });
              }
            } catch (e) {
              console.error("Dynamic MCP Fetch Failed during render:", e);
            }
          }
          if (isMounted) setActions(resolvedActions);
        } else {
          if (isMounted) setActions([]);
        }
      } catch (err) {
        console.error("Failed fetching MCP Actions", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (tool.appId) fetchActions();
    return () => { isMounted = false; };
  }, [tool.appId, tool.appName]);

  return (
    <Box sx={{ mt: 0.5 }}>
      <Tooltip
        title={
          <Box sx={{ maxHeight: 300, overflowY: 'auto', p: 1 }}>
            {loading ? (
              <Typography variant="body2">Loading tools...</Typography>
            ) : actions.length > 0 ? (
              actions.map((act, i) => (
                <Box key={i} sx={{ mb: 1, pb: 1, borderBottom: i < actions.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{act.name || act.action_name}</Typography>
                  {act.description && <Typography variant="caption" display="block">{act.description}</Typography>}
                  {act.parameters && act.parameters.length > 0 && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Params:{' '}
                      {act.parameters.map((p, pIdx) => (
                        <React.Fragment key={p.name}>
                          {pIdx > 0 && ', '}
                          <Box component="span" fontWeight={p.required ? 'bold' : 'normal'} sx={{ color: p.required ? (isDarkMode ? '#fff' : '#000') : 'inherit' }}>
                            {p.name}
                          </Box>
                        </React.Fragment>
                      ))}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2">No tools connected.</Typography>
            )}
          </Box>
        }
        placement="bottom-start"
        arrow
      >
        <Typography
          variant="caption"
          sx={{
            color: isDarkMode ? "#9ca3af" : "#666",
            fontSize: "0.7rem",
            cursor: 'default',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            lineHeight: 1.2,
          }}
        >
          <Info sx={{ fontSize: "14px" }} />
          {loading ? 'LOADING...' : `${actions.length} TOOLS`}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const DEFAULT_LEARNING_CONFIG = {
  enabled: false,
  mode: "always",
  stores: {
    user_profile: { enabled: true, mode: "always" },
    user_memory: { enabled: true, mode: "always" },
    session_context: { enabled: false, enable_planning: false },
    entity_memory: { enabled: false, mode: "always" },
    learned_knowledge: { enabled: false, mode: "agentic" },
    decision_log: { enabled: false, mode: "agentic" },
  },
};

const LEARNING_MODE_OPTIONS = [
  { value: "always", label: "Always", helper: "Auto-learns after every run" },
  { value: "agentic", label: "Agentic", helper: "Agent chooses when to save learnings" },
  { value: "propose", label: "Propose", helper: "Agent suggests, user approves", disabled: true },
  { value: "hitl", label: "HITL", helper: "Reserved / advanced", disabled: true },
];

/**
 * DIM1 query enhancements. Each one reads the guidance the admins already keep
 * in the agent's Rules book (D1) documents, so all three are gated on a D1
 * folder being attached.
 */
const QUERY_ENHANCEMENT_FEATURES = [
  "queryExpansion",
  "contextualRelevance",
  "qualifyingQuestions",
];

/**
 * Feature switches only the agent owner may flip on a shared agent. The backend
 * refuses the whole save (403) when a recipient changes `knowledge_base`,
 * `knowledge_base_dim3` or their flags.
 */
const OWNER_ONLY_FEATURES = ["knowledgeBase", "knowledgeBaseD3", "knowledgeGraph"];

const MiddleFeatureCard = ({
  icon: Icon,
  title,
  description,
  checked,
  onToggle,
  disabled,
  children,
  hide,
  // Why the switch is disabled, shown on hover.
  switchTooltip,
  // Small icon beside the title (lock / info).
  titleAdornment,
}) => {
  if (hide) return null;
  const toggle = (
    <Switch
      checked={checked}
      onChange={onToggle}
      disabled={disabled}
      size="small"
    />
  );
  return (
    <div className={styles.featureCard}>
      <div className={styles.featureCardTop}>
        <div className={styles.featureCardIcon} aria-hidden>
          <Icon className={styles.featureCardLucide} strokeWidth={2} size={18} />
        </div>
        <div className={styles.featureCardText}>
          <div className={styles.featureCardTitle}>
            {title}
            {titleAdornment}
          </div>
          {description ? (
            <p className={styles.featureCardDesc}>{description}</p>
          ) : null}
        </div>
        {switchTooltip ? (
          // A disabled switch fires no hover events — the span carries the tooltip.
          <Tooltip title={switchTooltip} arrow>
            <span>{toggle}</span>
          </Tooltip>
        ) : (
          toggle
        )}
      </div>
      {children}
    </div>
  );
};

const AGENT_CREATE_COLUMN_KEY = "agentCreateColumnWidths";
const COLUMN_GRIP_PX = 10;
/** Must match `.mainContent` `column-gap` in AgentCreate.module.scss */
const COLUMN_GAP_PX = 10;
/** Desktop grid: merged (config + features) | grip | test panel */
const MAIN_GRID_TRACK_COUNT = 3;
const COLUMN_GAPS_TOTAL_PX = COLUMN_GAP_PX * (MAIN_GRID_TRACK_COUNT - 1);
const COLUMN_MIN_LEFT = 260;
const COLUMN_MIN_MIDDLE = 280;
const COLUMN_MIN_RIGHT = 280;
/** Minimum px for the combined config + features column (old left + gap + middle mins). */
const COLUMN_MIN_MERGED = COLUMN_MIN_LEFT + COLUMN_GAP_PX + COLUMN_MIN_MIDDLE;
const COLUMN_DEFAULT_MERGED =
  360 + COLUMN_GAP_PX + 400;

function readStoredAgentColumnWidths() {
  try {
    const raw = localStorage.getItem(AGENT_CREATE_COLUMN_KEY);
    if (!raw) return { merged: COLUMN_DEFAULT_MERGED };
    const p = JSON.parse(raw);
    if (typeof p.merged === "number") {
      return { merged: Math.max(COLUMN_MIN_MERGED, p.merged) };
    }
    if (typeof p.w1 === "number" && typeof p.w2 === "number") {
      const w1 = Math.max(COLUMN_MIN_LEFT, p.w1);
      const w2 = Math.max(COLUMN_MIN_MIDDLE, p.w2);
      return { merged: Math.max(COLUMN_MIN_MERGED, w1 + COLUMN_GAP_PX + w2) };
    }
  } catch (_) {
    /* ignore */
  }
  return { merged: COLUMN_DEFAULT_MERGED };
}

const MiddleComingSoonCard = ({ icon: Icon, title, description, hide }) => {
  if (hide) return null;
  return (
    <div className={styles.comingSoonCard}>
      <div className={styles.comingSoonLeft}>
        <div className={styles.featureCardIcon} aria-hidden>
          <Icon className={styles.featureCardLucide} strokeWidth={2} size={18} />
        </div>
        <div className={styles.featureCardText}>
          <div className={styles.featureCardTitle}>{title}</div>
          {description ? (
            <p className={styles.featureCardDesc}>{description}</p>
          ) : null}
        </div>
      </div>
      <Chip label="Coming Soon" size="small" color="secondary" />
    </div>
  );
};

// Collapse the persisted tools array into the shape the UI renders.
// Composio rows now bind to a toolkit (no pinned action). Legacy agents may
// have multiple rows for the same toolkit — one per pinned action — so we
// silently dedupe to the unique toolkit set on load. Custom and MCP rows are
// passed through unchanged because they still pin an action.
const collapseToolsForUI = (rawTools) => {
  if (!Array.isArray(rawTools)) return [];
  const seenComposio = new Set();
  const result = [];

  rawTools.forEach((tool, index) => {
    const frontendToolType = tool.tool_type || tool.toolType || 'composio';

    if (frontendToolType === 'composio') {
      const slug =
        (tool.toolkit_slug || tool.toolkitSlug || tool.app_id || tool.appId || tool.app_name || tool.appName || '')
          .toString()
          .toLowerCase();
      if (seenComposio.has(slug)) return;
      seenComposio.add(slug);

      result.push({
        id: Date.now() + index,
        appId: tool.app_id || tool.appId || tool.app_name || tool.appName,
        appName: tool.app_name || tool.appName,
        appLogo: tool.app_logo || tool.appLogo,
        toolkitSlug:
          tool.toolkit_slug ||
          tool.toolkitSlug ||
          (tool.app_name || tool.appName || '').toString().toLowerCase(),
        displayName: tool.app_name || tool.appName,
        toolType: 'composio',
      });
      return;
    }

    const toolData = {
      id: Date.now() + index,
      appId: tool.app_id || tool.appId || tool.app_name || tool.appName,
      appName: tool.app_name || tool.appName,
      actionName: tool.action_name || tool.actionName,
      appLogo: tool.app_logo || tool.appLogo,
      displayName:
        tool.display_name ||
        tool.displayName ||
        `${tool.app_name}_${tool.action_name}`.toUpperCase().replace(/\s+/g, '_'),
      toolType: frontendToolType,
    };

    if (frontendToolType === 'custom' && tool.config) {
      toolData.customToolConfig = tool.config;
      toolData.customToolName = tool.app_name;
    }

    result.push(toolData);
  });

  return result;
};

// Convert a snake_case / dotted field path into a human-readable label.
const humanizeFieldPath = (loc) => {
  if (!Array.isArray(loc) || loc.length === 0) return "";
  const parts = loc[0] === "body" ? loc.slice(1) : loc;
  return parts
    .map((segment) =>
      typeof segment === "string"
        ? segment.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
        : `[${segment}]`
    )
    .join(" → ");
};

const isObjectCoercionMessage = (message) =>
  /^\[object Object\](,\[object Object\])*$/.test(message.trim());

// Extract a user-friendly message from an Axios/FastAPI error.
// Handles raw Axios errors, the app's transformed `error.data` shape,
// { detail: "string" }, { detail: [{ loc, msg, ... }] }, and plain Error.
const extractServerErrorMessage = (error, fallback) => {
  const data = error?.response?.data ?? error?.data;

  if (data) {
    if (typeof data === "string" && data.trim()) return data;

    const detail = Array.isArray(data)
      ? data
      : data.detail ?? data.message ?? data.error;

    if (typeof detail === "string" && detail.trim()) return detail;

    if (Array.isArray(detail) && detail.length > 0) {
      const lines = detail
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") return item;
          const field = humanizeFieldPath(item.loc);
          const msg = item.msg || item.message || item.type || "Invalid value";
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      if (lines.length > 0) return lines.join("\n");
    }

    if (detail && typeof detail === "object") {
      const msg = detail.msg || detail.message;
      if (typeof msg === "string" && msg.trim()) return msg;
    }
  }

  if (
    typeof error?.message === "string" &&
    error.message.trim() &&
    error.message !== "Network Error" &&
    !isObjectCoercionMessage(error.message)
  ) {
    return error.message.trim();
  }

  return fallback;
};

const extractServerErrorStatus = (error) =>
  error?.response?.status ?? error?.status;

/**
 * A recipient's save that would change an owner-only knowledge-base field is
 * refused whole with a 403 whose message names the owner. Any other 403 (a
 * missing permission, say) keeps the backend's own message.
 */
const isOwnerOnlyRefusal = (error) =>
  extractServerErrorStatus(error) === 403 &&
  /\bowner\b/i.test(extractServerErrorMessage(error, ""));

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const readBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return fallback;
};

const readNumber = (value, fallback) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const sortForStableStringify = (value) => {
  if (Array.isArray(value)) return value.map(sortForStableStringify);
  if (!isPlainObject(value)) return value;
  return Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      acc[key] = sortForStableStringify(value[key]);
      return acc;
    }, {});
};

const stableStringify = (value) => JSON.stringify(sortForStableStringify(value));

const readFeatureBoolean = (agentData, snakeKey, camelKey, fallback = false) => {
  const featureMap = agentData?.features || {};
  if (featureMap[snakeKey] !== undefined) {
    return readBoolean(featureMap[snakeKey], fallback);
  }
  if (camelKey && featureMap[camelKey] !== undefined) {
    return readBoolean(featureMap[camelKey], fallback);
  }
  return fallback;
};

/**
 * Read one of the Agno run-behaviour flags off a features map. An absent key
 * means off for all of them except `show_sources`, which the backend treats as
 * on when absent — hence the caller-supplied fallback.
 */
const readRunBehaviourFlag = (featureMap, key, fallback) => {
  if (!featureMap || featureMap[key] === undefined) return fallback;
  return readBoolean(featureMap[key], fallback);
};

/** Persisted `context_providers` → the shape the Live Sources UI edits. */
const normalizeContextProvidersForUI = (rawProviders) => {
  if (!Array.isArray(rawProviders)) return [];

  return rawProviders
    .map((provider, index) => {
      const toolkitSlug = (provider?.toolkit_slug || provider?.toolkitSlug || "").toString().toLowerCase();
      if (!toolkitSlug) return null;

      const appName = provider?.app_name || provider?.appName || toolkitSlug;
      return {
        id: `${toolkitSlug}-${index}`,
        toolkitSlug,
        appName,
        appId: provider?.app_id ?? provider?.appId ?? "",
        name: provider?.name || appName,
        mode: provider?.mode || "agent",
        instructions: provider?.instructions || "",
      };
    })
    .filter(Boolean);
};

/** Comparison key for a live source; order and local ids must not matter. */
const normalizeContextProvidersForCompare = (providers) =>
  (Array.isArray(providers) ? providers : [])
    .map((provider) => ({
      toolkit_slug: (provider?.toolkitSlug || provider?.toolkit_slug || "").toString().toLowerCase(),
      app_name: provider?.appName || provider?.app_name || "",
      app_id: String(provider?.appId ?? provider?.app_id ?? ""),
      name: provider?.name || "",
      mode: provider?.mode || "agent",
      instructions: (provider?.instructions || "").trim(),
    }))
    .filter((provider) => provider.toolkit_slug)
    .sort((a, b) => a.toolkit_slug.localeCompare(b.toolkit_slug) || a.name.localeCompare(b.name));

const resolveOriginalToolsEnabled = (agentData) =>
  readFeatureBoolean(
    agentData,
    "tools",
    "tools",
    Array.isArray(agentData?.tools) && agentData.tools.length > 0
  );

const resolveOriginalLearningEnabled = (agentData) =>
  readFeatureBoolean(
    agentData,
    "learning",
    "learning",
    readBoolean(agentData?.learning_config?.enabled, false)
  );

const resolveOriginalResponsibleAiEnabled = (agentData) => {
  const configEnabled = readBoolean(
    agentData?.responsible_ai_config?.enabled,
    readBoolean(agentData?.responsibleAiConfig?.enabled, false)
  );
  return readFeatureBoolean(agentData, "responsible_ai", "responsibleAi", configEnabled);
};

const normalizeResponsibleAiConfigForCompare = (config, enabled) => {
  const isEnabled = readBoolean(enabled, false);
  if (!isEnabled) return { enabled: false };
  return {
    ...(isPlainObject(config) ? config : {}),
    enabled: true,
  };
};

const normalizeAgentChainingForCompare = (value) => {
  if (!value) return null;
  return {
    selectedAgents: value.selectedAgents || value.selected_agents || [],
    chainingStrategy: value.chainingStrategy || value.chaining_strategy || "sequential",
  };
};

const extractAgentFromSaveResult = (result) => {
  if (isPlainObject(result?.agent)) return result.agent;
  if (isPlainObject(result?.data?.agent)) return result.data.agent;
  if (isPlainObject(result?.data)) return result.data;
  return isPlainObject(result) ? result : {};
};

// ── Conflict-safe saves ────────────────────────────────────────────────────
// Agents can also be changed from AI apps (Platform MCP). Every PUT carries the
// `updated_at` this tab last saw as `expected_updated_at`, and the server
// refuses (409) a save built on an older copy instead of silently undoing the
// other change.

const AGENT_CONFLICT_FALLBACK =
  "This agent was changed after you opened it. Reload it to see the latest version, then save again.";

/** Thrown for saves attempted while a 409 is unresolved — never sent. */
const conflictPausedError = () =>
  Object.assign(new Error(AGENT_CONFLICT_FALLBACK), { status: 409, conflictPaused: true });

const isAgentConflict = (error) => extractServerErrorStatus(error) === 409;

const toCopyText = (value) => {
  if (value == null || value === "") return "";
  return typeof value === "string" ? value : JSON.stringify(value, null, 2);
};

/**
 * What the conflict banner lists, in form order. `text` gives copyable text;
 * sections without it (switches, pickers, dialogs) can only be set again by
 * hand. Keys match `unsavedSections`, which checkForChanges fills in by
 * comparing the form with the copy loaded before the conflict.
 */
const CONFLICT_SECTIONS = [
  { key: "name", label: "Name", text: (s) => s.formData.name },
  { key: "description", label: "Description", text: (s) => s.formData.description },
  { key: "agentRole", label: "Role", text: (s) => s.formData.agentRole },
  { key: "agentGoal", label: "Goal", text: (s) => s.formData.agentGoal },
  { key: "agentInstructions", label: "Instructions", text: (s) => s.formData.agentInstructions },
  { key: "model", label: "Model" },
  { key: "settings", label: "Temperature / Top P" },
  { key: "features", label: "Feature switches" },
  { key: "examples", label: "Examples", text: (s) => (s.examples?.length ? toCopyText(s.examples) : "") },
  { key: "structuredOutput", label: "Structured output", text: (s) => toCopyText(s.structuredOutput) },
  { key: "tools", label: "Tools" },
  { key: "knowledge", label: "Knowledge (DIM1, DIM3, knowledge graph)" },
  { key: "memory", label: "Memory" },
  { key: "agentChaining", label: "Agent chaining" },
  { key: "responsibleAi", label: "Responsible AI" },
  { key: "learning", label: "Learning" },
  { key: "contextProviders", label: "Context providers" },
];

const buildConflictFields = (sections, source) =>
  CONFLICT_SECTIONS.filter((section) => sections?.[section.key]).map((section) => ({
    key: section.key,
    label: section.label,
    value: section.text ? section.text(source) || "" : null,
  }));

/** The folder a dim3 entry points at, as a string (ids arrive as both types). */
const dim3FolderId = (entry) =>
  String(Array.isArray(entry?.knowledgeBase) ? entry.knowledgeBase[0] : '');

/**
 * "No plain D3 folders" and "D3 never configured" are the same thing, and they
 * must compare equal.
 *
 * Attaching a Knowledge Graph dataset writes a `knowledge_base_dim3` block onto
 * the agent (that is how a dataset is attached), but leaves `knowledgeBaseD3Data`
 * null in state because the Customer Data dialog was never opened. Comparing
 * `null` against `{ragType, knowledgeBases: []}` marks the form permanently
 * dirty — "You have unsaved changes" that no amount of saving clears.
 */
const normalizeD3ForCompare = (ragType, entries) => {
  const list = Array.isArray(entries) ? entries : [];
  if (list.length === 0) return null;
  return { ragType: ragType || "semantic", knowledgeBases: list };
};

/**
 * The knowledge-base fields of a shared agent belong to its owner. For anyone
 * else, changing `knowledge_base`, `knowledge_base_dim3` or their feature flags
 * is a 403 that saves nothing, while sending them back unchanged is accepted.
 *
 * The form cannot be trusted to send them back unchanged: formatAgentData
 * rebuilds both blocks from dialog state (Knowledge Graph entries are split out
 * of the dim3 list and merged back with fresh ids, rag_type picks up defaults),
 * so an untouched form can still differ from what the server stored — and
 * auto-save would then fail on every edit. The agent PUT is a partial update,
 * so for a recipient both blocks are left out altogether and the flags go back
 * at the values the GET returned. Their own D3 folders are never part of this
 * call: they are saved separately through `/diy-agents/{id}/my-dim3`.
 */
const pinOwnerKnowledgeBase = (agentData, serverAgent) => {
  if (!isPlainObject(agentData) || !isPlainObject(serverAgent)) return agentData;
  delete agentData.knowledge_base;
  delete agentData.knowledge_base_dim3;
  agentData.features = {
    ...(agentData.features || {}),
    knowledge_base: readFeatureBoolean(serverAgent, "knowledge_base", "knowledgeBase"),
    knowledge_base_d3: readFeatureBoolean(serverAgent, "knowledge_base_d3", "knowledgeBaseD3"),
    // Knowledge Graph datasets are stored in the owner's dim3 list.
    knowledge_graph: readFeatureBoolean(serverAgent, "knowledge_graph", "knowledgeGraph"),
  };
  return agentData;
};

const buildSavedAgentBaseline = (previousAgent, submittedAgent, result) => {
  const previous = isPlainObject(previousAgent) ? previousAgent : {};
  const submitted = isPlainObject(submittedAgent) ? submittedAgent : {};
  const responseAgent = extractAgentFromSaveResult(result);
  const id =
    responseAgent.id ??
    responseAgent.agent_id ??
    previous.id ??
    previous.agent_id ??
    submitted.id ??
    submitted.agent_id;
  const agentId =
    responseAgent.agent_id ??
    responseAgent.id ??
    previous.agent_id ??
    previous.id ??
    submitted.agent_id ??
    submitted.id;

  const baseline = {
    ...previous,
    ...responseAgent,
    ...submitted,
  };

  if (id !== undefined) baseline.id = id;
  if (agentId !== undefined) baseline.agent_id = agentId;
  if (submitted.llm_provider == null) {
    baseline.llm_provider = responseAgent.llm_provider ?? previous.llm_provider ?? submitted.llm_provider;
  }
  if (submitted.llm_model == null) {
    baseline.llm_model = responseAgent.llm_model ?? previous.llm_model ?? submitted.llm_model;
  }
  if (previous.created_at !== undefined) {
    baseline.created_at = responseAgent.created_at ?? previous.created_at;
  }
  if (responseAgent.updated_at !== undefined) baseline.updated_at = responseAgent.updated_at;
  if (typeof responseAgent.is_publish === "boolean") baseline.is_publish = responseAgent.is_publish;
  if (typeof responseAgent.is_published_as_template === "boolean") {
    baseline.is_published_as_template = responseAgent.is_published_as_template;
  }

  return baseline;
};

const AgentCreate = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const [searchParams] = useSearchParams();
  // Share status of the edited agent: 'read' | 'write' | null (= own agent).
  // Seeded from the URL so shared agents open locked without a flash, then
  // re-resolved from shared-with-me (server truth) — deep links skip the
  // Agents-list gating and URL params can be stale or hand-edited.
  const [sharedAccess, setSharedAccess] = useState(() =>
    searchParams.get('shared') === 'true'
      ? (searchParams.get('permission') === 'write' ? 'write' : 'read')
      : null
  );
  const [sharedOwner, setSharedOwner] = useState(null);
  const isSharedResource = sharedAccess !== null;
  // Read share = view-only: the page stays browsable, but nothing can be saved.
  const isSharedReadOnly = sharedAccess === 'read';
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  // KG is not in the default member role — hide the feature card entirely rather
  // than offering a toggle that leads to an empty dialog.
  const canUseKnowledgeGraph = permissionsReady && has(PERMISSIONS.KNOWLEDGE_GRAPH);
  const isThreeColumnLayout = useMediaQuery("(min-width: 1025px)");
  const mainContentRef = useRef(null);
  const columnDragRef = useRef(null);
  const columnDragStartXRef = useRef(0);
  const columnDragStartMergedRef = useRef(0);
  const [columnWidths, setColumnWidths] = useState(readStoredAgentColumnWidths);
  const [resourceShareOpen, setResourceShareOpen] = useState(false);
  const [manageResourceSharingOpen, setManageResourceSharingOpen] = useState(false);

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          /* Don’t override root/thumb dimensions or translateX — it breaks vertical thumb alignment vs track */
          switchBase: {
            '&.Mui-checked': {
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: '#e36250',
                opacity: 1,
              },
            },
          },
          track: {
            opacity: 1,
          },
          thumb: {
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.18)',
          },
        },
      },
    },
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(false);
  const [agentLoaded, setAgentLoaded] = useState(false);
  const [originalAgentData, setOriginalAgentData] = useState(null);
  // A recipient's own Customer Data (D3) folders — the agent's own list stays
  // the owner's and is never searched for them. Shared with the test panel so
  // the card here and the chat show one state.
  const myDim3 = useMyDim3(editingAgentId, { enabled: isSharedResource });
  const [hasChanges, setHasChanges] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [justAutoSaved, setJustAutoSaved] = useState(false);
  const autoSaveInProgress = useRef(false);
  const [disableChangeDetection, setDisableChangeDetection] = useState(false);
  const fieldValueOnFocus = useRef(null);
  // See "Conflict-safe saves" above. The version and the pause live in refs so
  // queued saves read the latest value when they run, not when they were built.
  const agentVersionRef = useRef(null);
  const agentWriteQueueRef = useRef(Promise.resolve());
  const conflictRef = useRef(false);
  // null | { phase: 'paused' | 'reloaded', dialogOpen, message, fields }
  const [saveConflict, setSaveConflict] = useState(null);
  const [conflictReloading, setConflictReloading] = useState(false);
  // Which sections differ from the loaded copy — kept by checkForChanges.
  const [unsavedSections, setUnsavedSections] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    llmProvider: "",
    llmModel: "",
    agentRole: "",
    agentGoal: "",
    agentInstructions: "",
  });

  // Variables extracted from agent instructions
  const [extractedVariables, setExtractedVariables] = useState([]);

  // Helper component for detailed tooltips
  const InfoTooltip = ({ title, content, children, placement = "top", maxWidth = 1500 }) => (
    <Tooltip
      title={
        <Box sx={{
          width: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
          p: 1,
          boxSizing: "border-box",
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
        }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 1, color: "white" }}
          >
            {title}
          </Typography>
          {typeof content === "string" ? (
            <Typography variant="body2" sx={{
              color: "rgba(255,255,255,0.9)",
              wordWrap: "break-word",
              overflowWrap: "break-word"
            }}>
              {content}
            </Typography>
          ) : (
            content
          )}
        </Box>
      }
      placement={placement}
      arrow
      enterDelay={300}
      leaveDelay={200}
      sx={{
        "& .MuiTooltip-tooltip": {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          fontSize: "0.875rem",
          maxWidth: `${maxWidth}px !important`,
          padding: "12px 16px",
          wordWrap: "break-word",
          overflowWrap: "break-word",
          whiteSpace: "normal",
          boxSizing: "border-box",
        },
        "& .MuiTooltip-arrow": {
          color: "rgba(0, 0, 0, 0.9)",
        },
      }}
    >
      {children}
    </Tooltip>
  );

  const normalizeLearningConfig = useCallback((config, learningEnabled) => {
    const merged = {
      ...DEFAULT_LEARNING_CONFIG,
      ...(config || {}),
    };

    merged.stores = {
      user_profile: {
        ...DEFAULT_LEARNING_CONFIG.stores.user_profile,
        ...(config?.stores?.user_profile || {}),
      },
      user_memory: {
        ...DEFAULT_LEARNING_CONFIG.stores.user_memory,
        ...(config?.stores?.user_memory || {}),
      },
      session_context: {
        ...DEFAULT_LEARNING_CONFIG.stores.session_context,
        ...(config?.stores?.session_context || {}),
      },
      entity_memory: {
        ...DEFAULT_LEARNING_CONFIG.stores.entity_memory,
        ...(config?.stores?.entity_memory || {}),
      },
      learned_knowledge: {
        ...DEFAULT_LEARNING_CONFIG.stores.learned_knowledge,
        ...(config?.stores?.learned_knowledge || {}),
      },
      decision_log: {
        ...DEFAULT_LEARNING_CONFIG.stores.decision_log,
        ...(config?.stores?.decision_log || {}),
      },
    };

    if (typeof learningEnabled === "boolean") {
      merged.enabled = learningEnabled;
    }

    return merged;
  }, []);

  // API data state
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState({
    providers: false,
    models: false
  });
  const [error, setError] = useState({
    providers: null,
    models: null
  });

  // Features state - using toggle switches
  const [features, setFeatures] = useState({
    knowledgeBase: false,
    knowledgeBaseD3: false,
    // Faceted KG datasets — exact filters and counts. Off by default.
    knowledgeGraph: false,
    memory: false,
    context: false,
    agentChaining: false,
    humanInLoop: false,
    backgroundRun: false,
    tools: false,
    learning: false,
    // Default OFF until we know the agent config.
    // We'll enable by default only for *new* agent creation (see useEffect below).
    responsibleAi: false,

    // Run-behaviour toggles — all default OFF for a brand-new agent. Note this
    // differs from the backend, which treats an absent `show_sources` as on;
    // agents saved before citations shipped keep citing (see the edit-mode load).
    showSources: false,
    followups: false,
    promptCaching: false,
    compressToolResults: false,
    modelFallback: false,
    checkpointing: false,
    liveSources: false,

    // DIM1 query enhancements. Independent of one another, all off by default,
    // and each needs an attached Rules book (D1) folder to have anything to
    // read. The guidance itself lives in the DIM1 documents — there is no rule
    // editor here.
    queryExpansion: false,
    contextualRelevance: false,
    qualifyingQuestions: false,
  });

  // Live sources: apps the agent looks things up in at run time. Saved as
  // `context_providers` alongside features.live_sources.
  const [contextProviders, setContextProviders] = useState([]);
  const [liveSourcesDialogOpen, setLiveSourcesDialogOpen] = useState(false);

  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  /** 0 = Configuration, 1 = Core features (merged first column) */
  const [mergedColumnTab, setMergedColumnTab] = useState(0);

  const matchesFeatureSearch = useCallback((blob) => {
    const q = featureSearchQuery.trim().toLowerCase();
    if (!q) return true;
    return blob.toLowerCase().includes(q);
  }, [featureSearchQuery]);

  useEffect(() => {
    try {
      localStorage.setItem(AGENT_CREATE_COLUMN_KEY, JSON.stringify(columnWidths));
    } catch (_) {
      /* ignore */
    }
  }, [columnWidths]);

  const fitColumnWidthsToContainer = useCallback(() => {
    const el = mainContentRef.current;
    if (!el || !isThreeColumnLayout) return;
    const W = el.getBoundingClientRect().width;
    if (W < 200) return;
    const grips = COLUMN_GRIP_PX;
    const maxMerged = W - grips - COLUMN_MIN_RIGHT - COLUMN_GAPS_TOTAL_PX;
    if (maxMerged < COLUMN_MIN_MERGED) return;
    setColumnWidths((prev) => {
      let merged = Math.max(COLUMN_MIN_MERGED, prev.merged);
      merged = Math.min(merged, maxMerged);
      if (Math.abs(prev.merged - merged) < 1) return prev;
      return { merged };
    });
  }, [isThreeColumnLayout]);

  useEffect(() => {
    if (!isThreeColumnLayout) return undefined;
    const el = mainContentRef.current;
    if (typeof window === "undefined" || !el) return undefined;
    const t = window.requestAnimationFrame(() => fitColumnWidthsToContainer());
    const ro = new ResizeObserver(() => fitColumnWidthsToContainer());
    ro.observe(el);
    return () => {
      window.cancelAnimationFrame(t);
      ro.disconnect();
    };
  }, [isThreeColumnLayout, fitColumnWidthsToContainer]);

  useEffect(() => {
    const onMove = (e) => {
      const whichMove = columnDragRef.current;
      if (!whichMove || !mainContentRef.current) return;
      const total = mainContentRef.current.getBoundingClientRect().width;
      const grips = COLUMN_GRIP_PX;
      const delta = e.clientX - columnDragStartXRef.current;
      setColumnWidths((prev) => {
        if (whichMove === "merged") {
          const maxMerged = total - grips - COLUMN_MIN_RIGHT - COLUMN_GAPS_TOTAL_PX;
          const merged = Math.round(
            Math.min(
              Math.max(COLUMN_MIN_MERGED, columnDragStartMergedRef.current + delta),
              Math.max(COLUMN_MIN_MERGED, maxMerged)
            )
          );
          return { ...prev, merged };
        }
        return prev;
      });
    };
    const onUp = () => {
      if (!columnDragRef.current) return;
      columnDragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.requestAnimationFrame(() => fitColumnWidthsToContainer());
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [fitColumnWidthsToContainer]);

  const onColumnGripPointerDown = useCallback(
    (which) => (e) => {
      if (!isThreeColumnLayout) return;
      e.preventDefault();
      e.stopPropagation();
      columnDragRef.current = which;
      columnDragStartXRef.current = e.clientX;
      columnDragStartMergedRef.current = columnWidths.merged;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
    },
    [isThreeColumnLayout, columnWidths.merged]
  );

  const [learningConfig, setLearningConfig] = useState(DEFAULT_LEARNING_CONFIG);

  // Responsible AI state
  const [responsibleAiDialogOpen, setResponsibleAiDialogOpen] = useState(false);
  const [responsibleAiConfig, setResponsibleAiConfig] = useState({});
  const didInitResponsibleAiDefault = useRef(false);
  const didInitMemoryDefault = useRef(false);

  // For brand-new agents (create mode), default Responsible AI to ON.
  // For existing agents (edit mode), we always respect what backend returns.
  useEffect(() => {
    if (didInitResponsibleAiDefault.current) return;
    if (!isEditMode && !searchParams.get("id")) {
      setFeatures((prev) => ({ ...prev, responsibleAi: true }));
      didInitResponsibleAiDefault.current = true;
    }
  }, [isEditMode, searchParams]);

  // For brand-new agents (create mode), default Memory to ON with 5 messages.
  // For existing agents (edit mode), we always respect what backend returns.
  useEffect(() => {
    if (didInitMemoryDefault.current) return;
    if (!isEditMode && !searchParams.get("id")) {
      setFeatures((prev) => ({ ...prev, memory: true }));
      setMemoryData({ maxMessages: 5 });
      didInitMemoryDefault.current = true;
    }
  }, [isEditMode, searchParams]);

  // Examples state
  const [examples, setExamples] = useState([]);

  // Structured Output state
  const [structuredOutput, setStructuredOutput] = useState("");

  // Dialog state
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [modelsDialogOpen, setModelsDialogOpen] = useState(false);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [structuredOutputDialogOpen, setStructuredOutputDialogOpen] =
    useState(false);
  const [knowledgeBaseDialogOpen, setKnowledgeBaseDialogOpen] = useState(false);
  const [knowledgeBaseD3DialogOpen, setKnowledgeBaseD3DialogOpen] = useState(false);
  const [knowledgeGraphDialogOpen, setKnowledgeGraphDialogOpen] = useState(false);
  const [memoryDialogOpen, setMemoryDialogOpen] = useState(false);
  const [learningDialogOpen, setLearningDialogOpen] = useState(false);
  const [learningRecordsDialogOpen, setLearningRecordsDialogOpen] = useState(false);
  // const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [agentChainingDialogOpen, setAgentChainingDialogOpen] = useState(false);
  const [agentTeamDialogOpen, setAgentTeamDialogOpen] = useState(false);
  const [agentAPIDialogOpen, setAgentAPIDialogOpen] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [isPublishingAsTemplate, setIsPublishingAsTemplate] = useState(false);
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(0.9);

  // Rules book state
  const [knowledgeBaseData, setKnowledgeBaseData] = useState(null);

  // Customer Data state
  const [knowledgeBaseD3Data, setKnowledgeBaseD3Data] = useState(null);

  // Knowledge Graph state — { datasets: [{ dataset_id, folder_id, key, label }] }.
  // Kept separate from knowledgeBaseD3Data even though both serialise into the
  // dim3 block, so the two dialogs never fight over one array.
  const [knowledgeGraphData, setKnowledgeGraphData] = useState(null);
  // Folder ids owned by Knowledge Graph datasets. Classifying dim3 entries by
  // folder — rather than trusting the `__kg_dataset_id` marker to survive the
  // API round trip — keeps the split correct even if the backend drops unknown
  // keys from the payload.
  const [kgFolderIdSet, setKgFolderIdSet] = useState(() => new Set());

  // Memory state
  const [memoryData, setMemoryData] = useState(null);

  // Context state
  // const [contextData, setContextData] = useState(null);

  // Agent Chaining state
  const [agentChainingData, setAgentChainingData] = useState(null);
  const [chainedAgentVariables, setChainedAgentVariables] = useState([]);

  // Agent Team state (separate API resource; mirrored into features.agent_team)
  const [agentTeamData, setAgentTeamData] = useState(null);
  const [savingAgentTeam, setSavingAgentTeam] = useState(false);

  // Tools state
  const [tools, setTools] = useState([]);
  // Lowercased slugs of currently active Composio connections.
  // Used to detect tools the agent still references but the user has disconnected
  // from the Tools Configure page, and to gate agent testing until re-auth.
  const [connectedToolkitSlugs, setConnectedToolkitSlugs] = useState(new Set());

  // Version History state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersionHistory, setLoadingVersionHistory] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [isViewingVersion, setIsViewingVersion] = useState(false);
  const [viewingVersionInfo, setViewingVersionInfo] = useState(null);

  // AI Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isUnpublishing, setIsUnpublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [publishAsTemplateDialogOpen, setPublishAsTemplateDialogOpen] = useState(false);
  const [deleteTemplateDialogOpen, setDeleteTemplateDialogOpen] = useState(false);
  const [createWithoutKnowledgeDialogOpen, setCreateWithoutKnowledgeDialogOpen] = useState(false);
  // The enhancement waiting on the "D1 will stop answering" confirmation.
  const [pendingRulebookFeature, setPendingRulebookFeature] = useState(null);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Fetch providers on component mount
  useEffect(() => {
    const fetchProviders = async () => {
      console.log('Starting to fetch providers...');
      setLoading(prev => ({ ...prev, providers: true }));
      setError(prev => ({ ...prev, providers: null }));

      try {
        const data = await llmService.getProviders();
        console.log('Providers fetched successfully:', data);
        // Ensure data is an array
        const providersArray = Array.isArray(data) ? data : [];
        setProviders(providersArray);

        // No auto-selection of provider - let user choose
      } catch (err) {
        setError(prev => ({ ...prev, providers: err.message }));
        console.error('Error fetching providers:', err);
        setProviders([]); // Ensure providers is always an array
      } finally {
        console.log('Setting providers loading to false');
        setLoading(prev => ({ ...prev, providers: false }));
      }
    };

    fetchProviders();
  }, []);

  // Fetch models when provider changes
  useEffect(() => {
    if (formData.llmProvider) {
      // Set loading state immediately
      setLoading(prev => ({ ...prev, models: true }));
      setError(prev => ({ ...prev, models: null }));

      const fetchModels = async () => {
        console.log('Starting to fetch models for provider:', formData.llmProvider);

        try {
          const data = await llmService.getModelsByProvider(formData.llmProvider);
          console.log('Models fetched successfully:', data);
          // Ensure data is an array
          const modelsArray = Array.isArray(data) ? data : [];
          setModels(modelsArray);

          // No auto-selection of model - let user choose
        } catch (err) {
          setError(prev => ({ ...prev, models: err.message }));
          console.error('Error fetching models:', err);
          setModels([]); // Ensure models is always an array
        } finally {
          console.log('Setting models loading to false');
          setLoading(prev => ({ ...prev, models: false }));
        }
      };

      fetchModels();
    } else {
      // Clear models and reset selection when no provider is selected
      setModels([]);
      setFormData(prev => ({ ...prev, llmModel: "" }));
      setLoading(prev => ({ ...prev, models: false }));
      setError(prev => ({ ...prev, models: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.llmProvider, editingAgentId]); // Intentionally excluding formData.llmModel to prevent API calls when selecting models

  // Toast handlers
  const showToast = useCallback((message, severity = 'info') => {
    setToast({
      open: true,
      message,
      severity
    });
  }, []);

  // Refresh the set of currently authenticated Composio toolkits. Called on mount,
  // when tools change, and after returning from the Tools Configure page (window focus).
  const refreshConnectedToolkits = useCallback(async () => {
    try {
      const response = await composioService.getConnectedApps();
      const connections = response?.connections || response?.connected_apps || [];
      const slugs = new Set();
      connections.forEach((conn) => {
        const status = conn.status || conn.connection_status;
        if (status !== 'active' && status !== 'connected') return;
        const slug = (conn.toolkit_slug || conn.app_name || conn.name || conn.app_id || '')
          .toString()
          .toLowerCase();
        if (slug) slugs.add(slug);
      });
      setConnectedToolkitSlugs(slugs);
    } catch (err) {
      console.error('Failed to refresh connected toolkits:', err);
    }
  }, []);

  useEffect(() => {
    refreshConnectedToolkits();
  }, [refreshConnectedToolkits]);

  // Re-check on tab focus so disconnections/reconnections done in another tab
  // (e.g. Tools Configure opened via the Auth tool link) are reflected here.
  useEffect(() => {
    const onFocus = () => refreshConnectedToolkits();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshConnectedToolkits]);

  // Composio tools the agent references but the user is no longer authenticated for.
  const unauthenticatedComposioTools = useMemo(() => {
    return tools.filter((tool) => {
      if (tool.toolType !== 'composio') return false;
      const slug = (tool.toolkitSlug || tool.appName || '').toString().toLowerCase();
      return slug && !connectedToolkitSlugs.has(slug);
    });
  }, [tools, connectedToolkitSlugs]);

  // prompt_caching only does anything on Anthropic; the backend ignores it
  // elsewhere, so the card is hidden rather than offered as a no-op.
  const isAnthropicProvider = useMemo(() => {
    const provider = (providers || []).find((p) => p.id === formData.llmProvider);
    if (!provider) return false;
    const haystack = `${provider.type || ''} ${provider.name || ''} ${provider.display_name || ''}`.toLowerCase();
    return haystack.includes('anthropic') || haystack.includes('claude');
  }, [providers, formData.llmProvider]);

  // Chains and teams ignore context_providers, so Live Sources is single-agent only.
  const isSingleAgent =
    !agentTeamData && !(features.agentChaining && agentChainingData);

  // DIM1 guidance is the only source the query enhancements read — D3 is not
  // one, so a KG agent still needs a Rules book (D1) folder attached to use them.
  const hasDim1Attached =
    Boolean(features.knowledgeBase) && (knowledgeBaseData?.knowledgeBases?.length || 0) > 0;

  const hasQueryEnhancementEnabled =
    Boolean(features.queryExpansion) ||
    Boolean(features.contextualRelevance) ||
    Boolean(features.qualifyingQuestions);

  // Any one enhancement turns D1 into a rulebook on the backend: it steers the
  // search but is never quoted or cited, so answers must come from D3.
  const isDim1Rulebook = hasDim1Attached && hasQueryEnhancementEnabled;

  // What a rulebook-mode agent can still answer from: D3 folders or KG datasets.
  const hasDim3AnswerSource =
    (Boolean(features.knowledgeBaseD3) && (knowledgeBaseD3Data?.knowledgeBases?.length || 0) > 0) ||
    (Boolean(features.knowledgeGraph) && (knowledgeGraphData?.datasets?.length || 0) > 0);

  // Citations are only meaningful when the agent has documents to cite.
  const hasKnowledgeBaseAttached =
    (Boolean(features.knowledgeBase) && (knowledgeBaseData?.knowledgeBases?.length || 0) > 0) ||
    (Boolean(features.knowledgeBaseD3) && (knowledgeBaseD3Data?.knowledgeBases?.length || 0) > 0);

  const handleAuthTool = useCallback((tool) => {
    const searchTerm = tool.appName || tool.toolkitSlug || '';
    window.open(
      `/configure/tools?search=${encodeURIComponent(searchTerm)}`,
      '_blank',
      'noopener,noreferrer'
    );
  }, []);

  // Fetch chained agent variables for display (outside the test playground UI)
  const fetchChainedVariables = useCallback(async () => {
    try {
      const selected = agentChainingData?.selectedAgents || agentChainingData?.selected_agents || [];
      if (!Array.isArray(selected) || selected.length === 0) {
        setChainedAgentVariables([]);
        return;
      }

      const agentIds = selected
        .map((agentItem) => {
          const id = agentItem?.id || agentItem?.agent_id || agentItem;
          return typeof id === "string" ? parseInt(id, 10) : id;
        })
        .filter((id) => !Number.isNaN(id) && id !== null && id !== undefined);

      if (agentIds.length === 0) {
        setChainedAgentVariables([]);
        return;
      }

      const response = await agentService.getChainedVariables(agentIds);
      if (response?.success && Array.isArray(response?.all_variables)) {
        setChainedAgentVariables(response.all_variables);
      } else {
        setChainedAgentVariables([]);
      }
    } catch (e) {
      console.error("Error fetching chained variables:", e);
      setChainedAgentVariables([]);
    }
  }, [agentChainingData]);

  useEffect(() => {
    fetchChainedVariables();
  }, [fetchChainedVariables]);

  // Load agent data for editing
  const loadAgentForEdit = useCallback(async (agentId) => {
    setLoadingAgent(true);

    // Disable change detection immediately to prevent false positives
    setDisableChangeDetection(true);
    setHasChanges(false);

    try {
      const agentData = await agentService.getAgent(agentId);
      console.log('Agent data loaded for editing:', agentData);
      agentVersionRef.current = agentData?.updated_at ?? null;

      // Wait for providers to be loaded if they're still loading
      if (loading.providers) {
        console.log('Waiting for providers to load...');
        // Wait for providers to finish loading
        while (loading.providers) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Populate form data
      console.log('Setting form data with LLM provider:', agentData.llm_provider?.id, 'and model:', agentData.llm_model?.id);

      setFormData({
        name: agentData.name || "",
        description: agentData.description || "",
        llmProvider: agentData.llm_provider?.id || "",
        llmModel: agentData.llm_model?.id || "",
        agentRole: agentData.agent_role || "",
        agentGoal: agentData.agent_goal || "",
        agentInstructions: agentData.agent_instructions || "",
      });

      // Set features
      if (agentData.features) {
        const resolvedLearningEnabled =
          typeof agentData.features.learning === "boolean"
            ? agentData.features.learning
            : typeof agentData.learning_config?.enabled === "boolean"
              ? agentData.learning_config.enabled
              : false;

        setFeatures({
          knowledgeBase: typeof agentData.features.knowledge_base === 'boolean' ? agentData.features.knowledge_base : false,
          knowledgeBaseD3: typeof agentData.features.knowledge_base_d3 === 'boolean' ? agentData.features.knowledge_base_d3 : false,
          knowledgeGraph: typeof agentData.features.knowledge_graph === 'boolean' ? agentData.features.knowledge_graph : false,
          memory: typeof agentData.features.memory === 'boolean' ? agentData.features.memory : false,
          context: typeof agentData.features.context === 'boolean' ? agentData.features.context : false,
          agentChaining: typeof agentData.features.agent_chaining === 'boolean' ? agentData.features.agent_chaining : false,
          humanInLoop: typeof agentData.features.human_in_loop === 'boolean' ? agentData.features.human_in_loop : false,
          backgroundRun: typeof agentData.features.background_run === 'boolean' ? agentData.features.background_run : false,
          tools: typeof agentData.features.tools === 'boolean' ? agentData.features.tools : (agentData.tools && Array.isArray(agentData.tools) && agentData.tools.length > 0),
          learning: resolvedLearningEnabled,
          responsibleAi:
            typeof agentData.features.responsible_ai === "boolean"
              ? agentData.features.responsible_ai
              : typeof agentData.features.responsibleAi === "boolean"
                ? agentData.features.responsibleAi
                : typeof agentData.responsible_ai_config?.enabled === "boolean"
                  ? agentData.responsible_ai_config.enabled
                  : typeof agentData.responsibleAiConfig?.enabled === "boolean"
                    ? agentData.responsibleAiConfig.enabled
                    : false,
          // Absent means off for all of these except show_sources, which is on
          // when absent so agents saved before citations shipped still cite.
          showSources: readRunBehaviourFlag(agentData.features, "show_sources", true),
          followups: readRunBehaviourFlag(agentData.features, "followups", false),
          promptCaching: readRunBehaviourFlag(agentData.features, "prompt_caching", false),
          compressToolResults: readRunBehaviourFlag(agentData.features, "compress_tool_results", false),
          modelFallback: readRunBehaviourFlag(agentData.features, "model_fallback", false),
          checkpointing: readRunBehaviourFlag(agentData.features, "checkpointing", false),
          liveSources: readRunBehaviourFlag(agentData.features, "live_sources", false),
          // Absent means off; agents saved before DIM1 enhancements show all three off.
          queryExpansion: readRunBehaviourFlag(agentData.features, "query_expansion", false),
          contextualRelevance: readRunBehaviourFlag(agentData.features, "contextual_relevance", false),
          qualifyingQuestions: readRunBehaviourFlag(agentData.features, "qualifying_questions", false),
        });

        setLearningConfig(
          normalizeLearningConfig(agentData.learning_config, resolvedLearningEnabled)
        );
      } else {
        // If features object doesn't exist, still check tools array
        const resolvedLearningEnabled =
          typeof agentData.learning_config?.enabled === "boolean"
            ? agentData.learning_config.enabled
            : false;
        setFeatures(prev => ({
          ...prev,
          tools: agentData.tools && Array.isArray(agentData.tools) && agentData.tools.length > 0,
          learning: resolvedLearningEnabled,
          // No features map at all means this agent predates the run-behaviour
          // flags, and the backend cites when `show_sources` is absent.
          showSources: true,
        }));
        setLearningConfig(
          normalizeLearningConfig(agentData.learning_config, resolvedLearningEnabled)
        );
      }

      // Set Responsible AI config
      if (agentData.responsible_ai_config && typeof agentData.responsible_ai_config === "object") {
        setResponsibleAiConfig(agentData.responsible_ai_config);
      } else if (agentData.responsibleAiConfig && typeof agentData.responsibleAiConfig === "object") {
        setResponsibleAiConfig(agentData.responsibleAiConfig);
      } else {
        setResponsibleAiConfig({});
      }

      // Set other configurations
      if (agentData.temperature !== undefined) {
        setTemperature(agentData.temperature);
      }
      if (agentData.top_p !== undefined) {
        setTopP(agentData.top_p);
      }

      // Set knowledge base data
      if (agentData.knowledge_base) {
        setKnowledgeBaseData({
          ragType: agentData.knowledge_base.rag_type || "semantic",
          knowledgeBases: agentData.knowledge_base.knowledge_bases || []
        });
      }

      // Set knowledge base (D3) data.
      //
      // The dim3 array is a shared channel: ordinary D3 folders live alongside
      // the folders owned by attached Knowledge Graph datasets. Split them back
      // apart here so each dialog only ever sees its own entries — otherwise the
      // D3 dialog would show KG folders as plain, removable selections.
      if (agentData.knowledge_base_dim3) {
        const allDim3 = agentData.knowledge_base_dim3.knowledge_bases || [];
        const ragType = agentData.knowledge_base_dim3.rag_type || "semantic";

        // Provisional split on the marker alone, so the dialogs are populated
        // before the dataset list comes back.
        const markedKg = allDim3.filter((entry) => entry?.__kg_dataset_id !== undefined);
        setKnowledgeBaseD3Data({
          ragType,
          knowledgeBases: allDim3.filter((entry) => entry?.__kg_dataset_id === undefined),
        });
        if (markedKg.length > 0) {
          setKnowledgeGraphData({
            datasets: markedKg.map((entry) => ({
              dataset_id: entry.__kg_dataset_id,
              folder_id: Array.isArray(entry.knowledgeBase) ? entry.knowledgeBase[0] : null,
              key: '',
              label: '',
            })),
          });
        }

        // Then re-split authoritatively against the live dataset list. Folder
        // ownership — not the marker — is what actually decides: the marker is a
        // client-side convenience and may not survive the API round trip, and a
        // KG folder landing in the D3 dialog would let someone detach a dataset
        // from a dialog that never mentions datasets.
        kgDatasetService
          .listDatasets()
          .then((response) => {
            const datasets = unwrapKgList(response, 'datasets', 'kg_datasets');
            const byFolder = new Map(
              datasets
                .filter((d) => d?.folder_id !== undefined && d?.folder_id !== null)
                .map((d) => [String(d.folder_id), d])
            );
            setKgFolderIdSet(new Set(byFolder.keys()));

            const isKg = (entry) =>
              byFolder.has(dim3FolderId(entry)) || entry?.__kg_dataset_id !== undefined;

            setKnowledgeBaseD3Data({
              ragType,
              knowledgeBases: allDim3.filter((entry) => !isKg(entry)),
            });

            const kgEntries = allDim3.filter(isKg);
            setKnowledgeGraphData(
              kgEntries.length > 0
                ? {
                  datasets: kgEntries
                    .map((entry) => {
                      const match =
                        byFolder.get(dim3FolderId(entry)) ||
                        datasets.find((d) => String(d.id) === String(entry.__kg_dataset_id));
                      return match
                        ? {
                          dataset_id: match.id,
                          folder_id: match.folder_id,
                          key: match.key,
                          label: match.label,
                        }
                        : {
                          dataset_id: entry.__kg_dataset_id,
                          folder_id: Array.isArray(entry.knowledgeBase)
                            ? entry.knowledgeBase[0]
                            : null,
                          key: '',
                          label: '',
                        };
                    })
                    .filter((e) => e.folder_id !== null && e.folder_id !== undefined),
                }
                : null
            );
          })
          // No permission, or the feature is off — the attachment still
          // round-trips unchanged, so loading the agent must not break.
          .catch(() => { });
      }

      // Set memory data
      if (agentData.memory) {
        setMemoryData({
          maxMessages: agentData.memory.max_messages || 10
        });
      }

      // Set agent chaining data
      if (agentData.agent_chaining) {
        // Helper function to parse string arrays (handles both JSON and Python-style strings)
        const parseArrayField = (field) => {
          if (!field) return [];
          if (Array.isArray(field)) return field;
          if (typeof field === 'string') {
            const trimmedField = field.trim();
            if (!trimmedField) return [];
            if (!trimmedField.startsWith('[')) {
              return [trimmedField];
            }
            try {
              // Try parsing as JSON first (handles "['35', '102']" format)
              // Replace single quotes with double quotes for JSON parsing
              const jsonString = field.replace(/'/g, '"');
              const parsed = JSON.parse(jsonString);
              return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
              // If JSON parsing fails, try to extract array-like string
              // Handle format like "['35', '102']"
              const match = field.match(/\[(.*?)\]/);
              if (match) {
                const items = match[1].split(',').map(item => {
                  // Remove quotes and whitespace
                  return item.trim().replace(/^['"]|['"]$/g, '');
                }).filter(item => item.length > 0);
                return items;
              }
              return [];
            }
          }
          return [];
        };

        // Reconstruct connections array from agent data if not present
        let connections = agentData.agent_chaining.connections || [];

        // If connections array is missing, reconstruct from selected_agents
        if (connections.length === 0 && agentData.agent_chaining.selected_agents) {
          const allConnections = [];
          agentData.agent_chaining.selected_agents.forEach(agent => {
            // Extract connections from outgoingConnections (array format)
            if (agent.outgoingConnections && Array.isArray(agent.outgoingConnections)) {
              agent.outgoingConnections.forEach(conn => {
                // Avoid duplicates
                if (!allConnections.find(c => c.id === conn.id)) {
                  allConnections.push({
                    id: conn.id,
                    source: agent.id,
                    target: conn.target,
                    type: conn.type || "smoothstep"
                  });
                }
              });
            }

            // Extract connections from connectsTo (can be string or array)
            const connectsToArray = parseArrayField(agent.connectsTo);
            connectsToArray.forEach(targetId => {
              const connId = `${agent.id}-${targetId}`;
              if (!allConnections.find(c => c.id === connId || (c.source === agent.id && c.target === targetId))) {
                allConnections.push({
                  id: connId,
                  source: agent.id,
                  target: targetId,
                  type: "smoothstep"
                });
              }
            });
          });
          connections = allConnections;
        }

        const chainingData = {
          selectedAgents: agentData.agent_chaining.selected_agents || [],
          chainingStrategy: agentData.agent_chaining.chaining_strategy || "sequential",
          connections: connections
        };
        setAgentChainingData(chainingData);
        // Note: Chained variables will be fetched dynamically via API in useEffect
      }


      // Set examples
      if (agentData.examples) {
        setExamples(agentData.examples);
      }

      // Set structured output
      if (agentData.structured_output) {
        // Ensure structured output is a string
        const output = typeof agentData.structured_output === 'string'
          ? agentData.structured_output
          : JSON.stringify(agentData.structured_output, null, 2);
        setStructuredOutput(output);
      } else {
        // Set to empty string if no structured output
        setStructuredOutput("");
      }

      // Set tools
      // Legacy composio rows (one row per pinned action) are silently collapsed
      // to the unique toolkit set — the agent now picks the right action per request.
      if (agentData.tools && Array.isArray(agentData.tools)) {
        const toolsData = collapseToolsForUI(agentData.tools);
        setTools(toolsData);
        // Update features.tools based on whether tools exist
        setFeatures(prev => ({
          ...prev,
          tools: toolsData.length > 0,
        }));
      } else {
        // No tools, ensure features.tools is false
        setFeatures(prev => ({
          ...prev,
          tools: false,
        }));
      }

      // Live sources. A saved source whose app was later disconnected is kept as
      // is — the agent just emits a tool_warning and reconnecting fixes it.
      setContextProviders(normalizeContextProvidersForUI(agentData.context_providers));

      // Extract variables from agent instructions
      if (agentData.variables) {
        const variablesArray = agentData.variables.split(',').map(v => v.trim()).filter(v => v);
        setExtractedVariables(variablesArray);
      }

      try {
        const teamData = await agentService.getAgentTeamByAgent(agentId);
        setAgentTeamData(teamData || null);
        agentData.agent_team = teamData || null;
        agentData.features = {
          ...(agentData.features || {}),
          agent_team: Boolean(teamData),
        };
      } catch (teamError) {
        const status = teamError?.response?.status || teamError?.status;
        if (status !== 404) {
          console.warn("Agent team detection failed:", teamError);
        }
        setAgentTeamData(null);
        agentData.agent_team = null;
        agentData.features = {
          ...(agentData.features || {}),
          agent_team: false,
        };
      }

      // Store original agent data for comparison
      setOriginalAgentData(agentData);

      // Ensure hasChanges stays false after loading
      // Change detection will be re-enabled after models load (see useEffect below)
      return true;
    } catch (err) {
      console.error('Error loading agent for editing:', err);
      showToast('Failed to load agent data for editing', 'error');
      // Re-enable change detection on error
      setDisableChangeDetection(false);
      return false;
    } finally {
      setLoadingAgent(false);
    }
  }, [showToast, loading.providers, normalizeLearningConfig]);

  // Handle edit mode - check for ID parameter and load agent data
  useEffect(() => {
    const agentId = searchParams.get('id');
    if (agentId && !agentLoaded) {
      setIsEditMode(true);
      setEditingAgentId(agentId);
      setAgentLoaded(true);
      setLoadingAgent(true); // Set loading immediately
      loadAgentForEdit(agentId);
    }
  }, [searchParams, agentLoaded, loadAgentForEdit]);

  // Resolve the true share status of the edited agent from shared-with-me.
  // A 'read' share keeps the page open as a locked view — the update endpoint
  // is owner/write-scoped and would 404 ("DIY Agent with ID … not found"),
  // which reads as a data error, so saving is disabled instead. A 'write'
  // share adopts the shared-edit flow even without ?shared=true in the URL.
  // Server truth overrides the URL hint in both directions.
  useEffect(() => {
    if (!editingAgentId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const result = await resourceSharingService.getSharedWithMe('agent');
        if (cancelled) return;
        const share = (result?.items || []).find(
          (item) => String(item.resource_id) === String(editingAgentId)
        );
        if (!share) {
          // Not shared-with-me ⇒ the user's own agent; clears a stale/edited
          // ?shared URL hint so the owner keeps the full editor.
          setSharedAccess(null);
          setSharedOwner(null);
          return;
        }
        setSharedAccess(share.permission === 'write' ? 'write' : 'read');
        setSharedOwner(share.owner || null);
      } catch (error) {
        // Fail open — the backend enforces access on every call anyway.
      }
    })();
    return () => { cancelled = true; };
  }, [editingAgentId]);

  // Cleanup streams on component unmount
  useEffect(() => {
    return () => {
      // Close all streams when component unmounts
      agentService.closeAllStreams();
    };
  }, []);

  // Re-enable change detection after models have finished loading in edit mode
  useEffect(() => {
    // Only re-enable if we're in edit mode, have loaded the agent, and models are done loading
    if (isEditMode && originalAgentData && !loading.models && !loading.providers && disableChangeDetection && !loadingAgent) {
      // Add a small delay to ensure all state updates have completed
      const timer = setTimeout(() => {
        console.log('Re-enabling change detection after models loaded');
        // Explicitly set hasChanges to false before re-enabling detection
        setHasChanges(false);
        setDisableChangeDetection(false);
      }, 800); // Increased delay to ensure all state updates are complete

      return () => clearTimeout(timer);
    }
  }, [isEditMode, originalAgentData, loading.models, loading.providers, disableChangeDetection, loadingAgent]);

  // Ensure hasChanges is false when originalAgentData is first loaded
  useEffect(() => {
    if (isEditMode && originalAgentData && !hasChanges && disableChangeDetection) {
      console.log('Original agent data loaded, ensuring hasChanges is false');
      setHasChanges(false);
    }
  }, [originalAgentData, isEditMode, hasChanges, disableChangeDetection]);

  // Function to check if there are changes in the form data
  const checkForChanges = useCallback(() => {
    if (!originalAgentData || !isEditMode) {
      setHasChanges(false);
      return;
    }

    // Skip checking if change detection is disabled
    if (disableChangeDetection) {
      console.log('Change detection is disabled, skipping check');
      return;
    }

    // Skip checking if we just auto-saved or auto-save is in progress
    if (justAutoSaved || autoSaveInProgress.current) {
      return;
    }

    // Skip if still loading models or providers
    if (loading.models || loading.providers || loadingAgent) {
      console.log('Still loading data, skipping change detection');
      return;
    }

    console.log('Checking for changes...', {
      formData,
      originalAgentData,
      loadingModels: loading.models,
      loadingProviders: loading.providers
    });

    // Compare form data
    const formDataChanged =
      formData.name !== originalAgentData.name ||
      formData.description !== originalAgentData.description ||
      formData.llmProvider !== originalAgentData.llm_provider?.id ||
      formData.llmModel !== originalAgentData.llm_model?.id ||
      formData.agentRole !== originalAgentData.agent_role ||
      formData.agentGoal !== originalAgentData.agent_goal ||
      formData.agentInstructions !== originalAgentData.agent_instructions;

    // Compare features
    const originalToolsEnabled = resolveOriginalToolsEnabled(originalAgentData);
    const originalLearningEnabled = resolveOriginalLearningEnabled(originalAgentData);
    const originalResponsibleAiEnabled = resolveOriginalResponsibleAiEnabled(originalAgentData);
    const featuresChanged =
      features.knowledgeBase !== readFeatureBoolean(originalAgentData, "knowledge_base", "knowledgeBase") ||
      features.knowledgeBaseD3 !== readFeatureBoolean(originalAgentData, "knowledge_base_d3", "knowledgeBaseD3") ||
      features.knowledgeGraph !== readFeatureBoolean(originalAgentData, "knowledge_graph", "knowledgeGraph") ||
      features.memory !== readFeatureBoolean(originalAgentData, "memory", "memory") ||
      features.context !== readFeatureBoolean(originalAgentData, "context", "context") ||
      features.agentChaining !== readFeatureBoolean(originalAgentData, "agent_chaining", "agentChaining") ||
      features.humanInLoop !== readFeatureBoolean(originalAgentData, "human_in_loop", "humanInLoop") ||
      features.backgroundRun !== readFeatureBoolean(originalAgentData, "background_run", "backgroundRun") ||
      features.tools !== originalToolsEnabled ||
      features.learning !== originalLearningEnabled ||
      features.responsibleAi !== originalResponsibleAiEnabled ||
      features.showSources !== readRunBehaviourFlag(originalAgentData?.features, "show_sources", true) ||
      features.followups !== readRunBehaviourFlag(originalAgentData?.features, "followups", false) ||
      features.promptCaching !== readRunBehaviourFlag(originalAgentData?.features, "prompt_caching", false) ||
      features.compressToolResults !== readRunBehaviourFlag(originalAgentData?.features, "compress_tool_results", false) ||
      features.modelFallback !== readRunBehaviourFlag(originalAgentData?.features, "model_fallback", false) ||
      features.checkpointing !== readRunBehaviourFlag(originalAgentData?.features, "checkpointing", false) ||
      features.liveSources !== readRunBehaviourFlag(originalAgentData?.features, "live_sources", false) ||
      features.queryExpansion !== readRunBehaviourFlag(originalAgentData?.features, "query_expansion", false) ||
      features.contextualRelevance !== readRunBehaviourFlag(originalAgentData?.features, "contextual_relevance", false) ||
      features.qualifyingQuestions !== readRunBehaviourFlag(originalAgentData?.features, "qualifying_questions", false);

    // Compare temperature and topP
    const settingsChanged =
      readNumber(temperature, 0.7) !== readNumber(originalAgentData.temperature, 0.7) ||
      readNumber(topP, 0.9) !== readNumber(originalAgentData.top_p, 0.9);

    // Compare examples
    const examplesChanged = JSON.stringify(examples) !== JSON.stringify(originalAgentData.examples || []);

    // Compare structured output - normalize both values for comparison
    const normalizeStructuredOutput = (value) => {
      if (!value) return "";
      if (typeof value === 'string') return value;
      return JSON.stringify(value, null, 2);
    };

    const currentStructuredOutput = normalizeStructuredOutput(structuredOutput);
    const originalStructuredOutput = normalizeStructuredOutput(originalAgentData.structured_output);
    const structuredOutputChanged = currentStructuredOutput !== originalStructuredOutput;

    // Compare tools — composio rows dedupe by toolkit (action is no longer pinned),
    // so legacy multi-action rows on the original side must collapse the same way.
    const toolKey = (tool, isCurrent) => {
      const toolType = (isCurrent ? tool.toolType : tool.tool_type) || 'composio';
      const appName = isCurrent
        ? (tool.appName || tool.app_name || '')
        : (tool.app_name || tool.appName || '');
      if (toolType === 'composio') {
        const slug =
          (isCurrent ? tool.toolkitSlug : tool.toolkit_slug) ||
          appName.toString().toLowerCase();
        return `composio::${slug}`;
      }
      const actionName = isCurrent
        ? (tool.actionName || tool.action_name || '')
        : (tool.action_name || tool.actionName || '');
      return `${toolType}::${appName}::${actionName}`;
    };

    const currentToolKeys = Array.from(new Set(tools.map(t => toolKey(t, true)))).sort();
    const originalToolKeys = Array.from(
      new Set((originalAgentData.tools || []).map(t => toolKey(t, false)))
    ).sort();

    const toolsChanged = JSON.stringify(currentToolKeys) !== JSON.stringify(originalToolKeys);

    // Compare knowledge base data
    const knowledgeBaseChanged = JSON.stringify(knowledgeBaseData) !== JSON.stringify(
      originalAgentData.knowledge_base ? {
        ragType: originalAgentData.knowledge_base.rag_type || "semantic",
        knowledgeBases: originalAgentData.knowledge_base.knowledge_bases || []
      } : null
    );

    // Compare knowledge base D3 data.
    //
    // The saved dim3 array carries KG-dataset folders as well as plain D3
    // folders, and the two are held in separate state here — so compare against
    // the plain entries only, or every KG attachment would read as an unsaved
    // D3 change and auto-save would never settle.
    const originalDim3Entries = originalAgentData.knowledge_base_dim3?.knowledge_bases || [];
    // Marker OR folder id: the marker is a client-side convenience and may not
    // survive the API round trip, but the folder always identifies the dataset.
    const isKgEntry = (entry) =>
      entry?.__kg_dataset_id !== undefined || kgFolderIdSet.has(dim3FolderId(entry));
    const originalPlainDim3 = originalDim3Entries.filter((entry) => !isKgEntry(entry));
    const originalKgDim3 = originalDim3Entries.filter(isKgEntry);

    // Both sides through the same normaliser, so "null" and "configured but
    // empty" compare equal — see normalizeD3ForCompare.
    const knowledgeBaseD3Changed =
      JSON.stringify(
        normalizeD3ForCompare(knowledgeBaseD3Data?.ragType, knowledgeBaseD3Data?.knowledgeBases)
      ) !==
      JSON.stringify(
        normalizeD3ForCompare(
          originalAgentData.knowledge_base_dim3?.rag_type,
          originalPlainDim3
        )
      );

    // Compare attached Knowledge Graph datasets by the only thing that is saved:
    // which folders are attached. Labels are display-only and refresh from the
    // dataset list, so they must not count as a change.
    const currentKgFolders = stableStringify(
      (knowledgeGraphData?.datasets || [])
        .map((d) => String(d.folder_id))
        .sort()
    );
    const originalKgFolders = stableStringify(originalKgDim3.map(dim3FolderId).sort());
    const knowledgeGraphChanged = currentKgFolders !== originalKgFolders;

    // Compare memory data
    const memoryChanged = JSON.stringify(memoryData) !== JSON.stringify(
      originalAgentData.memory ? {
        maxMessages: originalAgentData.memory.max_messages || 10
      } : null
    );

    // Compare agent chaining data
    const agentChainingChanged =
      stableStringify(normalizeAgentChainingForCompare(agentChainingData)) !==
      stableStringify(normalizeAgentChainingForCompare(originalAgentData.agent_chaining));

    const currentResponsibleAiConfig = normalizeResponsibleAiConfigForCompare(
      responsibleAiConfig,
      features.responsibleAi
    );
    const originalResponsibleAiConfig = normalizeResponsibleAiConfigForCompare(
      originalAgentData.responsible_ai_config || originalAgentData.responsibleAiConfig,
      originalResponsibleAiEnabled
    );
    const responsibleAiConfigChanged =
      stableStringify(currentResponsibleAiConfig) !== stableStringify(originalResponsibleAiConfig);

    const normalizedOriginalLearningConfig = normalizeLearningConfig(
      originalAgentData.learning_config,
      originalLearningEnabled
    );
    const normalizedCurrentLearningConfig = normalizeLearningConfig(
      learningConfig,
      features.learning
    );
    const learningConfigChanged =
      stableStringify(normalizedCurrentLearningConfig) !==
      stableStringify(normalizedOriginalLearningConfig);

    const contextProvidersChanged =
      stableStringify(normalizeContextProvidersForCompare(contextProviders)) !==
      stableStringify(normalizeContextProvidersForCompare(originalAgentData.context_providers));

    const hasAnyChanges = formDataChanged || featuresChanged || settingsChanged ||
      examplesChanged || structuredOutputChanged || toolsChanged ||
      knowledgeBaseChanged || knowledgeBaseD3Changed || knowledgeGraphChanged ||
      memoryChanged || agentChainingChanged ||
      responsibleAiConfigChanged || learningConfigChanged || contextProvidersChanged;

    if (hasAnyChanges) {
      console.log('CHANGES DETECTED!', {
        formDataChanged,
        featuresChanged,
        settingsChanged,
        examplesChanged,
        structuredOutputChanged,
        toolsChanged,
        knowledgeBaseChanged,
        knowledgeBaseD3Changed,
        knowledgeGraphChanged,
        memoryChanged,
        agentChainingChanged,
        responsibleAiConfigChanged,
        learningConfigChanged,
        contextProvidersChanged
      });

      // Debug structured output if it changed
      if (structuredOutputChanged) {
        console.log('Structured Output Comparison:', {
          current: currentStructuredOutput,
          original: originalStructuredOutput,
          currentType: typeof structuredOutput,
          originalType: typeof originalAgentData.structured_output
        });
      }
    }

    const nextSections = {
      name: formData.name !== originalAgentData.name,
      description: formData.description !== originalAgentData.description,
      agentRole: formData.agentRole !== originalAgentData.agent_role,
      agentGoal: formData.agentGoal !== originalAgentData.agent_goal,
      agentInstructions: formData.agentInstructions !== originalAgentData.agent_instructions,
      model:
        formData.llmProvider !== originalAgentData.llm_provider?.id ||
        formData.llmModel !== originalAgentData.llm_model?.id,
      settings: settingsChanged,
      features: featuresChanged,
      examples: examplesChanged,
      structuredOutput: structuredOutputChanged,
      tools: toolsChanged,
      knowledge: knowledgeBaseChanged || knowledgeBaseD3Changed || knowledgeGraphChanged,
      memory: memoryChanged,
      agentChaining: agentChainingChanged,
      responsibleAi: responsibleAiConfigChanged,
      learning: learningConfigChanged,
      contextProviders: contextProvidersChanged,
    };
    setUnsavedSections((prev) =>
      stableStringify(prev) === stableStringify(nextSections) ? prev : nextSections
    );

    setHasChanges(hasAnyChanges);
  }, [
    originalAgentData, isEditMode, formData, features, temperature, topP,
    examples, structuredOutput, tools, knowledgeBaseData, knowledgeBaseD3Data, knowledgeGraphData,
    memoryData, agentChainingData, contextProviders, responsibleAiConfig, learningConfig, kgFolderIdSet, justAutoSaved, disableChangeDetection, loading.models, loading.providers, loadingAgent, normalizeLearningConfig
  ]);


  // Check for changes whenever form data changes
  useEffect(() => {
    // Don't check for changes if we're auto-saving, just finished auto-saving, or change detection is disabled
    if (isAutoSaving || justAutoSaved || disableChangeDetection) {
      return;
    }
    checkForChanges();
  }, [checkForChanges, isAutoSaving, justAutoSaved, disableChangeDetection]);

  // Auto-save function with debouncing
  const openSaveConflict = useCallback((error) => {
    conflictRef.current = true;
    setSaveConflict({
      phase: "paused",
      dialogOpen: true,
      message: extractServerErrorMessage(error, AGENT_CONFLICT_FALLBACK),
    });
  }, []);

  /**
   * Runs agent writes one at a time, in call order. Two overlapping saves would
   * both send the same `expected_updated_at`, and the second would get a 409
   * although nobody else changed the agent.
   */
  const enqueueAgentWrite = useCallback((task) => {
    const run = agentWriteQueueRef.current.then(task);
    agentWriteQueueRef.current = run.catch(() => {});
    return run;
  }, []);

  /** PUT /diy-agents/{id} — the only way this editor saves an existing agent. */
  const putAgent = useCallback(
    (agentId, agentData) =>
      enqueueAgentWrite(async () => {
        if (conflictRef.current) throw conflictPausedError();
        // formatAgentData stamps client-clock timestamps; the server owns them.
        // eslint-disable-next-line no-unused-vars
        const { created_at, updated_at, ...body } = agentData;
        if (agentVersionRef.current) body.expected_updated_at = agentVersionRef.current;
        try {
          const result = await agentService.updateAgent(agentId, body);
          // Taken before the next queued save runs, or that save would 409.
          const savedAt = extractAgentFromSaveResult(result).updated_at;
          if (savedAt) agentVersionRef.current = savedAt;
          return result;
        } catch (error) {
          if (isAgentConflict(error)) openSaveConflict(error);
          throw error;
        }
      }),
    [enqueueAgentWrite, openSaveConflict]
  );

  /**
   * For other calls that change the agent (publish, unpublish): take the new
   * `updated_at` from the response, or re-read the agent when the response
   * doesn't carry it. Queued with the saves so one already waiting doesn't go
   * out with the old value.
   */
  const runAgentWrite = useCallback(
    (agentId, call) =>
      enqueueAgentWrite(async () => {
        const result = await call();
        const fromResponse = extractAgentFromSaveResult(result).updated_at;
        if (fromResponse) {
          agentVersionRef.current = fromResponse;
          return result;
        }
        try {
          const fresh = await agentService.getAgent(agentId);
          if (fresh?.updated_at) agentVersionRef.current = fresh.updated_at;
        } catch (_) {
          // The next save may then 409, which offers a reload — still safe.
        }
        return result;
      }),
    [enqueueAgentWrite]
  );

  const autoSave = useCallback(async () => {
    // sharedAccess 'read' covers the race window between opening a read-only
    // shared agent and the redirect to the playground — never PUT for it.
    if (
      !isEditMode ||
      !editingAgentId ||
      !hasChanges ||
      isAutoSaving ||
      sharedAccess === 'read' ||
      conflictRef.current
    ) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout for debouncing (500ms delay)
    const timeoutId = setTimeout(async () => {
      try {
        // Validate required fields before auto-saving
        if (!formData.name.trim() || !formData.description.trim() ||
          !formData.llmProvider || !formData.llmModel ||
          !formData.agentRole.trim() ||
          !formData.agentInstructions.trim()) {
          console.log("Auto-save skipped: Required fields missing");
          return;
        }

        setIsAutoSaving(true);
        autoSaveInProgress.current = true;

        // Format agent data for API using the same function as manual update
        const agentData = agentService.formatAgentData(
          formData,
          features,
          examples,
          structuredOutput,
          knowledgeBaseData,
          memoryData,
          agentChainingData,
          extractedVariables,
          temperature,
          topP,
          providers,
          models,
          knowledgeBaseD3Data,
          tools,
          responsibleAiConfig,
          agentTeamData,
          contextProviders,
          knowledgeGraphData
        );

        // Explicitly ensure all features are included in auto-save flow
        agentData.features = {
          ...(agentData.features || {}),
          // Explicitly include all features to ensure nothing is missing
          knowledge_base: Boolean(features?.knowledgeBase),
          knowledge_base_d3: Boolean(features?.knowledgeBaseD3),
          knowledge_graph: Boolean(features?.knowledgeGraph),
          memory: Boolean(features?.memory),
          context: Boolean(features?.context),
          agent_chaining: Boolean(features?.agentChaining),
          agent_team: Boolean(agentTeamData),
          human_in_loop: Boolean(features?.humanInLoop),
          background_run: Boolean(features?.backgroundRun),
          tools: Boolean(features?.tools),
          learning: Boolean(features?.learning),
          responsible_ai: Boolean(features?.responsibleAi),
        };
        if (sharedAccess !== null) pinOwnerKnowledgeBase(agentData, originalAgentData);

        const normalizedLearningConfig = normalizeLearningConfig(
          learningConfig,
          features?.learning
        );
        agentData.learning_config = {
          ...normalizedLearningConfig,
          enabled: Boolean(features?.learning) && Boolean(normalizedLearningConfig.enabled),
        };

        console.log("Auto-saving agent with data:", agentData);

        // Call update API
        const result = await putAgent(editingAgentId, agentData);
        console.log("Agent auto-saved successfully:", result);

        // Disable change detection before updating state to prevent false detections
        setDisableChangeDetection(true);
        setJustAutoSaved(true);
        autoSaveInProgress.current = false;

        // Update the original data
        setOriginalAgentData((prev) => buildSavedAgentBaseline(prev, agentData, result));

        // Force hasChanges to false
        setHasChanges(false);
        setLastSaved(new Date());

        // Refresh chained variables if agent chaining is enabled
        if (agentChainingData && agentChainingData.selectedAgents && agentChainingData.selectedAgents.length > 0) {
          console.log('Refreshing chained variables after auto-save');
          fetchChainedVariables();
        }

        // Clear flags after delay to allow change detection to resume
        setTimeout(() => {
          setJustAutoSaved(false);
          // Re-check if there are any new changes after auto-save
          setTimeout(() => {
            setDisableChangeDetection(false);
          }, 100);
        }, 1000);

      } catch (error) {
        console.error('Auto-save error:', error);

        // The conflict dialog already explains it.
        if (isAgentConflict(error)) return;

        // Show toast only for critical errors (not validation errors)
        if (sharedAccess !== null && isOwnerOnlyRefusal(error)) {
          // Saving manually would be refused the same way.
          showToast(SHARED_KB_COPY.ownerOnlyChange, 'error');
        } else if (extractServerErrorStatus(error) !== 422) {
          showToast('Auto-save failed. Please save manually.', 'error');
        }
      } finally {
        setIsAutoSaving(false);
        autoSaveInProgress.current = false;
      }
    }, 500);

    setAutoSaveTimeout(timeoutId);
  }, [isEditMode, editingAgentId, hasChanges, isAutoSaving, sharedAccess, originalAgentData, formData, temperature, topP, features, examples, structuredOutput, tools, knowledgeBaseData, knowledgeBaseD3Data, knowledgeGraphData, memoryData, agentChainingData, contextProviders, responsibleAiConfig, agentTeamData, learningConfig, extractedVariables, providers, models, showToast, autoSaveTimeout, fetchChainedVariables, normalizeLearningConfig, putAgent]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeout) {
        clearTimeout(autoSaveTimeout);
      }
    };
  }, [autoSaveTimeout]);

  // Function to extract variables from text using single curly brackets {variable}
  const extractVariables = (text) => {
    if (!text) return [];

    // Regular expression to match {variable} pattern but NOT {{variable}}
    // First find all {something} patterns, then filter out those that are part of {{something}}
    const allMatches = text.match(/\{[^}]+\}/g) || [];
    const matches = [];

    allMatches.forEach(match => {
      // Check if this match is part of a double curly bracket pattern
      const startIndex = text.indexOf(match);
      const beforeChar = startIndex > 0 ? text[startIndex - 1] : '';
      const afterChar = startIndex + match.length < text.length ? text[startIndex + match.length] : '';

      // Only include if it's not part of {{ or }}
      if (beforeChar !== '{' && afterChar !== '}') {
        const variableName = match.slice(1, -1).trim(); // Remove { and }
        if (variableName && !matches.includes(variableName)) {
          matches.push(variableName);
        }
      }
    });

    return matches;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Extract variables when agent instructions change
    if (field === "agentInstructions") {
      const variables = extractVariables(value);
      setExtractedVariables(variables);
    }
  };

  // Handler for onFocus events to track original value
  const handleFieldFocus = useCallback((field) => {
    // Store the current value when focus enters the field
    fieldValueOnFocus.current = {
      field,
      value: formData[field]
    };
  }, [formData]);

  // Handler for onBlur events to trigger auto-save
  const handleFieldBlur = useCallback((field) => {
    // Only auto-save if the value actually changed
    if (isEditMode && hasChanges) {
      // Check if this specific field changed
      if (fieldValueOnFocus.current &&
        fieldValueOnFocus.current.field === field &&
        fieldValueOnFocus.current.value !== formData[field]) {
        autoSave();
      }
    }
    // Clear the stored value
    fieldValueOnFocus.current = null;
  }, [isEditMode, hasChanges, autoSave, formData]);

  // Auto-hide the saved indicator after 3 seconds
  useEffect(() => {
    if (lastSaved && !isAutoSaving) {
      const timer = setTimeout(() => {
        setLastSaved(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [lastSaved, isAutoSaving]);

  const handleToastClose = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Resize handlers
  // AI Generation function
  const handleGenerateWithAI = async () => {
    // Validate name and description
    if (!formData.name.trim()) {
      showToast('Please enter agent name before generating with AI', 'error');
      return;
    }

    if (!formData.description.trim()) {
      showToast('Please enter agent description before generating with AI', 'error');
      return;
    }

    setIsGenerating(true);

    try {
      const result = await aiGenerationService.generateRoleAndGoal(
        formData.name,
        formData.description
      );

      // Update form data with generated content
      console.log('Updating form with generated content:', result);
      setFormData(prev => {
        const updated = {
          ...prev,
          agentRole: result.role || prev.agentRole,
        };
        console.log('Form data updated:', updated);
        return updated;
      });

      showToast('AI generation completed successfully!', 'success');
    } catch (error) {
      console.error('AI generation error:', error);
      showToast('Failed to generate content. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFeatureToggle = (feature) => {
    // On a shared agent the knowledge-base switches are the owner's. Knowledge
    // Graph is included because its datasets are stored in the D3 list.
    if (isSharedResource && OWNER_ONLY_FEATURES.includes(feature)) {
      showToast(SHARED_KB_COPY.ownerOnlyChange, "info");
      return;
    }

    if (feature === "knowledgeBase") {
      if (!features.knowledgeBase) {
        // If enabling knowledge base, open the dialog
        setKnowledgeBaseDialogOpen(true);
        // Don't toggle yet - wait for dialog save/close
        return;
      } else {
        // If disabling knowledge base, clear the data
        setKnowledgeBaseData(null);
      }
    }

    if (feature === "knowledgeBaseD3") {
      if (!features.knowledgeBaseD3) {
        // If enabling knowledge base D3, open the dialog
        setKnowledgeBaseD3DialogOpen(true);
        // Don't toggle yet - wait for dialog save/close
        return;
      } else {
        // If disabling knowledge base D3, clear the data
        setKnowledgeBaseD3Data(null);
      }
    }

    if (feature === "knowledgeGraph") {
      if (!features.knowledgeGraph) {
        // If enabling knowledge graph, open the dataset picker
        setKnowledgeGraphDialogOpen(true);
        // Don't toggle yet - wait for dialog save/close
        return;
      } else {
        // If disabling, drop the attachments — the folders come off with them
        setKnowledgeGraphData(null);
      }
    }

    if (feature === "memory") {
      if (!features.memory) {
        // If enabling memory, open the dialog
        setMemoryDialogOpen(true);
        // Don't toggle yet - wait for dialog save/close
        return;
      } else {
        // If disabling memory, clear the data
        setMemoryData(null);
        // Checkpointing silently does nothing without memory.
        if (features.checkpointing) {
          setFeatures((prev) => ({ ...prev, checkpointing: false }));
        }
      }
    }

    if (feature === "checkpointing") {
      if (!features.checkpointing && !features.memory) {
        showToast("Checkpointing needs Memory enabled first.", "error");
        return;
      }
    }

    // The enhancements read the agent's DIM1 documents, so there is nothing to
    // enable until a Rules book (D1) folder is attached. Turning one off is
    // always allowed — including after the folder went away.
    if (QUERY_ENHANCEMENT_FEATURES.includes(feature) && !features[feature] && !hasDim1Attached) {
      showToast(
        "Attach a Rules book (D1) folder before enabling this — the guidance comes from those documents.",
        "error"
      );
      return;
    }

    // The first enhancement flips D1 from answer source to rulebook, so confirm
    // that step only — going from one-on to two-on changes nothing.
    if (
      QUERY_ENHANCEMENT_FEATURES.includes(feature) &&
      !features[feature] &&
      !hasQueryEnhancementEnabled
    ) {
      setPendingRulebookFeature(feature);
      return;
    }

    if (feature === "liveSources") {
      if (!features.liveSources) {
        // Pick the connected apps first; the toggle follows the dialog.
        setLiveSourcesDialogOpen(true);
        return;
      }
      setContextProviders([]);
    }

    // if (feature === "context") {
    //   if (!features.context) {
    //     // If enabling context, open the dialog
    //     setContextDialogOpen(true);
    //     // Don't toggle yet - wait for dialog save/close
    //     return;
    //   } else {
    //     // If disabling context, clear the data
    //     setContextData(null);
    //   }
    // }

    if (feature === "agentChaining") {
      if (!features.agentChaining) {
        // If enabling agent chaining, open the dialog
        setAgentChainingDialogOpen(true);
        // Don't toggle yet - wait for dialog save/close
        return;
      } else {
        // If disabling agent chaining, clear the data
        setAgentChainingData(null);
        // Also disable humanInLoop if agent chaining is disabled
        if (features.humanInLoop) {
          setFeatures((prev) => ({
            ...prev,
            humanInLoop: false,
          }));
        }
      }
    }

    if (feature === "agentTeam") {
      if (!editingAgentId) {
        showToast("Save the agent before configuring a team.", "info");
        return;
      }
      if (!agentTeamData) {
        setAgentTeamDialogOpen(true);
        return;
      }
      handleAgentTeamDelete();
      return;
    }

    if (feature === "humanInLoop") {
      if (!features.humanInLoop) {
        // Validate: HIL requires a multi-agent execution mode
        if ((!features.agentChaining || !agentChainingData) && !agentTeamData) {
          showToast("Human-in-Loop requires Agent Chaining or Agent Teams to be configured first.", "error");
          return;
        }
        // Validate: Cannot enable both HIL and Background Run
        if (features.backgroundRun) {
          showToast("Cannot enable both Human-in-Loop and Background Run. Please disable Background Run first.", "error");
          return;
        }
        // Warn: Loop nodes are not supported in human-in-the-loop workflows yet
        const chainNodes =
          agentChainingData?.selectedAgents || agentChainingData?.selected_agents || [];
        const hasLoopNode = chainNodes.some(
          (node) =>
            node?.executionKind === "loop" ||
            node?.execution_kind === "loop" ||
            node?.type === "loop"
        );
        if (hasLoopNode) {
          showToast("Loop nodes are not supported in human-in-the-loop workflows yet. Remove the loop node from the chain before enabling Human-in-Loop.", "error");
          return;
        }
      }
    }

    if (feature === "backgroundRun") {
      if (!features.backgroundRun) {
        // Chains and teams run in the background through their own workflow
        // endpoints; a plain agent needs checkpointing for it.
        if ((!features.agentChaining || !agentChainingData) && !agentTeamData && !features.checkpointing) {
          showToast("Background Run requires Agent Chaining, Agent Teams, or Checkpointing to be enabled first.", "error");
          return;
        }
        // Validate: Cannot enable both Background Run and HIL
        if (features.humanInLoop) {
          showToast("Cannot enable both Background Run and Human-in-Loop. Please disable Human-in-Loop first.", "error");
          return;
        }
      }
    }

    if (feature === "learning") {
      if (!features.learning) {
        const enabledConfig = {
          ...normalizeLearningConfig(learningConfig, true),
          enabled: true,
        };
        setLearningConfig(enabledConfig);
        setFeatures((prev) => ({
          ...prev,
          learning: true,
        }));
        setLearningDialogOpen(true);
        return;
      }
      setLearningConfig((prev) => ({
        ...normalizeLearningConfig(prev, false),
        enabled: false,
      }));
      setFeatures((prev) => ({
        ...prev,
        learning: false,
      }));
      return;
    }

    setFeatures((prev) => ({
      ...prev,
      [feature]: !prev[feature],
    }));
  };

  const handleConfirmDim1Rulebook = () => {
    const feature = pendingRulebookFeature;
    setPendingRulebookFeature(null);
    if (!feature) return;
    setFeatures((prev) => ({ ...prev, [feature]: true }));
  };

  const handleLearningSave = (data) => {
    const enabled = Boolean(features.learning);
    const normalized = {
      ...normalizeLearningConfig(data, enabled),
      enabled,
    };
    setLearningConfig(normalized);
    setFeatures((prev) => ({
      ...prev,
      learning: enabled,
    }));
  };

  const handleLearningClose = () => {
    setLearningDialogOpen(false);
  };

  const handleLearningRecordsOpen = () => {
    if (!editingAgentId) {
      showToast("Please save the agent first to view stored learnings.", "info");
      return;
    }
    setLearningRecordsDialogOpen(true);
  };

  const handleLiveSourcesSave = (nextProviders) => {
    const list = Array.isArray(nextProviders) ? nextProviders : [];
    setContextProviders(list);
    // The toggle only means something with at least one source behind it.
    setFeatures((prev) => ({ ...prev, liveSources: list.length > 0 }));
  };

  const handleLiveSourcesClose = (savedProviders) => {
    setLiveSourcesDialogOpen(false);
    // Cancelled (null) with nothing configured yet — leave the toggle off.
    if (savedProviders === null && contextProviders.length === 0) {
      setFeatures((prev) => ({ ...prev, liveSources: false }));
    }
  };

  const handleKnowledgeBaseSave = (data) => {
    setKnowledgeBaseData(data);
    // Only enable toggle if data is valid (has knowledge bases)
    const isValid =
      data && data.knowledgeBases && data.knowledgeBases.length > 0;
    setFeatures((prev) => ({
      ...prev,
      knowledgeBase: isValid,
    }));
  };

  const handleKnowledgeBaseClose = (data) => {
    setKnowledgeBaseDialogOpen(false);

    setTimeout(() => {
      // Reset knowledge base to false if no valid data was saved
      const hasValidData =
        data && data.knowledgeBases && data.knowledgeBases.length > 0;
      if (!hasValidData) {
        setFeatures((prev) => ({
          ...prev,
          knowledgeBase: false,
        }));
        setKnowledgeBaseData(null);
      }
    }, 1000);
  };

  const handleKnowledgeBaseD3Save = (data) => {
    setKnowledgeBaseD3Data(data);
    // Only enable toggle if data is valid (has knowledge bases)
    const isValid =
      data && data.knowledgeBases && data.knowledgeBases.length > 0;
    setFeatures((prev) => ({
      ...prev,
      knowledgeBaseD3: isValid,
    }));
  };

  const handleKnowledgeBaseD3Close = (data) => {
    setKnowledgeBaseD3DialogOpen(false);

    setTimeout(() => {
      // Reset knowledge base D3 to false if no valid data was saved
      const hasValidData =
        data && data.knowledgeBases && data.knowledgeBases.length > 0;
      if (!hasValidData) {
        setFeatures((prev) => ({
          ...prev,
          knowledgeBaseD3: false,
        }));
        setKnowledgeBaseD3Data(null);
      }
    }, 1000);
  };

  const handleKnowledgeGraphSave = (data) => {
    setKnowledgeGraphData(data);
    // The feature is only meaningfully on once a dataset is actually attached —
    // the flag alone gives the agent nothing.
    const isValid = data && Array.isArray(data.datasets) && data.datasets.length > 0;
    setFeatures((prev) => ({
      ...prev,
      knowledgeGraph: isValid,
    }));
  };

  const handleKnowledgeGraphClose = (data) => {
    setKnowledgeGraphDialogOpen(false);

    setTimeout(() => {
      // Closing without picking anything leaves the toggle off, matching how
      // every other dialog-backed feature behaves here.
      const hasValidData = data && Array.isArray(data.datasets) && data.datasets.length > 0;
      if (!hasValidData) {
        setFeatures((prev) => ({
          ...prev,
          knowledgeGraph: false,
        }));
        setKnowledgeGraphData(null);
      }
    }, 1000);
  };

  const handleMemorySave = (data) => {
    setMemoryData(data);
    // Only enable toggle if data is valid (has maxMessages)
    const isValid = data && data.maxMessages && data.maxMessages > 0;
    setFeatures((prev) => ({
      ...prev,
      memory: isValid,
    }));
  };

  const handleMemoryClose = (data) => {
    setMemoryDialogOpen(false);

    setTimeout(() => {
      // Reset memory to false if no valid data was saved
      const hasValidData = data && data.maxMessages && data.maxMessages > 0;
      if (!hasValidData) {
        setFeatures((prev) => ({
          ...prev,
          memory: false,
        }));
        setMemoryData(null);
      }
    }, 1000);
  };

  // const handleContextSave = (data) => {
  //   setContextData(data);
  //   // Only enable toggle if data is valid (has selectedContext)
  //   const isValid =
  //     data && data.selectedContext && data.selectedContext.trim() !== "";
  //   setFeatures((prev) => ({
  //     ...prev,
  //     context: isValid,
  //   }));
  // };

  // const handleContextClose = (data) => {
  //   setContextDialogOpen(false);

  //   setTimeout(() => {
  //     // Reset context to false if no valid data was saved
  //     const hasValidData =
  //       data && data.selectedContext && data.selectedContext.trim() !== "";
  //     if (!hasValidData) {
  //       setFeatures((prev) => ({
  //         ...prev,
  //         context: false,
  //       }));
  //       setContextData(null);
  //     }
  //   }, 1000);
  // };

  const handleAgentChainingSave = (data) => {
    setAgentChainingData(data);
    // Only enable toggle if data is valid (has selected agents)
    const isValid =
      data && data.selectedAgents && data.selectedAgents.length > 0;
    setFeatures((prev) => ({
      ...prev,
      agentChaining: isValid,
    }));

    // Note: chainedAgentVariables will be fetched automatically via API in the useEffect hook
    // when agentChainingData changes, so we don't need to manually set them here
  };

  const handleAgentChainingClose = (data) => {
    setAgentChainingDialogOpen(false);

    setTimeout(() => {
      // Reset agent chaining to false if no valid data was saved
      const hasValidData =
        data && data.selectedAgents && data.selectedAgents.length > 0;
      if (!hasValidData) {
        setFeatures((prev) => ({
          ...prev,
          agentChaining: false,
        }));
        setAgentChainingData(null);
      }
    }, 1000);
  };

  const syncAgentTeamFeatureFlag = async (nextAgentTeamData) => {
    if (!editingAgentId) return;

    const agentData = agentService.formatAgentData(
      formData,
      features,
      examples,
      structuredOutput,
      knowledgeBaseData,
      memoryData,
      agentChainingData,
      extractedVariables,
      temperature,
      topP,
      providers,
      models,
      knowledgeBaseD3Data,
      tools,
      responsibleAiConfig,
      nextAgentTeamData,
      contextProviders,
      knowledgeGraphData
    );

    agentData.features = {
      ...(agentData.features || {}),
      knowledge_base: Boolean(features?.knowledgeBase),
      knowledge_base_d3: Boolean(features?.knowledgeBaseD3),
      knowledge_graph: Boolean(features?.knowledgeGraph),
      memory: Boolean(features?.memory),
      context: Boolean(features?.context),
      agent_chaining: Boolean(features?.agentChaining),
      agent_team: Boolean(nextAgentTeamData),
      human_in_loop: Boolean(features?.humanInLoop),
      background_run: Boolean(features?.backgroundRun),
      tools: Boolean(features?.tools),
      learning: Boolean(features?.learning),
      responsible_ai: Boolean(features?.responsibleAi),
    };
    if (isSharedResource) pinOwnerKnowledgeBase(agentData, originalAgentData);

    const normalizedLearningConfig = normalizeLearningConfig(
      learningConfig,
      features?.learning
    );
    agentData.learning_config = {
      ...normalizedLearningConfig,
      enabled: Boolean(features?.learning) && Boolean(normalizedLearningConfig.enabled),
    };

    const result = await putAgent(editingAgentId, agentData);
    setOriginalAgentData((prev) => ({
      ...buildSavedAgentBaseline(prev, agentData, result),
      agent_team: nextAgentTeamData || null,
    }));
  };

  const handleAgentTeamSave = async (payload) => {
    if (!editingAgentId) {
      showToast("Save the agent before configuring a team.", "info");
      return;
    }

    setSavingAgentTeam(true);
    try {
      const result = agentTeamData?.id
        ? await agentService.updateAgentTeam(agentTeamData.id, payload)
        : await agentService.createAgentTeam(payload);

      setAgentTeamData(result);
      let didSyncFeatureFlag = true;
      try {
        await syncAgentTeamFeatureFlag(result);
      } catch (syncError) {
        didSyncFeatureFlag = false;
        console.warn("Agent team feature flag sync failed:", syncError);
      }
      setOriginalAgentData((prev) => ({
        ...(prev || {}),
        features: {
          ...(prev?.features || {}),
          agent_team: true,
        },
        agent_team: result,
      }));
      setAgentTeamDialogOpen(false);
      showToast(
        didSyncFeatureFlag
          ? (agentTeamData?.id ? "Agent team updated successfully." : "Agent team created successfully.")
          : "Agent team saved, but the agent list flag could not be synced.",
        didSyncFeatureFlag ? "success" : "error"
      );
    } catch (error) {
      const status = error?.response?.status || error?.status;
      const serverMessage = error?.response?.data?.detail || error?.response?.data?.message || error?.message;
      if (status === 400 && !agentTeamData) {
        try {
          const existingTeam = await agentService.getAgentTeamByAgent(editingAgentId);
          setAgentTeamData(existingTeam);
          setOriginalAgentData((prev) => ({
            ...(prev || {}),
            features: {
              ...(prev?.features || {}),
              agent_team: Boolean(existingTeam),
            },
            agent_team: existingTeam,
          }));
          showToast("This agent already has a team. Loaded the existing team for editing.", "info");
        } catch (_) {
          showToast(serverMessage || "This agent already has a team.", "error");
        }
      } else {
        showToast(serverMessage || "Failed to save agent team.", "error");
      }
    } finally {
      setSavingAgentTeam(false);
    }
  };

  const handleAgentTeamClose = () => {
    setAgentTeamDialogOpen(false);
  };

  const handleAgentTeamDelete = async () => {
    if (!agentTeamData?.id) return;
    const confirmed = window.confirm(
      "Delete this team? Member DIY agents will NOT be deleted - only the team configuration."
    );
    if (!confirmed) return;

    setSavingAgentTeam(true);
    try {
      await agentService.deleteAgentTeam(agentTeamData.id);
      setAgentTeamData(null);
      let didSyncFeatureFlag = true;
      try {
        await syncAgentTeamFeatureFlag(null);
      } catch (syncError) {
        didSyncFeatureFlag = false;
        console.warn("Agent team feature flag sync failed:", syncError);
      }
      setOriginalAgentData((prev) => ({
        ...(prev || {}),
        features: {
          ...(prev?.features || {}),
          agent_team: false,
        },
        agent_team: null,
      }));
      showToast(
        didSyncFeatureFlag
          ? "Agent team deleted."
          : "Agent team deleted, but the agent list flag could not be synced.",
        didSyncFeatureFlag ? "success" : "error"
      );
    } catch (error) {
      showToast(error?.response?.data?.detail || error?.message || "Failed to delete agent team.", "error");
    } finally {
      setSavingAgentTeam(false);
    }
  };

  // Version History handlers
  const handleVersionHistoryToggle = async () => {
    if (!editingAgentId) return;

    if (showVersionHistory) {
      // Close version history panel
      setShowVersionHistory(false);
    } else {
      // Open version history panel and load data
      setShowVersionHistory(true);
      setLoadingVersionHistory(true);

      try {
        const result = await agentService.getAgentHistory(editingAgentId);
        console.log('Version history loaded:', result);
        setVersionHistory(result.history || []);
      } catch (error) {
        console.error('Error loading version history:', error);
        showToast('Failed to load version history', 'error');
        setVersionHistory([]);
      } finally {
        setLoadingVersionHistory(false);
      }
    }
  };

  // Helper function to restore agent data from originalAgentData without API call
  const restoreOriginalAgentData = useCallback(() => {
    if (!originalAgentData) {
      // If originalAgentData is not available, fall back to API call
      if (editingAgentId) {
        loadAgentForEdit(editingAgentId);
      }
      return;
    }

    // Disable change detection temporarily
    setDisableChangeDetection(true);
    setHasChanges(false);

    // Populate form data from originalAgentData
    setFormData({
      name: originalAgentData.name || "",
      description: originalAgentData.description || "",
      llmProvider: originalAgentData.llm_provider?.id || "",
      llmModel: originalAgentData.llm_model?.id || "",
      agentRole: originalAgentData.agent_role || "",
      agentGoal: originalAgentData.agent_goal || "",
      agentInstructions: originalAgentData.agent_instructions || "",
    });

    // Set features
    if (originalAgentData.features) {
      const resolvedLearningEnabled =
        typeof originalAgentData.features.learning === "boolean"
          ? originalAgentData.features.learning
          : typeof originalAgentData.learning_config?.enabled === "boolean"
            ? originalAgentData.learning_config.enabled
            : false;

      setFeatures({
        knowledgeBase: typeof originalAgentData.features.knowledge_base === 'boolean' ? originalAgentData.features.knowledge_base : false,
        knowledgeBaseD3: typeof originalAgentData.features.knowledge_base_d3 === 'boolean' ? originalAgentData.features.knowledge_base_d3 : false,
        knowledgeGraph: typeof originalAgentData.features.knowledge_graph === 'boolean' ? originalAgentData.features.knowledge_graph : false,
        memory: typeof originalAgentData.features.memory === 'boolean' ? originalAgentData.features.memory : false,
        context: typeof originalAgentData.features.context === 'boolean' ? originalAgentData.features.context : false,
        agentChaining: typeof originalAgentData.features.agent_chaining === 'boolean' ? originalAgentData.features.agent_chaining : false,
        humanInLoop: typeof originalAgentData.features.human_in_loop === 'boolean' ? originalAgentData.features.human_in_loop : false,
        backgroundRun: typeof originalAgentData.features.background_run === 'boolean' ? originalAgentData.features.background_run : false,
        tools: typeof originalAgentData.features.tools === 'boolean' ? originalAgentData.features.tools : (originalAgentData.tools && Array.isArray(originalAgentData.tools) && originalAgentData.tools.length > 0),
        learning: resolvedLearningEnabled,
        responsibleAi:
          typeof originalAgentData.features.responsible_ai === "boolean"
            ? originalAgentData.features.responsible_ai
            : typeof originalAgentData.features.responsibleAi === "boolean"
              ? originalAgentData.features.responsibleAi
              : typeof originalAgentData.responsible_ai_config?.enabled === "boolean"
                ? originalAgentData.responsible_ai_config.enabled
                : typeof originalAgentData.responsibleAiConfig?.enabled === "boolean"
                  ? originalAgentData.responsibleAiConfig.enabled
                  : false,
      });

      setLearningConfig(
        normalizeLearningConfig(originalAgentData.learning_config, resolvedLearningEnabled)
      );
    } else {
      // If features object doesn't exist, still check tools array
      const resolvedLearningEnabled =
        typeof originalAgentData.learning_config?.enabled === "boolean"
          ? originalAgentData.learning_config.enabled
          : false;

      setFeatures(prev => ({
        ...prev,
        tools: originalAgentData.tools && Array.isArray(originalAgentData.tools) && originalAgentData.tools.length > 0,
        learning: resolvedLearningEnabled,
      }));

      setLearningConfig(
        normalizeLearningConfig(originalAgentData.learning_config, resolvedLearningEnabled)
      );
    }

    // Restore Responsible AI config
    if (originalAgentData.responsible_ai_config && typeof originalAgentData.responsible_ai_config === "object") {
      setResponsibleAiConfig(originalAgentData.responsible_ai_config);
    } else if (originalAgentData.responsibleAiConfig && typeof originalAgentData.responsibleAiConfig === "object") {
      setResponsibleAiConfig(originalAgentData.responsibleAiConfig);
    } else {
      setResponsibleAiConfig({});
    }

    // Set other configurations
    if (originalAgentData.temperature !== undefined) {
      setTemperature(originalAgentData.temperature);
    }
    if (originalAgentData.top_p !== undefined) {
      setTopP(originalAgentData.top_p);
    }

    // Set knowledge base data
    if (originalAgentData.knowledge_base) {
      setKnowledgeBaseData({
        ragType: originalAgentData.knowledge_base.rag_type || "semantic",
        knowledgeBases: originalAgentData.knowledge_base.knowledge_bases || []
      });
    } else {
      setKnowledgeBaseData(null);
    }

    // Set knowledge base (D3) data
    if (originalAgentData.knowledge_base_dim3) {
      setKnowledgeBaseD3Data({
        ragType: originalAgentData.knowledge_base_dim3.rag_type || "semantic",
        knowledgeBases: originalAgentData.knowledge_base_dim3.knowledge_bases || []
      });
    } else {
      setKnowledgeBaseD3Data(null);
    }

    // Set memory data
    if (originalAgentData.memory) {
      setMemoryData({
        maxMessages: originalAgentData.memory.max_messages || 10
      });
    } else {
      setMemoryData(null);
    }

    // Set agent chaining data
    if (originalAgentData.agent_chaining) {
      // Helper function to parse string arrays (handles both JSON and Python-style strings)
        const parseArrayField = (field) => {
          if (!field) return [];
          if (Array.isArray(field)) return field;
          if (typeof field === 'string') {
            const trimmedField = field.trim();
            if (!trimmedField) return [];
            if (!trimmedField.startsWith('[')) {
              return [trimmedField];
            }
            try {
              // Try parsing as JSON first (handles "['35', '102']" format)
              // Replace single quotes with double quotes for JSON parsing
            const jsonString = field.replace(/'/g, '"');
            const parsed = JSON.parse(jsonString);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            // If JSON parsing fails, try to extract array-like string
            // Handle format like "['35', '102']"
            const match = field.match(/\[(.*?)\]/);
            if (match) {
              const items = match[1].split(',').map(item => {
                // Remove quotes and whitespace
                return item.trim().replace(/^['"]|['"]$/g, '');
              }).filter(item => item.length > 0);
              return items;
            }
            return [];
          }
        }
        return [];
      };

      // Reconstruct connections array from agent data if not present
      let connections = originalAgentData.agent_chaining.connections || [];

      // If connections array is missing, reconstruct from selected_agents
      if (connections.length === 0 && originalAgentData.agent_chaining.selected_agents) {
        const allConnections = [];
        originalAgentData.agent_chaining.selected_agents.forEach(agent => {
          // Extract connections from outgoingConnections (array format)
          if (agent.outgoingConnections && Array.isArray(agent.outgoingConnections)) {
            agent.outgoingConnections.forEach(conn => {
              // Avoid duplicates
              if (!allConnections.find(c => c.id === conn.id)) {
                allConnections.push({
                  id: conn.id,
                  source: agent.id,
                  target: conn.target,
                  type: conn.type || "smoothstep"
                });
              }
            });
          }

          // Extract connections from connectsTo (can be string or array)
          const connectsToArray = parseArrayField(agent.connectsTo);
          connectsToArray.forEach(targetId => {
            const connId = `${agent.id}-${targetId}`;
            if (!allConnections.find(c => c.id === connId || (c.source === agent.id && c.target === targetId))) {
              allConnections.push({
                id: connId,
                source: agent.id,
                target: targetId,
                type: "smoothstep"
              });
            }
          });
        });
        connections = allConnections;
      }

      const chainingData = {
        selectedAgents: originalAgentData.agent_chaining.selected_agents || [],
        chainingStrategy: originalAgentData.agent_chaining.chaining_strategy || "sequential",
        connections: connections
      };
      setAgentChainingData(chainingData);
      // Note: Chained variables will be fetched dynamically via API in useEffect
    } else {
      setAgentChainingData(null);
      setChainedAgentVariables([]);
    }

    // Set examples
    if (originalAgentData.examples) {
      setExamples(originalAgentData.examples);
    } else {
      setExamples([]);
    }

    // Set structured output
    if (originalAgentData.structured_output) {
      const output = typeof originalAgentData.structured_output === 'string'
        ? originalAgentData.structured_output
        : JSON.stringify(originalAgentData.structured_output, null, 2);
      setStructuredOutput(output);
    } else {
      setStructuredOutput("");
    }

    // Set tools (collapse legacy multi-action composio rows to the unique toolkit set)
    if (originalAgentData.tools && Array.isArray(originalAgentData.tools)) {
      const toolsData = collapseToolsForUI(originalAgentData.tools);
      setTools(toolsData);
      // Update features.tools based on whether tools exist
      setFeatures(prev => ({
        ...prev,
        tools: toolsData.length > 0,
      }));
    } else {
      setTools([]);
      // No tools, ensure features.tools is false
      setFeatures(prev => ({
        ...prev,
        tools: false,
      }));
    }

    // Extract variables from agent instructions
    if (originalAgentData.variables) {
      const variablesArray = originalAgentData.variables.split(',').map(v => v.trim()).filter(v => v);
      setExtractedVariables(variablesArray);
    } else {
      setExtractedVariables([]);
    }

    // Re-enable change detection after a short delay
    setTimeout(() => {
      setHasChanges(false);
      setDisableChangeDetection(false);
    }, 100);
  }, [originalAgentData, editingAgentId, loadAgentForEdit, normalizeLearningConfig]);

  const handleVersionHistoryClose = () => {
    // Check if we were viewing a version before resetting state
    const wasViewingVersion = isViewingVersion;

    // Close the panel and reset everything to normal state
    setShowVersionHistory(false);
    setIsViewingVersion(false);
    setViewingVersionInfo(null);
    setSelectedVersion(null);

    // Clear version history cache so it loads fresh data next time
    setVersionHistory([]);

    // Only restore original data if we were viewing a version
    // If user just opened and closed without selecting, no need to restore
    if (wasViewingVersion && originalAgentData) {
      restoreOriginalAgentData();
    }
  };

  const handleVersionSelect = (version) => {
    // All versions in history are previous versions and should be selectable
    setSelectedVersion(version);
    setIsViewingVersion(true);
    setViewingVersionInfo({
      date: version.created_at,
      versionId: version.id
    });

    // Load the version data
    loadVersionData(version);
  };

  const loadVersionData = (version) => {
    // For now, we'll assume the version object contains the full agent data
    // If the API returns minimal data, we might need to fetch full version details
    if (version.agent_data) {
      // Populate form with version data
      setFormData({
        name: version.agent_data.name || "",
        description: version.agent_data.description || "",
        llmProvider: version.agent_data.llm_provider?.id || "",
        llmModel: version.agent_data.llm_model?.id || "",
        agentRole: version.agent_data.agent_role || "",
        agentGoal: version.agent_data.agent_goal || "",
        agentInstructions: version.agent_data.agent_instructions || "",
      });
      // You can load other data like features, tools, etc. if needed
    }
  };

  const handleRestoreVersion = async () => {
    if (!editingAgentId || !selectedVersion) return;

    // A debounced auto-save still holds the form from before the restore;
    // firing after the reload it would save over the restored version.
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);

    try {
      setIsCreatingAgent(true);
      // Queued with the saves, so a PUT already in flight or waiting can't
      // land on top of the restore.
      const result = await enqueueAgentWrite(async () => {
        const res = await agentService.restoreAgentVersion(editingAgentId, selectedVersion.id);
        // Restore returns the agent read back after the save. Taken here, before
        // the next queued write runs, so that write doesn't 409 on its own restore.
        const restoredAt = extractAgentFromSaveResult(res).updated_at;
        if (restoredAt) agentVersionRef.current = restoredAt;
        return res;
      });
      console.log('Version restored:', result);

      showToast('Version restored successfully!', 'success');

      // Still reloaded: the form has to show the restored fields.
      const loaded = await loadAgentForEdit(editingAgentId);
      if (loaded) {
        conflictRef.current = false;
        setSaveConflict(null);
      }

      // Exit viewing mode and reset state
      setIsViewingVersion(false);
      setViewingVersionInfo(null);
      setSelectedVersion(null);
      setShowVersionHistory(false);

      // Clear the version history cache so it loads fresh data next time
      setVersionHistory([]);
    } catch (error) {
      console.error('Error restoring version:', error);
      showToast('Failed to restore version. Please try again.', 'error');
    } finally {
      setIsCreatingAgent(false);
    }
  };

  // Tool handlers
  const handleToolAdd = () => {
    setEditingTool(null);
    setToolDialogOpen(true);
  };

  const handleToolEdit = (tool) => {
    setEditingTool(tool);
    setToolDialogOpen(true);
  };

  const handleToolSave = (toolData) => {
    if (toolData) {
      if (editingTool) {
        // Update existing tool
        setTools(prev => {
          const updated = prev.map(tool =>
            tool.id === editingTool.id ? toolData : tool
          );
          // Update features.tools based on whether tools exist
          setFeatures(prevFeatures => ({
            ...prevFeatures,
            tools: updated.length > 0,
          }));
          return updated;
        });
      } else {
        // Check for duplicate before adding new tool.
        // Composio dedupes by toolkit (action is no longer pinned); custom/MCP by action.
        const newType = toolData.toolType || 'composio';
        const newToolkitKey = (toolData.toolkitSlug || toolData.appName || '').toString().toLowerCase();
        const isDuplicate = tools.some(tool => {
          const existingType = tool.toolType || 'composio';
          if (existingType !== newType) return false;
          if (newType === 'composio') {
            const existingKey = (tool.toolkitSlug || tool.appName || '').toString().toLowerCase();
            return existingKey === newToolkitKey;
          }
          return tool.appName === toolData.appName && tool.actionName === toolData.actionName;
        });

        if (isDuplicate) {
          const msg = newType === 'composio'
            ? `${toolData.appName} is already connected.`
            : `This action (${toolData.appName} - ${toolData.actionName}) is already added. Please select a different action.`;
          showToast(msg, 'error');
          return; // Don't close dialog, let user select different option
        }

        // Add new tool
        setTools(prev => {
          const updated = [...prev, toolData];
          // Update features.tools to true when tool is added
          setFeatures(prevFeatures => ({
            ...prevFeatures,
            tools: true,
          }));
          return updated;
        });
      }
    }
    // Always close dialog and reset editing state
    setToolDialogOpen(false);
    setEditingTool(null);
  };

  const handleToolRemove = (toolId) => {
    setTools(prev => {
      const updated = prev.filter(tool => tool.id !== toolId);
      // Update features.tools based on whether any tools remain
      setFeatures(prevFeatures => ({
        ...prevFeatures,
        tools: updated.length > 0,
      }));
      return updated;
    });
  };


  // Example handlers
  const handleAddExample = () => {
    setExamples((prev) => [...prev, { id: Date.now(), text: "" }]);
  };

  const handleRemoveExample = (id) => {
    setExamples((prev) => prev.filter((example) => example.id !== id));
  };

  const handleExampleChange = (id, value) => {
    setExamples((prev) =>
      prev.map((example) =>
        example.id === id ? { ...example, text: value } : example
      )
    );
  };

  const handleCreateAgent = async () => {
    if (isSharedReadOnly) {
      showToast("This agent is shared with you as read-only — changes can't be saved.", 'warning');
      return;
    }
    if (isEditMode && conflictRef.current) {
      setSaveConflict((c) => (c ? { ...c, dialogOpen: true } : c));
      return;
    }
    try {
      // Validate required fields
      if (!formData.name.trim()) {
        showToast('Please enter agent name', 'error');
        return;
      }

      if (!formData.description.trim()) {
        showToast('Please enter agent description', 'error');
        return;
      }

      if (!formData.llmProvider) {
        showToast('Please select an LLM provider', 'error');
        return;
      }

      if (!formData.llmModel) {
        showToast('Please select an LLM model', 'error');
        return;
      }

      if (!formData.agentRole.trim()) {
        showToast('Please enter agent role', 'error');
        return;
      }

      if (!formData.agentInstructions.trim()) {
        showToast('Please enter agent instructions', 'error');
        return;
      }

      // Show loading state
      setIsCreatingAgent(true);

      // Format agent data for API
      const agentData = agentService.formatAgentData(
        formData,
        features,
        examples,
        structuredOutput,
        knowledgeBaseData,
        memoryData,
        agentChainingData,
        extractedVariables,
        temperature,
        topP,
        providers,
        models,
        knowledgeBaseD3Data,
        tools,
        responsibleAiConfig,
        agentTeamData,
        contextProviders,
        knowledgeGraphData
      );

      // Log the request data
      console.log(isEditMode ? "Updating agent with data:" : "Creating agent with data:", agentData);

      // Make API call - use update or create based on mode
      let result;
      if (isEditMode && editingAgentId) {
        // Explicitly ensure all features are included in update flow
        agentData.features = {
          ...(agentData.features || {}),
          // Explicitly include all features to ensure nothing is missing
          knowledge_base: Boolean(features?.knowledgeBase),
          knowledge_base_d3: Boolean(features?.knowledgeBaseD3),
          knowledge_graph: Boolean(features?.knowledgeGraph),
          memory: Boolean(features?.memory),
          context: Boolean(features?.context),
          agent_chaining: Boolean(features?.agentChaining),
          agent_team: Boolean(agentTeamData),
          human_in_loop: Boolean(features?.humanInLoop),
          background_run: Boolean(features?.backgroundRun),
          tools: Boolean(features?.tools),
          learning: Boolean(features?.learning),
          responsible_ai: Boolean(features?.responsibleAi),
        };
        if (isSharedResource) pinOwnerKnowledgeBase(agentData, originalAgentData);

        const normalizedLearningConfig = normalizeLearningConfig(
          learningConfig,
          features?.learning
        );
        agentData.learning_config = {
          ...normalizedLearningConfig,
          enabled: Boolean(features?.learning) && Boolean(normalizedLearningConfig.enabled),
        };

        result = await putAgent(editingAgentId, agentData);
        console.log("Agent updated successfully:", result);
        showToast('Agent updated successfully!', 'success');


        // Disable change detection temporarily while updating
        setDisableChangeDetection(true);
        setOriginalAgentData((prev) => buildSavedAgentBaseline(prev, agentData, result));
        setHasChanges(false);

        // Refresh chained variables if agent chaining is enabled
        if (agentChainingData && agentChainingData.selectedAgents && agentChainingData.selectedAgents.length > 0) {
          console.log('Refreshing chained variables after manual save');
          fetchChainedVariables();
        }

        // Re-enable change detection after a brief delay
        setTimeout(() => {
          setDisableChangeDetection(false);
        }, 500);
      } else {
        // On create, respect the UI toggle for Responsible AI.
        // When OFF, avoid ambiguity by sending only `{ enabled: false }`.
        // When ON, keep the config and force `enabled: true`.
        const responsibleAiEnabled = Boolean(features?.responsibleAi);
        agentData.features = {
          ...(agentData.features || {}),
          // Explicitly include all features to ensure nothing is missing
          knowledge_base: Boolean(features?.knowledgeBase),
          knowledge_base_d3: Boolean(features?.knowledgeBaseD3),
          knowledge_graph: Boolean(features?.knowledgeGraph),
          memory: Boolean(features?.memory),
          context: Boolean(features?.context),
          agent_chaining: Boolean(features?.agentChaining),
          agent_team: Boolean(agentTeamData),
          human_in_loop: Boolean(features?.humanInLoop),
          background_run: Boolean(features?.backgroundRun),
          tools: Boolean(features?.tools),
          learning: Boolean(features?.learning),
          responsible_ai: responsibleAiEnabled,
        };
        const normalizedLearningConfig = normalizeLearningConfig(
          learningConfig,
          features?.learning
        );
        agentData.learning_config = {
          ...normalizedLearningConfig,
          enabled: Boolean(features?.learning) && Boolean(normalizedLearningConfig.enabled),
        };
        agentData.responsible_ai_config = responsibleAiEnabled
          ? {
            ...(agentData.responsible_ai_config ?? responsibleAiConfig ?? {}),
            enabled: true,
          }
          : { enabled: false };

        result = await agentService.createAgent(agentData);
        console.log("Agent created successfully:", result);
        showToast('Agent created successfully!', 'success');

        // Extract agent ID from response
        const newAgentId = result.id || result.agent_id || result.agent?.id;

        // Switch to edit mode after successful creation
        setIsEditMode(true);
        setEditingAgentId(newAgentId);
        setAgentLoaded(true);

        // Disable change detection temporarily while setting up edit mode
        setDisableChangeDetection(true);
        setOriginalAgentData(buildSavedAgentBaseline(null, agentData, result));
        setHasChanges(false);

        // Update URL to include agent ID for edit mode
        navigate(`/agent-create?id=${newAgentId}`, { replace: true });

        // Re-enable change detection after a brief delay
        setTimeout(() => {
          setDisableChangeDetection(false);
        }, 800);
      }

    } catch (error) {
      console.error(isEditMode ? 'Error updating agent:' : 'Error creating agent:', error);
      // The conflict dialog already explains it.
      if (isEditMode && isAgentConflict(error)) return;
      const fallback = isEditMode
        ? 'Failed to update agent. Please try again.'
        : 'Failed to create agent. Please try again.';
      showToast(
        isSharedResource && isOwnerOnlyRefusal(error)
          ? SHARED_KB_COPY.ownerOnlyChange
          : extractServerErrorMessage(error, fallback),
        'error'
      );
    } finally {
      setIsCreatingAgent(false);
    }
  };

  const handleCreateAgentClick = () => {
    const shouldWarnMissingKnowledge =
      !isEditMode && (!features.knowledgeBase || !features.knowledgeBaseD3);

    if (shouldWarnMissingKnowledge) {
      setCreateWithoutKnowledgeDialogOpen(true);
      return;
    }

    handleCreateAgent();
  };

  const handleCloseCreateWithoutKnowledgeDialog = () => {
    if (isCreatingAgent) return;
    setCreateWithoutKnowledgeDialogOpen(false);
  };

  const handleConfirmCreateWithoutKnowledge = () => {
    setCreateWithoutKnowledgeDialogOpen(false);
    handleCreateAgent();
  };

  const isPublished = !!originalAgentData?.is_publish;
  // Check if agent is published as template using is_published_as_template flag from API
  const isPublishedAsTemplate = !!originalAgentData?.is_published_as_template;
  const learningModeLabel =
    LEARNING_MODE_OPTIONS.find((option) => option.value === learningConfig?.mode)?.label ||
    learningConfig?.mode ||
    "always";
  const learningEnabledStoresCount = Object.values(learningConfig?.stores || {}).filter(
    (store) => store?.enabled
  ).length;

  const handlePublish = async () => {
    if (!isEditMode || !editingAgentId) return;

    try {
      setIsPublishing(true);
      await runAgentWrite(editingAgentId, () => agentService.publishAgent(editingAgentId));
      setOriginalAgentData((prev) => (prev ? { ...prev, is_publish: true } : prev));
      showToast("Agent published successfully!", "success");
    } catch (e) {
      console.error("Publish error:", e);
      showToast("Failed to publish agent. Please try again.", "error");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!isEditMode || !editingAgentId) return;

    try {
      setIsUnpublishing(true);
      await runAgentWrite(editingAgentId, () => agentService.unpublishAgent(editingAgentId));
      setOriginalAgentData((prev) => (prev ? { ...prev, is_publish: false } : prev));
      showToast("Agent unpublished successfully!", "success");
    } catch (e) {
      console.error("Unpublish error:", e);
      showToast("Failed to unpublish agent. Please try again.", "error");
    } finally {
      setIsUnpublishing(false);
    }
  };

  // ── Save conflict (409) ──────────────────────────────────────────────────

  const conflictSource = { formData, examples, structuredOutput };

  const handleConflictKeepEditing = () => {
    setSaveConflict((c) => (c ? { ...c, dialogOpen: false } : c));
  };

  /**
   * The changed-field list is taken now, before the reload replaces the form,
   * and is relative to the copy loaded before the conflict — never to the
   * reloaded one, which already contains the other change.
   */
  const handleConflictReload = async () => {
    if (!editingAgentId || conflictReloading) return;
    const fields = buildConflictFields(unsavedSections, conflictSource);
    // A debounced auto-save still holds the pre-reload form; firing after the
    // reload it would overwrite the change from Claude with the old copy.
    if (autoSaveTimeout) clearTimeout(autoSaveTimeout);
    setConflictReloading(true);
    const loaded = await loadAgentForEdit(editingAgentId);
    setConflictReloading(false);
    if (!loaded) return;
    conflictRef.current = false;
    setSaveConflict({ phase: "reloaded", dialogOpen: false, fields });
  };

  const handleOpenPublishDialog = () => {
    setPublishDialogOpen(true);
  };

  const handleClosePublishDialog = () => {
    if (isPublishing) return;
    setPublishDialogOpen(false);
  };

  const handleConfirmPublish = async () => {
    await handlePublish();
    setPublishDialogOpen(false);
  };

  const handleOpenUnpublishDialog = () => {
    setUnpublishDialogOpen(true);
  };

  const handleCloseUnpublishDialog = () => {
    if (isUnpublishing) return;
    setUnpublishDialogOpen(false);
  };

  const handleConfirmUnpublish = async () => {
    await handleUnpublish();
    setUnpublishDialogOpen(false);
  };

  const handleOpenPublishAsTemplateDialog = () => {
    setMenuAnchor(null); // Close menu
    setPublishAsTemplateDialogOpen(true);
  };

  const handleClosePublishAsTemplateDialog = () => {
    if (isPublishingAsTemplate) return;
    setPublishAsTemplateDialogOpen(false);
  };

  const handleConfirmPublishAsTemplate = async () => {
    if (!isEditMode || !editingAgentId) return;

    try {
      setIsPublishingAsTemplate(true);

      const result = await agentService.publishAsTemplate(editingAgentId);
      console.log("Agent published as template successfully:", result);

      // Check if there's a note message about chained agents in the response
      const noteMessage = result?.note_message || result?.message || "";
      if (noteMessage) {
        console.log("Publish template note:", noteMessage);
      }

      showToast("Agent published as template successfully!", "success");
      setPublishAsTemplateDialogOpen(false);

      // Reload agent data to get template_id
      if (editingAgentId) {
        await loadAgentForEdit(editingAgentId);
      }
    } catch (error) {
      console.error("Publish as template error:", error);
      showToast("Failed to publish agent as template. Please try again.", "error");
    } finally {
      setIsPublishingAsTemplate(false);
    }
  };

  const handleOpenDeleteTemplateDialog = () => {
    setMenuAnchor(null); // Close menu
    setDeleteTemplateDialogOpen(true);
  };

  const handleCloseDeleteTemplateDialog = () => {
    if (isDeletingTemplate) return;
    setDeleteTemplateDialogOpen(false);
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!isEditMode || !editingAgentId) return;

    try {
      setIsDeletingTemplate(true);

      const result = await agentService.deleteTemplate(editingAgentId);
      console.log("Template deleted successfully:", result);
      showToast("Template unpublished successfully!", "success");
      setDeleteTemplateDialogOpen(false);

      // Update originalAgentData to mark is_published_as_template as false
      if (originalAgentData) {
        setOriginalAgentData(prev => ({
          ...prev,
          is_published_as_template: false
        }));
      }

      // Reload agent data to get updated status
      if (editingAgentId) {
        await loadAgentForEdit(editingAgentId);
      }
    } catch (error) {
      console.error("Delete template error:", error);
      showToast("Failed to unpublish template. Please try again.", "error");
    } finally {
      setIsDeletingTemplate(false);
    }
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Agent test playground (chat/logs/HIL) has been extracted to the shared
  // `AgentTestPlayground` component.
  /*
            // Initialize or update workflow steps
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              // Ensure we have all steps initialized
              while (newSteps.length < data.total_groups) {
                newSteps.push({
                  index: newSteps.length,
                  name: `Step ${newSteps.length + 1}`,
                  status: 'pending',
                  isParallel: false
                });
              }
              // Update current step to active
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  name: data.group_name,
                  status: 'active',
                  isParallel: data.is_parallel || false
                };
              }
              return newSteps;
            });
            
            // Update chat message to show step progress
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === streamingMessageId
                  ? {
                      ...msg,
                      content: `Executing Step ${data.group_index + 1} of ${data.total_groups}: ${data.group_name}...`,
                      isLoading: true
                    }
                  : msg
              )
            );
          },
          onStepCompleted: (data) => {
            let stepOutput = null;
            if (data.output_json) {
              stepOutput = data.output_json;
            } else if (data.output_raw) {
              try {
                stepOutput = JSON.parse(data.output_raw);
              } catch (e) {
                stepOutput = data.output_raw; // Use raw string if not valid JSON
              }
            }
            setHilStepOutput(stepOutput);
            
            // Update step status to completed
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  status: 'completed',
                  output: stepOutput
                };
              }
              return newSteps;
            });
          },
          onPause: (data) => {
            // Update step status to show it's waiting for approval
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              if (newSteps[data.waiting_group_index]) {
                newSteps[data.waiting_group_index] = {
                  ...newSteps[data.waiting_group_index],
                  status: 'waiting_approval',
                  name: data.waiting_group_name || newSteps[data.waiting_group_index].name
                };
              }
              return newSteps;
            });
            
            setHilApprovalDialogOpen(true);
            setHilStepOutput(data.draft);
            
            // Update chat message
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === streamingMessageId
                  ? {
                      ...msg,
                      content: `Step ${data.waiting_group_index + 1} completed. Waiting for approval...`,
                      isLoading: false,
                      isStreaming: false
                    }
                  : msg
              )
            );
            setIsChatLoading(false);
          },
          onCompleted: (data) => {
            setHilApprovalDialogOpen(false);
            setHilCurrentStep(null);
            
            // Mark all steps as completed
            setHilWorkflowSteps(prev => 
              prev.map(step => ({ ...step, status: 'completed' }))
            );
            
            // Update chat message
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === streamingMessageId
                  ? {
                      ...msg,
                      content: "Workflow completed successfully!",
                      isLoading: false,
                      isStreaming: false
                    }
                  : msg
              )
            );
            
            // Set run ID and fetch metrics for logs
            if (hilRunId) {
              runIdRef.current = hilRunId;
              setLastRunId(hilRunId);
              // Fetch run metrics after completion
              fetchRunMetrics(hilRunId);
            }
            
            setAttachedFiles([]);
            setIsChatLoading(false);
            setStreamingMessageId(null);
          },
          onError: (error) => {
            setHilApprovalDialogOpen(false);
            const errorMessage = {
              id: streamingMessageId,
              type: "assistant",
              content: error.message || "HIL workflow encountered an error",
              timestamp: new Date(),
              isLoading: false,
              isError: true,
              isStreaming: false,
            };
            setChatMessages(prev =>
              prev.map(msg => msg.id === streamingMessageId ? errorMessage : msg)
            );
            showToast(error.message || "HIL workflow error", "error");
            setIsChatLoading(false);
            setStreamingMessageId(null);
          }
        }
      );
    } catch (error) {
      console.error("Error executing HIL workflow:", error);
      const errorMessage = {
        id: streamingMessageId,
        type: "assistant",
        content: "Sorry, I encountered an error starting the HIL workflow.",
        timestamp: new Date(),
        isLoading: false,
        isError: true,
        isStreaming: false,
      };
      setChatMessages(prev =>
        prev.map(msg => msg.id === streamingMessageId ? errorMessage : msg)
      );
      showToast("Failed to start HIL workflow", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  // Continue HIL workflow after approval
  const handleHILApproval = async (approvedOutput) => {
    if (!hilRunId || hilCurrentStep === null) {
      showToast("Invalid workflow state", "error");
      return;
    }

    try {
      // Mark the current step as completed before continuing
      setHilWorkflowSteps(prev => {
        const newSteps = [...prev];
        if (newSteps[hilCurrentStep.index]) {
          newSteps[hilCurrentStep.index] = {
            ...newSteps[hilCurrentStep.index],
            status: 'completed'
          };
        }
        return newSteps;
      });

      setHilApprovalDialogOpen(false);
      setIsChatLoading(true);

      // Create new streaming message for next step
      const newStreamingMessageId = Date.now() + 1;
      const streamingMessage = {
        id: newStreamingMessageId,
        type: "assistant",
        content: "",
        timestamp: new Date(),
        isLoading: true,
        isStreaming: true,
      };
      setChatMessages(prev => [...prev, streamingMessage]);
      setStreamingMessageId(newStreamingMessageId);

      await agentService.continueHILWorkflow(
        hilRunId, // Same run_id for entire workflow
        hilCurrentStep.index,
        approvedOutput,
        {
          onRunMeta: (meta) => {
            // Ensure run_id is tracked (should be same as hilRunId)
            if (meta.run_id) {
              setHilRunId(meta.run_id);
            }
          },
          onStepStarted: (data) => {
            setHilCurrentStep({
              index: data.group_index,
              name: data.group_name,
              total: data.total_groups,
              isParallel: data.is_parallel || false
            });
            
            // Update workflow steps
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  name: data.group_name,
                  status: 'active',
                  isParallel: data.is_parallel || false
                };
              }
              return newSteps;
            });
            
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === newStreamingMessageId
                  ? {
                      ...msg,
                      content: `Executing Step ${data.group_index + 1} of ${data.total_groups}: ${data.group_name}...`,
                      isLoading: true
                    }
                  : msg
              )
            );
          },
          onStepCompleted: (data) => {
            let stepOutput = null;
            if (data.output_json) {
              stepOutput = data.output_json;
            } else if (data.output_raw) {
              try {
                stepOutput = JSON.parse(data.output_raw);
              } catch (e) {
                stepOutput = data.output_raw; // Use raw string if not valid JSON
              }
            }
            setHilStepOutput(stepOutput);
            
            // Update step status to completed
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              if (newSteps[data.group_index]) {
                newSteps[data.group_index] = {
                  ...newSteps[data.group_index],
                  status: 'completed',
                  output: stepOutput
                };
              }
              return newSteps;
            });
          },
          onPause: (data) => {
            // Update step status to show it's waiting for approval
            setHilWorkflowSteps(prev => {
              const newSteps = [...prev];
              if (newSteps[data.waiting_group_index]) {
                newSteps[data.waiting_group_index] = {
                  ...newSteps[data.waiting_group_index],
                  status: 'waiting_approval',
                  name: data.waiting_group_name || newSteps[data.waiting_group_index].name
                };
              }
              return newSteps;
            });
            
            setHilApprovalDialogOpen(true);
            setHilStepOutput(data.draft);
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === newStreamingMessageId
                  ? {
                      ...msg,
                      content: `Step ${data.waiting_group_index + 1} completed. Waiting for approval...`,
                      isLoading: false,
                      isStreaming: false
                    }
                  : msg
              )
            );
            setIsChatLoading(false);
          },
          onCompleted: (data) => {
            setHilApprovalDialogOpen(false);
            setHilCurrentStep(null);
            
            // Mark all steps as completed
            setHilWorkflowSteps(prev => 
              prev.map(step => ({ ...step, status: 'completed' }))
            );
            
            setChatMessages(prev =>
              prev.map(msg =>
                msg.id === newStreamingMessageId
                  ? {
                      ...msg,
                      content: "Workflow completed successfully!",
                      isLoading: false,
                      isStreaming: false
                    }
                  : msg
              )
            );
            
            // Set run ID and fetch metrics for logs (use the same hilRunId)
            if (hilRunId) {
              runIdRef.current = hilRunId;
              setLastRunId(hilRunId);
              // Fetch run metrics after completion
              fetchRunMetrics(hilRunId);
            }
            
            setAttachedFiles([]);
            setIsChatLoading(false);
            setStreamingMessageId(null);
          },
          onError: (error) => {
            setHilApprovalDialogOpen(false);
            const errorMessage = {
              id: newStreamingMessageId,
              type: "assistant",
              content: error.message || "HIL workflow encountered an error",
              timestamp: new Date(),
              isLoading: false,
              isError: true,
              isStreaming: false,
            };
            setChatMessages(prev =>
              prev.map(msg => msg.id === newStreamingMessageId ? errorMessage : msg)
            );
            showToast(error.message || "HIL workflow error", "error");
            setIsChatLoading(false);
            setStreamingMessageId(null);
          }
        }
      );
    } catch (error) {
      console.error("Error continuing HIL workflow:", error);
      showToast("Failed to continue HIL workflow", "error");
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim() || !editingAgentId || isChatLoading) return;

    // Validate required variables before sending
    if (!validateRequiredVariables()) {
      // Expand variables accordion if it's collapsed to show errors
      if (!isVariablesExpanded) {
        setIsVariablesExpanded(true);
      }
      showToast('Please fill in all required parameters before sending', 'error');
      return;
    }

    const userMessage = chatInput.trim();
    runIdRef.current = null;
    setLastRunId(null);
    setRunMetricsState(null);
    setRunMetricsError(null);
    
    // Add user message to chat
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    
    setChatMessages(prev => [...prev, newUserMessage]);
    
    // Add to chat history
    setChatHistory(prev => [userMessage, ...prev.filter(msg => msg !== userMessage)]);
    setCurrentHistoryIndex(-1);
    
    // Clear input
    setChatInput('');
    
    // Create streaming message placeholder
    const streamingMessageId = Date.now() + 1;
    const streamingMessage = {
      id: streamingMessageId,
      type: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
      isStreaming: true
    };
    
    setChatMessages(prev => [...prev, streamingMessage]);
    setStreamingMessageId(streamingMessageId);
    setIsChatLoading(true);

    try {
      // Generate session ID if not exists
      const currentSessionId = sessionId || agentService.generateSessionId();
      if (!sessionId) {
        setSessionId(currentSessionId);
      }

      // Check agent features to determine execution mode
      // Use originalAgentData if in edit mode, otherwise check features state
      const agentData = originalAgentData || { features: features, agent_chaining: agentChainingData };
      const isHIL = agentData?.features?.human_in_loop || agentData?.features?.humanInLoop || features?.humanInLoop;
      const isBackground = agentData?.features?.background_run || agentData?.features?.backgroundRun || features?.backgroundRun;
      const hasChain = !!agentData?.agent_chaining || !!agentChainingData;

      // Validation
      if (isHIL && !hasChain) {
        showToast("Human-in-loop requires agent chaining to be configured", "error");
        setIsChatLoading(false);
        setStreamingMessageId(null);
        return;
      }
      if (isHIL && isBackground) {
        showToast("Cannot enable both Human-in-loop and Background Run", "error");
        setIsChatLoading(false);
        setStreamingMessageId(null);
        return;
      }

      // Route to appropriate execution flow
      if (isHIL) {
        await executeHILWorkflow(editingAgentId, userMessage, variableValues, currentSessionId, userLocation, userTimezone, attachedFiles, streamingMessageId);
        return;
      }

      // Execute agent with streaming (normal workflow)
      await agentService.executeAgentStream(
        editingAgentId,
        userMessage,
        variableValues, // Pass user-entered variables
        currentSessionId,
        userLocation, // User location as separate parameter
        userTimezone, // User timezone as separate parameter
        attachedFiles, // Files to upload
        // onMessage callback - called for each chunk
        (chunk, fullMessage) => {
          setChatMessages(prev => {
            // Check if message exists before updating
            const messageExists = prev.some(msg => msg.id === streamingMessageId);
            if (!messageExists) {
              console.warn('Streaming message not found in state, adding it');
              return [...prev, {
                id: streamingMessageId,
                type: 'assistant',
                content: fullMessage,
                timestamp: new Date(),
                isLoading: false,
                isStreaming: true
              }];
            }
            return prev.map(msg =>
              msg.id === streamingMessageId
                ? { ...msg, content: fullMessage, isLoading: false }
                : msg
            );
          });
        },
        // onError callback
        (error) => {
          console.error('Streaming error:', error);

          const errorMessage = {
            id: streamingMessageId,
            type: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date(),
            isLoading: false,
            isError: true,
            isStreaming: false
          };

          setChatMessages(prev =>
            prev.map(msg => msg.id === streamingMessageId ? errorMessage : msg)
          );

          showToast('Failed to get response from agent', 'error');
          setIsChatLoading(false);
          setStreamingMessageId(null);
        },
        // onComplete callback
        (fullMessage) => {
          // Ensure content is a string
          const messageContent = typeof fullMessage === 'string' 
            ? fullMessage 
            : fullMessage 
              ? JSON.stringify(fullMessage, null, 2)
              : '';

          setChatMessages(prev =>
            prev.map(msg =>
              msg.id === streamingMessageId
                ? { ...msg, content: messageContent, isLoading: false, isStreaming: false }
                : msg
            )
          );

          // Clear attached files after successful message
          setAttachedFiles([]);

          // Fetch run metrics after completion (if run_id was received)
          const runId = runIdRef.current;
          if (runId) {
            fetchRunMetrics(runId);
          }

          // Clear loading state after completion
          setIsChatLoading(false);
          setStreamingMessageId(null);
        }
        ,
        // onRunMeta callback (run_id)
        (meta) => {
          if (meta?.run_id) {
            runIdRef.current = meta.run_id;
            setLastRunId(meta.run_id);
          }
        }
      );

    } catch (error) {
      console.error('Error executing agent stream:', error);
      
      // Replace streaming message with error
      const errorMessage = {
        id: streamingMessageId,
        type: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        isLoading: false,
        isError: true,
        isStreaming: false
      };

      setChatMessages(prev => 
        prev.map(msg => msg.id === streamingMessageId ? errorMessage : msg)
      );
      
      showToast('Failed to get response from agent', 'error');
      setIsChatLoading(false);
      setStreamingMessageId(null);
    }
  };

  */
  // (legacy test playground removed)

  // Title icons for the knowledge-base cards of a shared agent.
  const ownerLockAdornment = (
    <Tooltip title={SHARED_KB_COPY.dim1Locked} arrow>
      <span className={styles.featureTitleAdornment}>
        <LucideLock size={13} strokeWidth={2} aria-label={SHARED_KB_COPY.dim1Locked} />
      </span>
    </Tooltip>
  );
  const myDim3InfoAdornment = (
    <Tooltip title={SHARED_KB_COPY.dim3Info} arrow>
      <span className={styles.featureTitleAdornment}>
        <LucideInfo size={13} strokeWidth={2} aria-label={SHARED_KB_COPY.dim3Info} />
      </span>
    </Tooltip>
  );

  const agentForTestPlayground = {
    ...(originalAgentData || {}),
    name: originalAgentData?.name || formData?.name || "",
    description: originalAgentData?.description || formData?.description || "",
    features: {
      ...(originalAgentData?.features || {
        human_in_loop: !!features?.humanInLoop,
        humanInLoop: !!features?.humanInLoop,
        background_run: !!features?.backgroundRun,
        backgroundRun: !!features?.backgroundRun,
      }),
      // Run-behaviour flags gate playground affordances (sources panel,
      // follow-up chips, regenerate/fork), so mirror the live toggles.
      memory: !!features?.memory,
      show_sources: !!features?.showSources,
      followups: !!features?.followups,
      checkpointing: !!features?.checkpointing,
      live_sources: !!features?.liveSources,
      query_expansion: !!features?.queryExpansion,
      contextual_relevance: !!features?.contextualRelevance,
      qualifying_questions: !!features?.qualifyingQuestions,
    },
    agent_chaining:
      originalAgentData?.agent_chaining ||
      (agentChainingData
        ? {
          ...agentChainingData,
          selected_agents:
            agentChainingData.selected_agents ||
            agentChainingData.selectedAgents ||
            [],
        }
        : null),
    agent_team: agentTeamData || originalAgentData?.agent_team || null,
  };

  const showMergedColumnFloatingAction =
    !isEditMode ||
    (isEditMode && !isViewingVersion && !showVersionHistory && (hasChanges || isCreatingAgent));

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.agentCreate}>
        {/* Add CSS for animations */}
        <style>
          {`
            @keyframes blink {
              0%, 50% { opacity: 1; }
              51%, 100% { opacity: 0; }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            @keyframes fadeInOut {
              0% { opacity: 0; transform: translateY(-10px); }
              20% { opacity: 1; transform: translateY(0); }
              80% { opacity: 1; transform: translateY(0); }
              100% { opacity: 0; transform: translateY(-10px); }
            }
          `}
        </style>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerLeft}>
              <IconButton
                onClick={() => navigate('/agents')}
                className={styles.headerBackIcon}
                aria-label="Back to agents"
                size="medium"
                sx={{
                  color: '#e36250',
                  '&:hover': { backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : '#f4f4f5' },
                }}
              >
                <ArrowBack />
              </IconButton>
              <div className={styles.headerText}>
                <div className={styles.headerTitleRow}>
                  <Typography
                    variant="h5"
                    component="h1"
                    className={styles.headerTitle}
                    style={{ color: isDarkMode ? '#ffffff' : undefined }}
                  >
                    {isSharedReadOnly ? 'Shared Agent (Read Only)' : isSharedResource ? 'Edit Shared Agent' : isEditMode ? 'Edit Agent' : 'Create Agent'}
                  </Typography>
                  {isEditMode ? (
                    <Chip
                      label={isPublished ? 'Published' : 'Draft'}
                      size="small"
                      className={styles.headerStatusChip}
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      label="New"
                      size="small"
                      className={styles.headerStatusChip}
                      variant="outlined"
                    />
                  )}
                </div>
                <Typography
                  variant="body2"
                  className={styles.headerSubtitle}
                  style={{ color: isDarkMode ? '#9ca3af' : undefined }}
                >
                  {isEditMode
                    ? 'Modify your agent configuration and test its behavior'
                    : 'Build and configure your AI agent'}
                </Typography>
              </div>
            </div>
            <div className={styles.headerActions}>
              {isEditMode && editingAgentId && !isSharedResource && canShareResources && (
                <>
                  <Button variant="text" onClick={() => setResourceShareOpen(true)} className={styles.headerToolbarButton}>Share</Button>
                  <Button variant="text" onClick={() => setManageResourceSharingOpen(true)} className={styles.headerToolbarButton}>Manage sharing</Button>
                </>
              )}
              {/* Viewing version info text */}
              {isViewingVersion && viewingVersionInfo && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#e36250',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Viewing previous version: {new Date(viewingVersionInfo.date).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </Typography>
              )}

              {/* Version History / Restore Button */}
              {isViewingVersion ? (
                <Button
                  variant="contained"
                  startIcon={isCreatingAgent ? <CircularProgress size={16} color="inherit" /> : <RestoreRounded />}
                  onClick={handleRestoreVersion}
                  disabled={isCreatingAgent || saveConflict?.phase === "paused"}
                  sx={{
                    height: '40px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    backgroundColor: '#e36250',
                    color: '#ffffff',
                    mr: 1,
                    '&:hover': {
                      backgroundColor: '#d14e3c',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(227, 98, 80, 0.5)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }
                  }}
                >
                  {isCreatingAgent ? 'Restoring...' : 'Restore'}
                </Button>
              ) : !showVersionHistory && (
                <Tooltip
                  title={
                    !isEditMode || !editingAgentId
                      ? "Version History is only available for saved agents. Please create and save your agent first."
                      : ""
                  }
                  arrow
                  placement="bottom"
                >
                  <span>
                    <Button
                      variant="text"
                      startIcon={<History />}
                      onClick={handleVersionHistoryToggle}
                      disabled={!isEditMode || !editingAgentId || isSharedResource}
                      className={styles.headerToolbarButton}
                      sx={{
                        height: '40px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: isDarkMode ? '#ffffff' : '#52525b',
                        mr: 0.5,
                        '&:hover': {
                          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f4f4f5',
                        },
                        '&.Mui-disabled': {
                          color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                          cursor: 'not-allowed',
                        }
                      }}
                    >
                      Version History
                    </Button>
                  </span>
                </Tooltip>
              )}

              {!isViewingVersion && !isSharedResource && (
                <Tooltip
                  title={
                    isEditMode && editingAgentId
                      ? 'Manage this agent schedules'
                      : 'Create the agent first to add schedules'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="text"
                      startIcon={<CalendarMonth />}
                      onClick={() => navigate(`/schedules?agentId=${editingAgentId}`)}
                      disabled={!isEditMode || !editingAgentId}
                      className={styles.headerToolbarButton}
                      sx={{
                        height: '40px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: isDarkMode ? '#ffffff' : '#52525b',
                      }}
                    >
                      Schedule
                    </Button>
                  </span>
                </Tooltip>
              )}

              {!isViewingVersion && !isSharedResource && (
                <Tooltip
                  title={
                    isEditMode && editingAgentId
                      ? 'Run this agent when Composio app events fire (new email, Slack message, etc.)'
                      : 'Save the agent first to add Composio triggers'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="text"
                      startIcon={<Bolt />}
                      onClick={() => navigate(`/configure/triggers?agentId=${editingAgentId}&view=agent`)}
                      disabled={!isEditMode || !editingAgentId}
                      className={styles.headerToolbarButton}
                      sx={{
                        height: '40px',
                        padding: '8px 12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: isDarkMode ? '#ffffff' : '#52525b',
                      }}
                    >
                      Triggers
                    </Button>
                  </span>
                </Tooltip>
              )}

              {!showVersionHistory && !isSharedResource && (
                <>
                  <Tooltip
                    title={
                      !isEditMode || !editingAgentId
                        ? "Agent API is only available for saved agents. Please create and save your agent first."
                        : !isPublished
                          ? "Publish this agent to enable Agent API."
                          : ""
                    }
                    arrow
                    placement="bottom"
                  >
                    <span>
                      <Button
                        variant="text"
                        startIcon={<Code />}
                        className={`${styles.apiButton} ${styles.headerToolbarButton}`}
                        onClick={() => setAgentAPIDialogOpen(true)}
                        disabled={!isEditMode || !editingAgentId || !isPublished}
                        sx={{
                          height: '40px',
                          padding: '8px 12px',
                          fontSize: '14px',
                          fontWeight: 500,
                          color: isDarkMode ? '#ffffff' : '#52525b',
                          mr: 0.5,
                          '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f4f4f5',
                          },
                          '&.Mui-disabled': {
                            color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
                            cursor: 'not-allowed',
                          }
                        }}
                      >
                        Agent API
                      </Button>
                    </span>
                  </Tooltip>

                  {/* Three dots menu with Publish and Publish as Template */}
                  {isEditMode && editingAgentId && (
                    <>
                      <span className={styles.headerDivider} aria-hidden />
                      <IconButton
                        onClick={handleMenuOpen}
                        sx={{
                          color: isDarkMode ? '#9ca3af' : '#a1a1aa',
                          '&:hover': {
                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f4f4f5',
                          }
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        {/* Publish / Unpublish Menu Item */}
                        <MenuItem
                          onClick={() => {
                            handleMenuClose();
                            if (isPublished) {
                              handleOpenUnpublishDialog();
                            } else {
                              handleOpenPublishDialog();
                            }
                          }}
                          disabled={isPublishing || isUnpublishing || isPublishingAsTemplate}
                          sx={{
                            minWidth: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <ListItemIcon>
                              {isPublishing || isUnpublishing ? (
                                <CircularProgress size={16} />
                              ) : isPublished ? (
                                <UnpublishedRounded fontSize="small" />
                              ) : (
                                <PublishRounded fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                isPublishing
                                  ? "Publishing..."
                                  : isUnpublishing
                                    ? "Unpublishing..."
                                    : isPublished
                                      ? "Unpublish"
                                      : "Publish"
                              }
                            />
                          </Box>
                          <InfoTooltip
                            title={isPublished ? "Unpublish Agent" : "Publish Agent"}
                            maxWidth={400}
                            placement="left"
                            content={
                              <Box>
                                <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}>
                                  <strong>What it does:</strong>
                                </Typography>
                                {isPublished ? (
                                  <>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      Unpublishes your agent. Agent API access will be disabled until you publish it again.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      ⚠️ Note:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      Integrations relying on this agent will stop working until republished.
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      Publishes your agent to make it available via the Agent API. Once published, the agent can be accessed programmatically.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      ✅ Benefits:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Enables Agent API access
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Makes agent available for integration
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                ml: 1,
                                color: "#e36250",
                                padding: "4px",
                                "&:hover": {
                                  backgroundColor: "rgba(227, 98, 80, 0.1)",
                                },
                              }}
                            >
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </InfoTooltip>
                        </MenuItem>

                        {/* Publish/Unpublish Template Menu Item */}
                        <MenuItem
                          onClick={isPublishedAsTemplate ? handleOpenDeleteTemplateDialog : handleOpenPublishAsTemplateDialog}
                          disabled={isPublishing || isPublishingAsTemplate || isDeletingTemplate}
                          sx={{
                            minWidth: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <ListItemIcon>
                              {isPublishingAsTemplate || isDeletingTemplate ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PublishRounded fontSize="small" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                isPublishingAsTemplate
                                  ? "Publishing as Template..."
                                  : isDeletingTemplate
                                    ? "Unpublishing Template..."
                                    : isPublishedAsTemplate
                                      ? "Unpublish Template"
                                      : "Publish as Template"
                              }
                            />
                          </Box>
                          <InfoTooltip
                            title={isPublishedAsTemplate ? "Unpublish Template" : "Publish as Template"}
                            maxWidth={400}
                            placement="left"
                            content={
                              <Box>
                                <Typography variant="body2" sx={{ mb: 1, color: "rgba(255,255,255,0.9)" }}>
                                  <strong>What it does:</strong>
                                </Typography>
                                {isPublishedAsTemplate ? (
                                  <>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      Removes the template version of your agent. The agent itself will remain unchanged, but it will no longer be available as a template for others to use.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                                      ⚠️ Note:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      This action cannot be undone. You can republish as template later if needed.
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                                      Publishes your agent as a reusable template that can be used by other users or in other projects. Templates help standardize agent configurations.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                                      ✅ Benefits:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Share agent configurations with others
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                                      • Reuse proven agent setups
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                                      • Standardize agent creation process
                                    </Typography>
                                  </>
                                )}
                              </Box>
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                ml: 1,
                                color: "#e36250",
                                padding: "4px",
                                "&:hover": {
                                  backgroundColor: "rgba(227, 98, 80, 0.1)",
                                },
                              }}
                            >
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </InfoTooltip>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Loading state for editing */}
        {loadingAgent && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              padding: 4,
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading agent data...
            </Typography>
          </Box>
        )}

        {/* Google Docs-style auto-save indicator */}
        {isEditMode && (isAutoSaving || lastSaved) && (
          <Box sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: isAutoSaving ? '#fef3c7' : '#d1fae5',
            color: isAutoSaving ? '#92400e' : '#065f46',
            px: 2,
            py: 1,
            borderRadius: 2,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            minWidth: 140,
            border: isAutoSaving ? '1px solid #f59e0b' : '1px solid #10b981',
            animation: lastSaved && !isAutoSaving ? 'fadeInOut 3s ease-in-out' : 'none'
          }}>
            {isAutoSaving ? (
              <>
                <CircularProgress
                  size={16}
                  sx={{
                    color: '#92400e',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  Saving changes...
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  color: 'white'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" />
                  </svg>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  All changes saved
                </Typography>
              </>
            )}
          </Box>
        )}

        {/* Main Content */}
        <Box
          ref={mainContentRef}
          className={styles.mainContent}
          sx={{
            opacity: loadingAgent ? 0.5 : 1,
            transition: 'opacity 0.3s ease',
            ...(isThreeColumnLayout
              ? {
                  display: 'grid',
                  width: '100%',
                  maxWidth: '100%',
                  gridTemplateRows: 'minmax(0, 1fr)',
                
                  columnGap: `${COLUMN_GAP_PX}px`,
                  rowGap: 0,
                  gridTemplateColumns: `${columnWidths.merged}px ${COLUMN_GRIP_PX}px minmax(0, 1fr)`,
                }
              : {
                  display: 'grid',
                  width: '100%',
                  gridTemplateColumns: '1fr',
                }),
          }}
        >
          {/* Left column: Configuration & Core features as tabs */}
          <Box className={styles.mergedConfigColumn}>
          <Paper
            elevation={0}
            className={styles.mergedTabPaper}
            component="div"
          >
            {isSharedReadOnly && (
              <Alert severity="info" sx={{ m: 1, borderRadius: '10px', flexShrink: 0 }}>
                Shared by {sharedOwner?.name || sharedOwner?.email || 'a teammate'} as read-only —
                you can review the configuration and test the agent in chat, but changes can't be saved.
              </Alert>
            )}
            {saveConflict && (
              <AgentConflictBanner
                phase={saveConflict.phase}
                fields={
                  saveConflict.phase === "paused"
                    ? buildConflictFields(unsavedSections, conflictSource)
                    : saveConflict.fields || []
                }
                reloading={conflictReloading}
                onReload={handleConflictReload}
                onDismiss={() => setSaveConflict(null)}
              />
            )}
            <Box
              sx={{
                px: 1,
                py: 0.75,
                flexShrink: 0,
                width: "100%",
                bgcolor: "background.paper",
                display: "flex",
                alignItems: "stretch",
              }}
            >
              <div className={styles.mergedColumnSegmented} role="tablist" aria-label="Agent instructions and configuration">
                <button
                  type="button"
                  role="tab"
                  id="merged-tab-0"
                  aria-controls="merged-tabpanel-0"
                  aria-selected={mergedColumnTab === 0}
                  className={`${styles.mergedSegmentTab}${mergedColumnTab === 0 ? ` ${styles.mergedSegmentTabActive}` : ""}`}
                  onClick={() => setMergedColumnTab(0)}
                >
                  <span className={styles.mergedSegmentTabInner}>
                    <FileText className={styles.mergedSegmentTabIcon} size={12} strokeWidth={2.5} aria-hidden />
                    INSTRUCTIONS
                  </span>
                </button>
                <button
                  type="button"
                  role="tab"
                  id="merged-tab-1"
                  aria-controls="merged-tabpanel-1"
                  aria-selected={mergedColumnTab === 1}
                  className={`${styles.mergedSegmentTab}${mergedColumnTab === 1 ? ` ${styles.mergedSegmentTabActive}` : ""}`}
                  onClick={() => setMergedColumnTab(1)}
                >
                  <span className={styles.mergedSegmentTabInner}>
                    <LucideSettings className={styles.mergedSegmentTabIcon} size={12} strokeWidth={2.5} aria-hidden />
                    CONFIGURATION
                  </span>
                </button>
              </div>
            </Box>
            <Box
              id="merged-tabpanel-0"
              role="tabpanel"
              aria-labelledby="merged-tab-0"
              className={isSharedReadOnly ? styles.readOnlyLock : undefined}
              sx={{
                flex: 1,
                minHeight: 0,
                display: mergedColumnTab === 0 ? 'flex' : 'none',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
            <div
              className={`${styles.leftPanelScroll}${
                showMergedColumnFloatingAction ? ` ${styles.leftPanelScrollWithFloatingAction}` : ""
              }`}
            >
            <Stack spacing={2} className={styles.configSection}>
              <Box className={styles.section}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" className={styles.sectionLabel}>
                    Name <span className={styles.required}>*</span>
                  </Typography>
                  <InfoTooltip
                    title="Agent Name Guide"
                    maxWidth={800}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>How to name your agent:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Best Practices:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Use descriptive, clear names (e.g., "Customer Support Bot", "Email Assistant")
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Keep it concise but meaningful
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          • Avoid generic names like "Bot" or "Assistant"
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Examples:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • "Sales Lead Qualifier"
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                          • "Technical Support Agent"
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                <TextField
                  placeholder="Enter agent name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  onFocus={() => handleFieldFocus("name")}
                  onBlur={() => handleFieldBlur("name")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={styles.textField}
                  disabled={loadingAgent}
                  inputProps={{ maxLength: 100 }}
                  helperText={`${formData.name.length}/100 characters`}
                />
              </Box>
              <Box className={styles.section}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" className={styles.sectionLabel}>
                    Description <span className={styles.required}>*</span>
                  </Typography>
                  <InfoTooltip
                    title="Agent Description Guide"
                    maxWidth={800}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>How to describe your agent:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Best Practices:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Explain what the agent does and its main purpose
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Mention key capabilities and use cases
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          • Be specific about the target audience or domain
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Example:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                          "A customer support agent that helps users with billing questions, account issues, and product support. Handles common inquiries and escalates complex cases to human agents."
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                <TextField
                  placeholder="Describe what your agent does and its main purpose"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  onFocus={() => handleFieldFocus("description")}
                  onBlur={() => handleFieldBlur("description")}
                  multiline
                  rows={2}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={styles.textField}
                  disabled={loadingAgent}
                  inputProps={{ maxLength: 1000, style: { resize: 'vertical' } }}
                  helperText={`${formData.description.length}/1000 characters`}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
                  <FormControl
                    size="small"
                    margin="dense"
                    sx={{ minWidth: 185, flex: 1 }}
                    disabled={loading.providers || loadingAgent}
                  >
                    <InputLabel>LLM Provider</InputLabel>
                    <Select
                      value={formData.llmProvider}
                      onChange={(e) =>
                        handleInputChange("llmProvider", e.target.value)
                      }
                      onFocus={() => handleFieldFocus("llmProvider")}
                      onBlur={() => handleFieldBlur("llmProvider")}
                      label="LLM Provider"
                      renderValue={(selected) => {
                        if (!selected) return "";
                        const provider = providers.find(p => p.id === selected);
                        if (provider) {
                          return provider.display_name || provider.name || selected;
                        }
                        // If provider not found in array, return a loading message or the ID
                        return providers.length === 0 ? "Loading..." : selected;
                      }}
                    >
                      {(() => {
                        console.log('Rendering providers dropdown - loading:', loading.providers, 'error:', error.providers, 'providers count:', providers.length);
                        if (loading.providers) {
                          return <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading providers...
                          </MenuItem>;
                        } else if (error.providers) {
                          return <MenuItem disabled>
                            <Cancel sx={{ mr: 1, color: 'error.main' }} />
                            Error loading providers
                          </MenuItem>;
                        } else if (providers.length === 0) {
                          return <MenuItem disabled>No providers available</MenuItem>;
                        } else {
                          return providers.map((provider) => (
                            <MenuItem key={provider.id} value={provider.id}>
                              {provider.display_name || provider.name}
                            </MenuItem>
                          ));
                        }
                      })()}
                    </Select>
                  </FormControl>
                  <FormControl
                    size="small"
                    margin="dense"
                    sx={{ minWidth: 185, flex: 1 }}
                    disabled={loading.models || !formData.llmProvider || loadingAgent}
                  >
                    <InputLabel>LLM Model</InputLabel>
                    <Select
                      value={formData.llmModel}
                      onChange={(e) =>
                        handleInputChange("llmModel", e.target.value)
                      }
                      onFocus={() => handleFieldFocus("llmModel")}
                      onBlur={() => handleFieldBlur("llmModel")}
                      label="LLM Model"
                      renderValue={(selected) => {
                        if (!selected) return "";
                        const model = models.find(m => m.id === selected);
                        if (model) {
                          return model.name || model.display_name || model.model_name || selected;
                        }
                        // If model not found in array, return a loading message or the ID
                        return models.length === 0 ? "Loading..." : selected;
                      }}
                      endAdornment={
                        loading.models ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <CircularProgress size={16} />
                          </Box>
                        ) : null
                      }
                    >
                      {(() => {
                        console.log('Rendering models dropdown - loading:', loading.models, 'error:', error.models, 'models count:', models.length);
                        if (loading.models) {
                          return <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading models...
                          </MenuItem>;
                        } else if (error.models) {
                          return <MenuItem disabled>
                            <Cancel sx={{ mr: 1, color: 'error.main' }} />
                            Error loading models: {error.models}
                          </MenuItem>;
                        } else if (models.length === 0) {
                          return <MenuItem disabled>No models available</MenuItem>;
                        } else {
                          return models.map((model) => (
                            <MenuItem key={model.id} value={model.id}>
                              {model.name || model.display_name || model.model_name}
                            </MenuItem>
                          ));
                        }
                      })()}
                    </Select>
                  </FormControl>
                </Box>
                <div className={styles.llmFooterRow}>
                  <Button
                    size="small"
                    startIcon={<HelpOutline sx={{ fontSize: 16 }} />}
                    onClick={() => setModelsDialogOpen(true)}
                    className={styles.helpChoosingButton}
                  >
                    Need help choosing?
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => setSettingsAnchor(e.currentTarget)}
                    sx={{ color: '#a1a1aa', '&:hover': { backgroundColor: '#f4f4f5', color: '#52525b' } }}
                  >
                    <Settings />
                  </IconButton>
                </div>
              </Box>
              <Box className={styles.agentLogicBlock}>
                <Box className={styles.agentLogicBlockHeader}>
                  <Typography component="h3" className={styles.agentLogicBlockTitle}>
                    Agent logic
                  </Typography>
                  <Button
                    size="small"
                    startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesome sx={{ fontSize: 16 }} />}
                    onClick={handleGenerateWithAI}
                    disabled={isGenerating}
                    className={styles.generateWithAiButton}
                  >
                    {isGenerating ? 'Generating...' : 'Generate with AI'}
                  </Button>
                </Box>
                <Box className={styles.section}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="body2" className={styles.sectionLabel}>
                      Agent Role <span className={styles.required}>*</span>
                    </Typography>
                    <InfoTooltip
                      title="Agent Role Guide"
                      maxWidth={1000}
                      placement="right"
                      content={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                            <strong>How to define your agent's role:</strong>
                          </Typography>

                          <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                            ✅ Best Practices:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Start with "You are a..." to establish identity
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • Define expertise level and domain knowledge
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                            • Mention personality traits and communication style
                          </Typography>

                          <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                            💡 Examples:
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                            • "You are an expert customer support agent with 5+ years of experience..."
                          </Typography>
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                            • "You are a friendly and knowledgeable sales assistant specializing in..."
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ color: "#e36250" }}>
                        <HelpOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InfoTooltip>
                  </Box>
                  <TextField
                    placeholder="You are an expert customer support agent with 5+ years of experience..."
                    value={formData.agentRole}
                    onChange={(e) => handleInputChange("agentRole", e.target.value)}
                    onFocus={() => handleFieldFocus("agentRole")}
                    onBlur={() => handleFieldBlur("agentRole")}
                    multiline
                    rows={2}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={styles.textField}
                    disabled={loadingAgent}
                    inputProps={{ maxLength: 1000, style: { resize: 'vertical' } }}
                    helperText={`${formData.agentRole.length}/1000 characters`}
                  />
                </Box>
              <Box className={styles.section}>
                {/*
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" className={styles.sectionLabel} sx={{ color: 'text.disabled' }}>
                    Agent Goal
                  </Typography>
                  <Chip
                    label="Deprecated"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(158, 158, 158, 0.2)',
                      color: 'text.secondary',
                    }}
                  />
                </Box>
               
                <TextField
                  placeholder="This field is deprecated and no longer used."
                  value={formData.agentGoal}
                  multiline
                  rows={3}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={styles.textField}
                  disabled
                  helperText="Agent Goal is deprecated. Define your goal directly in Agent Instructions."
                />*/}
              </Box>
              <Box className={styles.section} sx={{ position: "relative" }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" className={styles.sectionLabel}>
                    Agent Instructions <span className={styles.required}>*</span>
                  </Typography>
                  <InfoTooltip
                    title="Agent Instructions Guide"
                    maxWidth={1200}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>How to write effective agent instructions:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Best Practices:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Use action verbs (LISTEN, GATHER, PROVIDE, ESCALATE)
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Be specific about behaviors and responses
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Include escalation procedures and boundaries
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          • Use variables like {`{customer_name}`} for personalization
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Example Structure:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          1. LISTEN to the customer's concern
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          2. GATHER all relevant information
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                          3. PROVIDE clear and helpful responses
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                <TextField
                  placeholder={"Write step-by-step instructions for your agent using action verbs (LISTEN, GATHER, VERIFY, PROVIDE, ESCALATE).\n\n🔑 Variables — wrap any dynamic value in single curly braces: {customer_name}, {order_id}, {issue_type}\n   → Detected variables appear as chips below and are passed at runtime by the caller.\n\n🔗 Chain other agents — use the Agent Chaining toggle above to connect upstream agents.\n   → Their output variables will also appear as chips and can be used as {variable} tokens here.\n\n🛠 Use tools & MCPs — connect integrations via the Add Tool option below, then instruct the agent when to use them.\n\n📋 Example:\nLISTEN to {customer_name}'s concern about {issue_type}.\nGATHER the relevant details and verify against their account {account_id}.\nPROVIDE a clear resolution using the available tools. ESCALATE if unresolved."}
                  value={formData.agentInstructions}
                  onChange={(e) => handleInputChange("agentInstructions", e.target.value)}
                  onFocus={() => handleFieldFocus("agentInstructions")}
                  onBlur={() => handleFieldBlur("agentInstructions")}
                  multiline
                  rows={6}
                  fullWidth
                  variant="outlined"
                  size="small"
                  className={styles.textField}
                  disabled={loadingAgent}
                  inputProps={{ style: { resize: 'vertical' } }}
                  helperText={`${formData.agentInstructions.length} characters`}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 25,
                    right: 8,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Button size="small">Improve prompt</Button>
                  <IconButton
                    size="small"
                    onClick={() => setInstructionsDialogOpen(true)}
                  >
                    <OpenInFull />
                  </IconButton>
                </Box>
              </Box>
              </Box>

              {/* Display extracted variables as chips */}
              {extractedVariables.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Detected Variables:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {extractedVariables.map((variable, index) => (
                      <Chip
                        key={index}
                        label={`{${variable}}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.75rem",
                          backgroundColor: isDarkMode ? "rgba(227, 98, 80, 0.1)" : "rgba(227, 98, 80, 0.05)",
                          borderColor: isDarkMode ? "rgba(227, 98, 80, 0.3)" : "rgba(227, 98, 80, 0.2)",
                          color: isDarkMode ? "#ffab91" : "#d84315",
                          "&:hover": {
                            backgroundColor: isDarkMode ? "rgba(227, 98, 80, 0.2)" : "rgba(227, 98, 80, 0.1)",
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Display chained agent variables as chips */}
              {chainedAgentVariables.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Chained Agent Variables:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {chainedAgentVariables
                      .filter(variable => !extractedVariables.includes(variable))
                      .map((variable, index) => (
                        <Chip
                          key={`chained-${index}`}
                          label={`{${variable}}`}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.75rem",
                            backgroundColor: isDarkMode ? "rgba(139, 92, 246, 0.1)" : "rgba(139, 92, 246, 0.05)",
                            borderColor: isDarkMode ? "rgba(139, 92, 246, 0.3)" : "rgba(139, 92, 246, 0.2)",
                            color: isDarkMode ? "#a78bfa" : "#6d28d9",
                            "&:hover": {
                              backgroundColor: isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)",
                            }
                          }}
                        />
                      ))}
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Connected Toolkits</Typography>
                  <InfoTooltip
                    title="Connected Toolkits"
                    maxWidth={1000}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>Apps the agent can use:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Pick a toolkit
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          Connect a toolkit (e.g. Gmail, Slack, Google Calendar). The agent picks the right action per request based on your instructions — you don't have to pin one.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Custom & MCP tools
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          Custom tools still pin a specific action; MCP servers expose all of their actions automatically.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Examples:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Gmail: send email, search inbox, create draft
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                          • Slack: post message, list channels, react
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleToolAdd}
                >
                  + Add
                </Button>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.5,
                  mb: 1,
                  color: isDarkMode ? '#9ca3af' : '#666',
                  lineHeight: 1.5,
                }}
              >
                Toolkits let the agent <strong>use</strong> apps during a run. To <strong>start</strong> the agent when
                something happens (new Gmail, Slack message, GitHub commit), add a{' '}
                <strong>Composio trigger</strong> via the Triggers button above or Configure → Triggers.
              </Typography>

              {/* Tool Cards Display */}
              {tools.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {tools.map((tool) => {
                    const toolSlug = (tool.toolkitSlug || tool.appName || '').toString().toLowerCase();
                    const isDisconnected =
                      tool.toolType === 'composio' &&
                      toolSlug &&
                      !connectedToolkitSlugs.has(toolSlug);
                    return (
                    <Box
                      key={tool.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        mb: 0.5,
                        backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa",
                        border: `1px solid ${
                          isDisconnected
                            ? "#e36250"
                            : (isDarkMode ? "#444" : "#e0e0e0")
                        }`,
                        borderRadius: 0.75,
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#333" : "#f0f0f0",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1 }}>
                        {tool.appLogo && (
                          <Box
                            component="img"
                            src={tool.appLogo}
                            alt={tool.appName}
                            sx={{
                              width: 16,
                              height: 16,
                              objectFit: "contain",
                              borderRadius: 0.5,
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? "#ffffff" : "#1a1a1a",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontSize: "0.875rem",
                              lineHeight: 1.2,
                            }}
                          >
                            {tool.appName}
                          </Typography>
                          {tool.toolType === "mcp_server" ? (
                            <McpToolDisplay tool={tool} isDarkMode={isDarkMode} />
                          ) : tool.toolType === "composio" ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? "#9ca3af" : "#666",
                                fontSize: "0.7rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                lineHeight: 1.2,
                              }}
                            >
                              Toolkit
                            </Typography>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDarkMode ? "#9ca3af" : "#666",
                                fontSize: "0.7rem",
                                textTransform: "uppercase",
                                letterSpacing: "0.3px",
                                lineHeight: 1.2,
                              }}
                            >
                              {tool.actionName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {tool.toolType === 'composio' && isEditMode && editingAgentId && !isDisconnected && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              navigate(
                                `/configure/triggers?agentId=${editingAgentId}&view=agent&create=composio&toolkit=${encodeURIComponent(toolSlug)}`,
                              )
                            }
                            sx={{
                              textTransform: "none",
                              fontSize: "0.7rem",
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              lineHeight: 1.4,
                              borderColor: "#8b5cf6",
                              color: "#8b5cf6",
                              "&:hover": {
                                borderColor: "#7c3aed",
                                backgroundColor: "rgba(139, 92, 246, 0.08)",
                              },
                            }}
                          >
                            Add trigger
                          </Button>
                        )}
                        {isDisconnected && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleAuthTool(tool)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.7rem",
                              py: 0.25,
                              px: 1,
                              minWidth: 0,
                              lineHeight: 1.4,
                              borderColor: "#e36250",
                              color: "#e36250",
                              "&:hover": {
                                borderColor: "#e36250",
                                backgroundColor: "rgba(227, 98, 80, 0.08)",
                              },
                            }}
                          >
                            Authenticate
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleToolEdit(tool)}
                          sx={{
                            color: isDarkMode ? "#9ca3af" : "#666",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <Edit sx={{ fontSize: "16px" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToolRemove(tool.id)}
                          sx={{
                            color: "#e36250",
                            padding: "4px",
                            "&:hover": {
                              backgroundColor: "rgba(227, 98, 80, 0.1)",
                            },
                          }}
                        >
                          <Remove sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Box>
                    </Box>
                    );
                  })}
                </Box>
              )}

              {/* Examples Section */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Examples (Text)</Typography>
                  <InfoTooltip
                    title="Examples Guide"
                    maxWidth={1000}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>How to create effective examples for your agent:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ What are examples?
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          Examples show your agent how to respond in specific situations, improving its performance and consistency.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Best practices:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Include realistic user inputs and expected responses
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Cover different scenarios and edge cases
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          • Use the same tone and style you want your agent to adopt
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Example format:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          User: "I need help with my billing"
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                          Agent: "I'd be happy to help with your billing. Can you tell me what specific issue you're experiencing?"
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                {examples.length === 0 ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddExample}
                  >
                    + Add
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Remove />}
                    onClick={() => setExamples([])}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              {/* Render example text areas */}
              {examples.map((example, index) => (
                <Box key={example.id} sx={{ position: "relative" }}>
                  <TextField
                    label={`Example ${index + 1}`}
                    placeholder="Add example outputs to guide your agent"
                    value={example.text}
                    onChange={(e) =>
                      handleExampleChange(example.id, e.target.value)
                    }
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    size="small"
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: { resize: 'vertical' } }}
                  />
                  {examples.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveExample(example.id)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "background.paper",
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}

              {/* Add another example button when examples exist */}
              {examples.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddExample}
                  sx={{ alignSelf: "flex-start" }}
                >
                  + Add Another Example
                </Button>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Structured Output (JSON)</Typography>
                  <InfoTooltip
                    title="Structured Output Guide"
                    maxWidth={1000}
                    placement="right"
                    content={
                      <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                          <strong>How to define structured output for your agent:</strong>
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ What is structured output?
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          Structured output ensures your agent returns data in a consistent JSON format, making it easier to integrate with other systems.
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                          ✅ Benefits:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Consistent data format for API integration
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                          • Easier to parse and process responses
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                          • Better error handling and validation
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                          💡 Example JSON schema:
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5, fontFamily: "monospace" }}>
                          {`{
  "response": "string",
  "confidence": "number",
  "action_required": "boolean"
}`}
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton size="small" sx={{ color: "#e36250" }}>
                      <HelpOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InfoTooltip>
                </Box>
                {!structuredOutput ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setStructuredOutputDialogOpen(true)}
                  >
                    + Add
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Remove />}
                    onClick={() => setStructuredOutput("")}
                  >
                    Remove
                  </Button>
                )}
              </Box>

              {/* Show structured output preview */}
              {structuredOutput && (
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: "background.paper",
                    position: "relative",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Structured Output Preview:
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      fontSize: "12px",
                      fontFamily: "Monaco, 'Lucida Console', monospace",
                      margin: 0,
                      overflow: "auto",
                      maxHeight: "150px",
                      color: "text.primary",
                      backgroundColor: "action.hover",
                      padding: 1,
                      borderRadius: 0.5,
                    }}
                  >
                    {typeof structuredOutput === 'string' ? structuredOutput.substring(0, 200) : ''}
                    {typeof structuredOutput === 'string' && structuredOutput.length > 200 && "..."}
                  </Box>
                  <Button
                    size="small"
                    onClick={() => setStructuredOutputDialogOpen(true)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      minWidth: "auto",
                      padding: "4px 8px",
                    }}
                  >
                    Edit
                  </Button>
                </Box>
              )}
            </Stack>
            </div>
            </Box>
            <Box
              id="merged-tabpanel-1"
              role="tabpanel"
              aria-labelledby="merged-tab-1"
              component="section"
              className={isSharedReadOnly ? styles.readOnlyLock : undefined}
              sx={{
                flex: 1,
                minHeight: 0,
                display: mergedColumnTab === 1 ? 'flex' : 'none',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
            <div
              className={`${styles.middlePanelInner}${
                showMergedColumnFloatingAction ? ` ${styles.middlePanelInnerWithFloatingAction}` : ""
              }`}
            >
              <div className={styles.middleFeaturesBody}>
                <div className={styles.searchContainer}>
                  <div className={styles.searchBox}>
                    <LucideSearch className={styles.searchLucideIcon} size={16} strokeWidth={2} aria-hidden />
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Search features..."
                      value={featureSearchQuery}
                      onChange={(e) => setFeatureSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className={styles.coreFeaturesBlock}>
                  <h2 className={styles.featuresTitle}>Core Features</h2>

                  <div className={styles.middleFeaturesStack}>
                    <MiddleFeatureCard
                      icon={Book}
                      title="Rules book (D1)"
                      description="Use your docs to ground answers and reduce hallucinations"
                      checked={features.knowledgeBase}
                      onToggle={() => handleFeatureToggle("knowledgeBase")}
                      disabled={isSharedResource}
                      switchTooltip={isSharedResource ? SHARED_KB_COPY.ownerOnlyToggle : undefined}
                      titleAdornment={isSharedResource ? ownerLockAdornment : undefined}
                      hide={!matchesFeatureSearch("rules book d1 knowledge docs hallucinations")}
                    >
                      {features.knowledgeBase && knowledgeBaseData && (
                        <Box className={styles.knowledgeBaseDisplay}>
                          <Box className={styles.kbContent}>
                            {/* A recipient gets a view-only dialog, so the button
                                stays live even in the locked read-share view. */}
                            <Box className={`${styles.kbLeft}${isSharedResource ? ` ${styles.readOnlyExempt}` : ""}`}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setKnowledgeBaseDialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                {isSharedResource ? "View" : "Configure"}
                              </Button>
                            </Box>

                            <Box className={styles.kbRight}>
                              {hasDim1Attached && (
                                <Tooltip
                                  title={
                                    isDim1Rulebook
                                      ? "A search enhancement is on, so D1 only guides how the agent searches. Answers come from D3 documents or datasets."
                                      : "D1 is searched and cited like any other knowledge base."
                                  }
                                >
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    color={isDim1Rulebook ? "warning" : "default"}
                                    label={isDim1Rulebook ? "D1: Rulebook only" : "D1: Answer source"}
                                    className={styles.dim1ModeBadge}
                                  />
                                </Tooltip>
                              )}

                              <Box className={styles.kbInfoBox}>
                                <Typography variant="body2" className={styles.kbName}>
                                  {knowledgeBaseData.ragType}
                                </Typography>
                              </Box>

                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {knowledgeBaseData.knowledgeBases.length}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Rules book
                                  {knowledgeBaseData.knowledgeBases.length !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={Database}
                      title="Customer Data (D3)"
                      description="Connect customer sources for personalized responses"
                      checked={features.knowledgeBaseD3}
                      onToggle={() => handleFeatureToggle("knowledgeBaseD3")}
                      disabled={isSharedResource}
                      switchTooltip={isSharedResource ? SHARED_KB_COPY.ownerOnlyToggle : undefined}
                      titleAdornment={
                        isSharedResource && myDim3.mode === "personal" && myDim3.canSelect
                          ? myDim3InfoAdornment
                          : undefined
                      }
                      hide={!matchesFeatureSearch("customer data d3 rag source personalized")}
                    >
                      {/* A recipient never sees or edits the agent's own D3 list
                          here — the card is bound to their own selection, and
                          stays usable inside the locked read-share view. */}
                      {isSharedResource && (myDim3.isRecipient || myDim3.loading) && (
                        <Box className={`${styles.knowledgeBaseDisplay} ${styles.readOnlyExempt}`}>
                          <MyDim3Status controller={myDim3} variant="card" />
                        </Box>
                      )}
                      {!isSharedResource && features.knowledgeBaseD3 && knowledgeBaseD3Data && (
                        <Box className={styles.knowledgeBaseDisplay}>
                          <Box className={styles.kbContent}>
                            <Box className={styles.kbLeft}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setKnowledgeBaseD3DialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                Configure
                              </Button>
                            </Box>

                            <Box className={styles.kbRight}>
                              <Box className={styles.kbInfoBox}>
                                <Typography variant="body2" className={styles.kbName}>
                                  {knowledgeBaseD3Data.ragType}
                                </Typography>
                              </Box>

                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {knowledgeBaseD3Data.knowledgeBases.length}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Source
                                  {knowledgeBaseD3Data.knowledgeBases.length !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    {/* Hidden entirely without the knowledge_graph permission —
                        the whole area is gated, not just the routes. */}
                    <MiddleFeatureCard
                      icon={LucideNetwork}
                      title="Knowledge Graph"
                      description="Answer “how many” and “which ones” exactly, over structured records"
                      checked={features.knowledgeGraph}
                      onToggle={() => handleFeatureToggle("knowledgeGraph")}
                      disabled={isSharedResource}
                      switchTooltip={isSharedResource ? SHARED_KB_COPY.ownerOnlyToggle : undefined}
                      titleAdornment={isSharedResource ? ownerLockAdornment : undefined}
                      hide={
                        !canUseKnowledgeGraph ||
                        !matchesFeatureSearch(
                          "knowledge graph kg dataset facets filter count exact structured records"
                        )
                      }
                    >
                      {features.knowledgeGraph && knowledgeGraphData && (
                        <Box className={styles.knowledgeBaseDisplay}>
                          <Box className={styles.kbContent}>
                            <Box className={styles.kbLeft}>
                              {/* The datasets live in the owner's D3 list. */}
                              {!isSharedResource && (
                                <Button
                                  variant="text"
                                  className={styles.configureButton}
                                  onClick={() => setKnowledgeGraphDialogOpen(true)}
                                  startIcon={<LucideSettings size={14} strokeWidth={2} />}
                                >
                                  Configure
                                </Button>
                              )}
                            </Box>

                            <Box className={styles.kbRight}>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {knowledgeGraphData.datasets?.length || 0}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Dataset
                                  {(knowledgeGraphData.datasets?.length || 0) !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={Radio}
                      title="Live Sources"
                      description="Let the agent look things up in a connected app while it answers"
                      checked={features.liveSources}
                      onToggle={() => handleFeatureToggle("liveSources")}
                      hide={
                        !isSingleAgent ||
                        !matchesFeatureSearch(
                          "live sources context provider drive gmail lookup realtime connected app"
                        )
                      }
                    >
                      {features.liveSources && contextProviders.length > 0 && (
                        <Box className={styles.knowledgeBaseDisplay}>
                          <Box className={styles.kbContent}>
                            <Box className={styles.kbLeft}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setLiveSourcesDialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                Configure
                              </Button>
                            </Box>

                            <Box className={styles.kbRight}>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {contextProviders.length}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Source{contextProviders.length !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={Brain}
                      title="Memory"
                      description="Keep conversation context across messages"
                      checked={features.memory}
                      onToggle={() => handleFeatureToggle("memory")}
                      hide={!matchesFeatureSearch("memory conversation context messages")}
                    >
                      {features.memory && memoryData && (
                        <Box className={styles.memoryDisplay}>
                          <Box className={styles.memoryContent}>
                            <Box className={styles.memoryLeft}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setMemoryDialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                Configure
                              </Button>
                            </Box>

                            <Box className={styles.memoryRight}>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {memoryData.maxMessages}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Messages
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={Zap}
                      title="Learning"
                      description="Configure safe, controlled learning for this agent"
                      checked={features.learning}
                      onToggle={() => handleFeatureToggle("learning")}
                      hide={!matchesFeatureSearch("learning stores configure records")}
                    >
                      {features.learning && (
                        <Box className={styles.memoryDisplay}>
                          <Box className={styles.memoryContent}>
                            <Box className={styles.memoryLeft}>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="text"
                                  className={styles.configureButton}
                                  onClick={() => setLearningDialogOpen(true)}
                                  startIcon={<LucideSettings size={14} strokeWidth={2} />}
                                >
                                  Configure
                                </Button>
                                <Button
                                  variant="text"
                                  className={styles.configureButton}
                                  onClick={handleLearningRecordsOpen}
                                  startIcon={<LucideSearch size={14} strokeWidth={2} />}
                                  disabled={!editingAgentId}
                                >
                                  View
                                </Button>
                              </Stack>
                            </Box>

                            <Box className={styles.memoryRight}>
                              <Box className={`${styles.kbInfoBox} ${styles.learningModePill}`}>
                                <Typography variant="body2" className={styles.featureStatBadge}>
                                  {learningModeLabel}
                                </Typography>
                              </Box>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {learningEnabledStoresCount}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Stores
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={Link2}
                      title="Agent Chaining"
                      description="Run multi-step workflows by chaining agents"
                      checked={features.agentChaining}
                      onToggle={() => handleFeatureToggle("agentChaining")}
                      hide={!matchesFeatureSearch("agent chaining workflow multi-step")}
                    >
                      {features.agentChaining && agentChainingData && (
                        <Box className={styles.agentChainingDisplay}>
                          <Box className={styles.agentChainingContent}>
                            <Box className={styles.agentChainingLeft}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setAgentChainingDialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                Configure
                              </Button>
                            </Box>

                            <Box className={styles.agentChainingRight}>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {(agentChainingData.selectedAgents || agentChainingData.selected_agents || []).length}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Agent
                                  {(agentChainingData.selectedAgents || agentChainingData.selected_agents || []).length !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={UsersRound}
                      title="Agent Teams"
                      description="Leader-led delegation across specialist agents"
                      checked={Boolean(agentTeamData)}
                      onToggle={() => handleFeatureToggle("agentTeam")}
                      disabled={!editingAgentId || savingAgentTeam}
                      hide={!matchesFeatureSearch("agent teams leader members coordinate route broadcast")}
                    >
                      {agentTeamData && (
                        <Box className={styles.agentChainingDisplay}>
                          <Box className={styles.agentChainingContent}>
                            <Box className={styles.agentChainingLeft}>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  variant="text"
                                  className={styles.configureButton}
                                  onClick={() => setAgentTeamDialogOpen(true)}
                                  startIcon={<LucideSettings size={14} strokeWidth={2} />}
                                >
                                  Configure
                                </Button>
                                <Button
                                  variant="text"
                                  className={styles.configureButton}
                                  onClick={handleAgentTeamDelete}
                                  disabled={savingAgentTeam}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            </Box>

                            <Box className={styles.agentChainingRight}>
                              <Box className={styles.kbInfoBox}>
                                <Typography variant="body2" className={styles.featureStatBadge}>
                                  {agentTeamData.team_mode || "coordinate"}
                                </Typography>
                              </Box>
                              <Box className={styles.kbChunksBox}>
                                <Typography variant="body2" className={styles.kbChunksNumber}>
                                  {agentTeamData.members?.length || 0}
                                </Typography>
                                <Typography variant="body2" className={styles.kbChunksLabel}>
                                  Member{(agentTeamData.members?.length || 0) !== 1 ? "s" : ""}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleFeatureCard
                      icon={UserRoundCheck}
                      title="Human-in-Loop"
                      description="Step-by-step execution with user approval"
                      checked={features.humanInLoop}
                      onToggle={() => handleFeatureToggle("humanInLoop")}
                      disabled={(!features.agentChaining || !agentChainingData) && !agentTeamData}
                      hide={!matchesFeatureSearch("human loop hitl approval")}
                    />

                    <MiddleFeatureCard
                      icon={CloudCog}
                      title="Background Run"
                      description="Execute workflow in background"
                      checked={features.backgroundRun}
                      onToggle={() => handleFeatureToggle("backgroundRun")}
                      disabled={
                        ((!features.agentChaining || !agentChainingData) &&
                          !agentTeamData &&
                          !features.checkpointing) ||
                        features.humanInLoop
                      }
                      hide={!matchesFeatureSearch("background run workflow async")}
                    />

                    <MiddleFeatureCard
                      icon={ShieldCheck}
                      title="Safe & Responsible AI"
                      description="Apply policy/guardrails to this agent"
                      checked={features.responsibleAi}
                      onToggle={() => handleFeatureToggle("responsibleAi")}
                      hide={!matchesFeatureSearch("responsible safe guardrails policy ai")}
                    >
                      {features.responsibleAi && (
                        <Box className={styles.agentChainingDisplay}>
                          <Box className={styles.agentChainingContent}>
                            <Box className={styles.agentChainingLeft}>
                              <Button
                                variant="text"
                                className={styles.configureButton}
                                onClick={() => setResponsibleAiDialogOpen(true)}
                                startIcon={<LucideSettings size={14} strokeWidth={2} />}
                              >
                                Configure
                              </Button>
                            </Box>

                            <Box className={styles.agentChainingRight}>
                              <Box className={styles.kbInfoBox}>
                                <Typography variant="body2" className={styles.featureStatBadge}>
                                  {Object.keys(responsibleAiConfig || {}).length > 0
                                    ? `${Object.keys(responsibleAiConfig || {}).length} keys`
                                    : "Default"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </MiddleFeatureCard>

                    <MiddleComingSoonCard
                      icon={Mic}
                      title="Voice Agent"
                      hide={!matchesFeatureSearch("voice agent")}
                    />

                    <MiddleComingSoonCard
                      icon={ScanSearch}
                      title="Hallucination Manager"
                      hide={!matchesFeatureSearch("hallucination manager")}
                    />
                  </div>
                </div>

                <div className={styles.coreFeaturesBlock}>
                  <h2 className={styles.featuresTitle}>
                    Search Enhancements (D1)
                    {hasDim1Attached && (
                      <Tooltip title="Turning on any of these makes D1 a rulebook (instructions only). Answers will come from D3 documents or datasets, not from D1.">
                        <Info className={styles.featuresTitleInfo} aria-label="How D1 is used" />
                      </Tooltip>
                    )}
                  </h2>

                  <div className={styles.middleFeaturesStack}>
                    {hasDim1Attached && (
                      <Alert
                        severity={isDim1Rulebook && !hasDim3AnswerSource ? "warning" : "info"}
                        sx={{ mb: 1 }}
                      >
                        <AlertTitle>How your D1 knowledge base is used</AlertTitle>
                        <Box
                          component="ul"
                          className={styles.dim1ModeList}
                        >
                          <li className={!isDim1Rulebook ? styles.dim1ModeActive : undefined}>
                            <strong>All three options off:</strong> D1 is treated like any other
                            knowledge base. The agent can search it and answer directly from it,
                            with D1 citations.
                          </li>
                          <li className={isDim1Rulebook ? styles.dim1ModeActive : undefined}>
                            <strong>Any option on:</strong> D1 becomes a rulebook. The agent
                            follows it to decide how to search and respond, but never answers
                            from it or cites it. Answers come only from your D3 documents or
                            datasets.
                          </li>
                        </Box>
                        {isDim1Rulebook && !hasDim3AnswerSource && (
                          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                            This agent has no D3 documents or dataset, so it will not be able to
                            answer questions from its knowledge base.
                          </Typography>
                        )}
                      </Alert>
                    )}

                    {!hasDim1Attached && (
                      <Alert
                        severity={hasQueryEnhancementEnabled ? "warning" : "info"}
                        action={
                          // Attaching a folder is the owner's to do on a shared agent.
                          isSharedResource ? undefined : (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => setKnowledgeBaseDialogOpen(true)}
                            >
                              Attach D1
                            </Button>
                          )
                        }
                        sx={{ mb: 1, alignItems: "center" }}
                      >
                        {hasQueryEnhancementEnabled
                          ? "These enhancements have no guidance to read until a Rules book (D1) folder is attached."
                          : "Attach a Rules book (D1) folder to use these. Knowledge Graph agents need one too."}
                      </Alert>
                    )}

                    <MiddleFeatureCard
                      icon={SearchCheck}
                      title="Expand search using D1"
                      description="Add relevant terms from your D1 guidance to improve search"
                      checked={features.queryExpansion}
                      onToggle={() => handleFeatureToggle("queryExpansion")}
                      disabled={!hasDim1Attached && !features.queryExpansion}
                      hide={
                        !matchesFeatureSearch(
                          "query expansion expand search d1 dim1 terms synonyms rag retrieval"
                        )
                      }
                    />

                    <MiddleFeatureCard
                      icon={Crosshair}
                      title="Use relevant context"
                      description="Use D1 guidance and the details you provide to make search more relevant"
                      checked={features.contextualRelevance}
                      onToggle={() => handleFeatureToggle("contextualRelevance")}
                      disabled={!hasDim1Attached && !features.contextualRelevance}
                      hide={
                        !matchesFeatureSearch(
                          "contextual relevance context d1 dim1 search rag kg semantic"
                        )
                      }
                    />

                    <MiddleFeatureCard
                      icon={MessageCircleQuestion}
                      title="Ask for missing details"
                      description="Ask a question when D1 guidance requires more information to answer"
                      checked={features.qualifyingQuestions}
                      onToggle={() => handleFeatureToggle("qualifyingQuestions")}
                      disabled={!hasDim1Attached && !features.qualifyingQuestions}
                      hide={
                        !matchesFeatureSearch(
                          "qualifying questions clarification missing details ask d1 dim1"
                        )
                      }
                    />
                  </div>
                </div>

                <div className={styles.coreFeaturesBlock}>
                  <h2 className={styles.featuresTitle}>Run Behaviour</h2>

                  <div className={styles.middleFeaturesStack}>
                    <MiddleFeatureCard
                      icon={Quote}
                      title="Citations & Sources"
                      description="Mark which documents each answer came from"
                      checked={features.showSources}
                      onToggle={() => handleFeatureToggle("showSources")}
                      hide={
                        !hasKnowledgeBaseAttached ||
                        !matchesFeatureSearch("citations sources references grounding show sources")
                      }
                    />

                    <MiddleFeatureCard
                      icon={MessageCircleQuestion}
                      title="Suggested Follow-ups"
                      description="Offer follow-up questions after each answer"
                      checked={features.followups}
                      onToggle={() => handleFeatureToggle("followups")}
                      hide={!matchesFeatureSearch("followups follow-up suggested questions next")}
                    />

                    <MiddleFeatureCard
                      icon={Gauge}
                      title="Prompt Caching"
                      description="Cheaper and faster repeat turns"
                      checked={features.promptCaching}
                      onToggle={() => handleFeatureToggle("promptCaching")}
                      hide={
                        !isAnthropicProvider ||
                        !matchesFeatureSearch("prompt caching anthropic cost speed cache")
                      }
                    />

                    <MiddleFeatureCard
                      icon={FileText}
                      title="Compress Tool Results"
                      description="Trim bulky tool output before it reaches the model"
                      checked={features.compressToolResults}
                      onToggle={() => handleFeatureToggle("compressToolResults")}
                      hide={
                        !features.tools ||
                        !matchesFeatureSearch("compress tool results trim output tokens")
                      }
                    />

                    <MiddleFeatureCard
                      icon={Shuffle}
                      title="Model Fallback"
                      description="Switch to a backup model if the primary one fails"
                      checked={features.modelFallback}
                      onToggle={() => handleFeatureToggle("modelFallback")}
                      hide={!matchesFeatureSearch("model fallback backup failover retry")}
                    />

                    <MiddleFeatureCard
                      icon={LucideHistory}
                      title="Checkpointing"
                      description={
                        features.memory
                          ? "Enables regenerate, edit & re-run, and background runs"
                          : "Needs Memory enabled first"
                      }
                      checked={features.checkpointing}
                      onToggle={() => handleFeatureToggle("checkpointing")}
                      disabled={!features.memory}
                      hide={
                        !matchesFeatureSearch(
                          "checkpointing regenerate rerun fork resume background run"
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
            </Box>

            {showMergedColumnFloatingAction && (
              <Box
                className={styles.mergedColumnFloatingBar}
                sx={{
                  pointerEvents: "none",
                  "& > *": { pointerEvents: "auto" },
                }}
              >
                {!isEditMode ? (
                  <Button
                    variant="contained"
                    size="large"
                    className={styles.mergedColumnFloatingButton}
                    onClick={handleCreateAgentClick}
                    disabled={isCreatingAgent || loadingAgent || isAutoSaving}
                    startIcon={isCreatingAgent ? <CircularProgress size={20} color="inherit" /> : <Rocket />}
                    sx={{
                      px: 3,
                      py: 1.25,
                      backgroundColor: "#e36250",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 8px 24px -4px rgba(227, 98, 80, 0.45), 0 4px 16px -4px rgba(0, 0, 0, 0.15)",
                      "&:hover": {
                        backgroundColor: "#d14e3c",
                        boxShadow: "0 10px 28px -4px rgba(227, 98, 80, 0.5), 0 6px 18px -4px rgba(0, 0, 0, 0.18)",
                      },
                    }}
                  >
                    {isCreatingAgent ? "Creating agent..." : isAutoSaving ? "Auto-saving..." : "Create agent"}
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      hasChanges && !isAutoSaving && !justAutoSaved
                        ? "You have unsaved changes — click to save"
                        : ""
                    }
                    arrow
                    placement="top"
                  >
                    <span>
                      <Button
                        variant="contained"
                        className={styles.mergedColumnFloatingButton}
                        startIcon={
                          isCreatingAgent ? <CircularProgress size={18} color="inherit" /> : <Edit />
                        }
                        onClick={handleCreateAgent}
                        disabled={isCreatingAgent || loadingAgent || isAutoSaving || !hasChanges || isSharedReadOnly}
                        sx={{
                          px: 3,
                          py: 1.25,
                          fontSize: "14px",
                          fontWeight: 700,
                          textTransform: "none",
                          backgroundColor: "#e36250",
                          color: "#ffffff",
                          position: "relative",
                          boxShadow:
                            "0 8px 24px -4px rgba(227, 98, 80, 0.45), 0 4px 16px -4px rgba(0, 0, 0, 0.15)",
                          "&:hover": {
                            backgroundColor: "#d14e3c",
                            boxShadow:
                              "0 10px 28px -4px rgba(227, 98, 80, 0.5), 0 6px 18px -4px rgba(0, 0, 0, 0.18)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: "rgba(227, 98, 80, 0.35)",
                            color: "rgba(255, 255, 255, 0.85)",
                          },
                          ...(hasChanges &&
                            !isAutoSaving && {
                              "&::before": {
                                content: '""',
                                position: "absolute",
                                top: -2,
                                right: -2,
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "warning.main",
                                animation: "pulse 2s infinite",
                              },
                            }),
                        }}
                      >
                        {isCreatingAgent
                          ? "Updating Agent..."
                          : isAutoSaving
                            ? "Auto-saving..."
                            : "Update Agent"}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )}
          </Paper>
          </Box>

          <div
            className={styles.columnGrip}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize configuration and test panel columns"
            tabIndex={0}
            onPointerDown={onColumnGripPointerDown("merged")}
          />

          {/* Right Panel - Conditional Content */}
          <Paper
            elevation={0}
            className={`${styles.rightPanel}${isEditMode && !showVersionHistory ? ` ${styles.rightPanelPlaygroundShell}` : ''}`}
          >
            <div className={styles.rightPanelBody}>
            {isEditMode ? (
              showVersionHistory ? (
                // Version History Panel
                <VersionHistoryPanel
                  versionHistory={versionHistory}
                  loading={loadingVersionHistory}
                  selectedVersion={selectedVersion}
                  onVersionSelect={handleVersionSelect}
                  onClose={handleVersionHistoryClose}
                  isViewingVersion={isViewingVersion}
                />
              ) : (
                <div className={styles.rightPanelPlaygroundInset}>
                  <AgentTestPlayground
                    variant="panel"
                    agentId={editingAgentId}
                    agent={agentForTestPlayground}
                    variables={extractedVariables}
                    title="Test Agent Interface"
                    containerSx={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%", maxWidth: "100%" }}
                    hasUnsavedChanges={hasChanges}
                    isAutoSaving={isAutoSaving || isCreatingAgent}
                    unauthenticatedTools={unauthenticatedComposioTools.map(t => ({
                      name: t.appName,
                      slug: t.toolkitSlug,
                    }))}
                    onAuthenticateTool={(toolInfo) => handleAuthTool(toolInfo)}
                    myDim3={myDim3}
                  />
                </div>
              )
            ) : (
              // Start Building Guide (Create Mode)
              <div className={styles.rightPanelScroll}>
              <Box
                sx={{
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Rocket sx={{ fontSize: 48, mb: 2, color: "primary.main" }} />
                <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
                  Start Building
                </Typography>

                <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
                  <Box sx={{ display: "flex", gap: 2, textAlign: "left" }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      1
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        Choose LLM & Define Role
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select your preferred large language model and define your
                        agent's purpose.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, textAlign: "left" }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      2
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        Add Tools (Optional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enhance your agent with external tools and capabilities.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", gap: 2, textAlign: "left" }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        color: "white",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: "bold",
                        flexShrink: 0,
                      }}
                    >
                      3
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 0.5 }}
                      >
                        Enable Features (Optional)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add advanced features to boost your agent's internal
                        powers.
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              </div>
            )}
            </div>
          </Paper>
        </Box>

        {/* Instructions Dialog */}
        <Dialog
          open={instructionsDialogOpen}
          onClose={() => setInstructionsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Agent Instructions</DialogTitle>
          <DialogContent>
            <TextField
              label="Agent Instructions"
              value={formData.agentInstructions}
              onChange={(e) =>
                handleInputChange("agentInstructions", e.target.value)
              }
              multiline
              rows={12}
              fullWidth
              variant="outlined"
              margin="normal"
              helperText={`${formData.agentInstructions.length} characters`}
            />

            {/* Display extracted variables in dialog */}
            {extractedVariables.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Detected Variables:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {extractedVariables.map((variable, index) => (
                    <Chip
                      key={index}
                      label={`{${variable}}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.75rem",
                        backgroundColor: isDarkMode ? "rgba(227, 98, 80, 0.1)" : "rgba(227, 98, 80, 0.05)",
                        borderColor: isDarkMode ? "rgba(227, 98, 80, 0.3)" : "rgba(227, 98, 80, 0.2)",
                        color: isDarkMode ? "#ffab91" : "#d84315",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "rgba(227, 98, 80, 0.2)" : "rgba(227, 98, 80, 0.1)",
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Display chained agent variables in dialog */}
            {chainedAgentVariables.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Chained Agent Variables:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {chainedAgentVariables
                    .filter(variable => !extractedVariables.includes(variable))
                    .map((variable, index) => (
                      <Chip
                        key={`chained-${index}`}
                        label={`{${variable}}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontSize: "0.75rem",
                          backgroundColor: isDarkMode ? "rgba(139, 92, 246, 0.1)" : "rgba(139, 92, 246, 0.05)",
                          borderColor: isDarkMode ? "rgba(139, 92, 246, 0.3)" : "rgba(139, 92, 246, 0.2)",
                          color: isDarkMode ? "#a78bfa" : "#6d28d9",
                          "&:hover": {
                            backgroundColor: isDarkMode ? "rgba(139, 92, 246, 0.2)" : "rgba(139, 92, 246, 0.1)",
                          }
                        }}
                      />
                    ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInstructionsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => setInstructionsDialogOpen(false)}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <PublishConfirmDialog
          open={publishDialogOpen}
          isDarkMode={isDarkMode}
          isPublishing={isPublishing}
          agentName={originalAgentData?.name || formData?.name || ""}
          onClose={handleClosePublishDialog}
          onConfirm={handleConfirmPublish}
        />

        <UnpublishConfirmDialog
          open={unpublishDialogOpen}
          isDarkMode={isDarkMode}
          isUnpublishing={isUnpublishing}
          agentName={originalAgentData?.name || formData?.name || ""}
          onClose={handleCloseUnpublishDialog}
          onConfirm={handleConfirmUnpublish}
        />

        <PublishAsTemplateConfirmDialog
          open={publishAsTemplateDialogOpen}
          isDarkMode={isDarkMode}
          isPublishing={isPublishingAsTemplate}
          agentName={originalAgentData?.name || formData?.name || ""}
          chainedAgentCount={
            originalAgentData?.agent_chaining?.selected_agents?.length || 0
          }
          publishedChainedCount={
            originalAgentData?.agent_chaining?.selected_agents?.filter(
              (agent) => agent.is_published_as_template
            )?.length || 0
          }
          onClose={handleClosePublishAsTemplateDialog}
          onConfirm={handleConfirmPublishAsTemplate}
        />

        <DeleteTemplateConfirmDialog
          open={deleteTemplateDialogOpen}
          isDarkMode={isDarkMode}
          isDeleting={isDeletingTemplate}
          agentName={originalAgentData?.name || formData?.name || ""}
          onClose={handleCloseDeleteTemplateDialog}
          onConfirm={handleConfirmDeleteTemplate}
        />

        <CreateWithoutKnowledgeConfirmDialog
          open={createWithoutKnowledgeDialogOpen}
          isDarkMode={isDarkMode}
          isCreatingAgent={isCreatingAgent}
          missingRulebook={!features.knowledgeBase}
          missingCustomerData={!features.knowledgeBaseD3}
          onClose={handleCloseCreateWithoutKnowledgeDialog}
          onConfirm={handleConfirmCreateWithoutKnowledge}
        />

        <Dim1RulebookConfirmDialog
          open={Boolean(pendingRulebookFeature)}
          isDarkMode={isDarkMode}
          hasAnswerSource={hasDim3AnswerSource}
          onClose={() => setPendingRulebookFeature(null)}
          onConfirm={handleConfirmDim1Rulebook}
        />

        {/* Models Dialog */}
        <Dialog
          open={modelsDialogOpen}
          onClose={() => setModelsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">Available Models</Typography>
            <Typography variant="body2" color="text.secondary">
              List of available models from different providers. Click column
              headers to sort.
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Cost</TableCell>
                    <TableCell>Speed</TableCell>
                    <TableCell>Context</TableCell>
                    <TableCell>Special</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading.models ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Loading models...
                      </TableCell>
                    </TableRow>
                  ) : error.models ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Error loading models: {error.models}
                      </TableCell>
                    </TableRow>
                  ) : models.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No models available
                      </TableCell>
                    </TableRow>
                  ) : (
                    models.map((model, index) => (
                      <TableRow key={index}>
                        <TableCell>{model.name || model.display_name || model.model_name}</TableCell>
                        <TableCell>{providers.find(p => p.id === formData.llmProvider)?.display_name || 'Unknown'}</TableCell>
                        <TableCell>{model.cost || '-'}</TableCell>
                        <TableCell>{model.speed || '-'}</TableCell>
                        <TableCell>{model.context || '-'}</TableCell>
                        <TableCell>{model.special || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModelsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Tool Dialog */}
        <AddToolDialog
          open={toolDialogOpen}
          onClose={handleToolSave}
          editTool={editingTool}
        />

        {/* Structured Output Dialog */}
        <StructuredOutputDialog
          open={structuredOutputDialogOpen}
          onClose={() => setStructuredOutputDialogOpen(false)}
          onSave={(jsonValue) => setStructuredOutput(jsonValue)}
          initialValue={structuredOutput}
        />

        {/* Rules book Dialog — view-only for a share recipient. The plain
            close matters: handleKnowledgeBaseClose resets the feature. */}
        <KnowledgeBaseDialog
          open={knowledgeBaseDialogOpen}
          onClose={isSharedResource ? () => setKnowledgeBaseDialogOpen(false) : handleKnowledgeBaseClose}
          onSave={handleKnowledgeBaseSave}
          initialData={knowledgeBaseData}
          readOnly={isSharedResource}
        />

        {/* Customer Data Dialog — the agent's own list, owner only */}
        <KnowledgeBaseD3Dialog
          open={knowledgeBaseD3DialogOpen && !isSharedResource}
          onClose={handleKnowledgeBaseD3Close}
          onSave={handleKnowledgeBaseD3Save}
          initialData={knowledgeBaseD3Data}
          hiddenFolderIds={(knowledgeGraphData?.datasets || []).map((d) => d.folder_id)}
        />

        {/* A recipient's own Customer Data folders (my-dim3) */}
        <MyDim3Dialog
          controller={myDim3}
          agentName={formData?.name}
          onNotify={showToast}
        />

        {/* Knowledge Graph Dialog */}
        <KnowledgeGraphDialog
          open={knowledgeGraphDialogOpen}
          onClose={handleKnowledgeGraphClose}
          onSave={handleKnowledgeGraphSave}
          initialData={knowledgeGraphData}
        />

        {/* Memory Dialog */}
        <MemoryDialog
          open={memoryDialogOpen}
          onClose={handleMemoryClose}
          onSave={handleMemorySave}
          initialData={memoryData}
        />

        <LiveSourcesDialog
          open={liveSourcesDialogOpen}
          onClose={handleLiveSourcesClose}
          onSave={handleLiveSourcesSave}
          initialProviders={contextProviders}
        />

        <LearningDialog
          open={learningDialogOpen}
          onClose={handleLearningClose}
          onSave={handleLearningSave}
          initialData={normalizeLearningConfig(learningConfig, features.learning)}
          modeOptions={LEARNING_MODE_OPTIONS}
        />

        <LearningRecordsDialog
          open={learningRecordsDialogOpen}
          onClose={() => setLearningRecordsDialogOpen(false)}
          agentId={editingAgentId}
        />

        {/* Responsible AI Dialog */}
        <ResponsibleAIDialog
          open={responsibleAiDialogOpen}
          onClose={() => setResponsibleAiDialogOpen(false)}
          onSave={(config) => {
            setResponsibleAiConfig(config || {});
            setFeatures((prev) => ({ ...prev, responsibleAi: true }));
          }}
          initialData={responsibleAiConfig}
        />

        {/* Context Dialog */}
        {/* <ContextDialog
          open={contextDialogOpen}
          onClose={handleContextClose}
          onSave={handleContextSave}
          initialData={contextData}
        /> */}

        {/* Agent Chaining Dialog */}
        <AgentChainingDialog
          open={agentChainingDialogOpen}
          onClose={handleAgentChainingClose}
          onSave={handleAgentChainingSave}
          initialData={agentChainingData}
          humanInLoopEnabled={Boolean(features?.humanInLoop)}
          currentAgentId={editingAgentId}
        />

        <AgentTeamDialog
          open={agentTeamDialogOpen}
          onClose={handleAgentTeamClose}
          onSave={handleAgentTeamSave}
          initialData={agentTeamData}
          leaderAgentId={editingAgentId}
          leaderName={originalAgentData?.name || formData?.name || ""}
          saving={savingAgentTeam}
        />

        {/* Agent API Dialog */}
        <AgentAPIDialog
          open={agentAPIDialogOpen}
          onClose={() => setAgentAPIDialogOpen(false)}
          agentData={originalAgentData}
          agentId={editingAgentId}
        />

        <ResourceShareDialog
          open={resourceShareOpen}
          resource={editingAgentId ? { type: 'agent', id: editingAgentId, name: originalAgentData?.name || formData?.name } : null}
          onClose={() => setResourceShareOpen(false)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            showToast(warnings.length ? `Agent shared. ${warnings.join(' ')}` : 'Agent shared successfully.', warnings.length ? 'warning' : 'success');
          }}
        />
        <ManageSharingDialog
          open={manageResourceSharingOpen}
          resource={editingAgentId ? { type: 'agent', id: editingAgentId, name: originalAgentData?.name || formData?.name } : null}
          onClose={() => setManageResourceSharingOpen(false)}
        />

        {/* Settings Popover */}
        <Popover
          open={Boolean(settingsAnchor)}
          anchorEl={settingsAnchor}
          onClose={() => setSettingsAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 3, minWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body2">Temperature</Typography>
                <InfoTooltip
                  title="Temperature Guide"
                  maxWidth={1500}
                  placement="right"
                  content={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                        <strong>What it does:</strong> Controls how creative and random the AI responses are
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                        🌡️ Low Temperature (0.0 - 0.3):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> AI gives very focused, predictable answers
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "What is 2+2?" → Always answers "4" (same response every time)
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> Math problems, factual questions, technical documentation
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                        🌡️ Medium Temperature (0.4 - 0.7):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> Balanced creativity and consistency
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "Write a story about a cat" → Creative but coherent stories
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> General conversations, creative writing, brainstorming
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                        🌡️ High Temperature (0.8 - 1.0):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> Very creative, sometimes unpredictable responses
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "Write a story about a cat" → Wild, imaginative, sometimes weird stories
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> Creative writing, poetry, experimental ideas
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                        💡 Quick Tips:
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                        • Start with 0.7 for most tasks
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                        • Lower for factual/technical work
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                        • Higher for creative tasks
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton size="small" sx={{ ml: 1, color: "#e36250" }}>
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </InfoTooltip>
                <Typography variant="body2" sx={{ ml: "auto" }}>
                  {temperature}
                </Typography>
              </Box>
              <Slider
                value={temperature}
                onChange={(e, value) => {
                  // Store initial value on first change
                  if (fieldValueOnFocus.current === null) {
                    fieldValueOnFocus.current = { field: 'temperature', value: temperature };
                  }
                  setTemperature(value);
                }}
                onChangeCommitted={(e, value) => {
                  // Check if value actually changed
                  if (fieldValueOnFocus.current && fieldValueOnFocus.current.value !== value && hasChanges) {
                    autoSave();
                  }
                  fieldValueOnFocus.current = null;
                }}
                min={0}
                max={1}
                step={0.1}
                valueLabelDisplay="auto"
              />
            </Box>

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body2">Top P</Typography>
                <InfoTooltip
                  title="Top P Guide"
                  maxWidth={1500}
                  placement="right"
                  content={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 2, color: "rgba(255,255,255,0.9)" }}>
                        <strong>What it does:</strong> Controls how many different word choices the AI considers when writing
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#4caf50", fontWeight: 600 }}>
                        🎯 Low Top P (0.1 - 0.3):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> AI only considers the most likely words
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "The weather is..." → Always says "sunny" or "rainy" (very predictable)
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> Technical writing, formal documents, consistent responses
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#ff9800", fontWeight: 600 }}>
                        🎯 Medium Top P (0.4 - 0.7):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> AI considers a good variety of word choices
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "The weather is..." → Says "beautiful", "pleasant", "gloomy", etc. (varied but sensible)
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> General conversations, creative writing, balanced responses
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#f44336", fontWeight: 600 }}>
                        🎯 High Top P (0.8 - 1.0):
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>What happens:</strong> AI considers many different word choices, including unusual ones
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 1 }}>
                        <strong>Example:</strong> "The weather is..." → Says "magnificent", "ethereal", "mysterious" (creative and varied)
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 2 }}>
                        <strong>Best for:</strong> Creative writing, poetry, experimental text, diverse vocabulary
                      </Typography>

                      <Typography variant="body2" sx={{ mb: 1, color: "#2196f3", fontWeight: 600 }}>
                        💡 Quick Tips:
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                        • Start with 0.9 for most tasks
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                        • Lower for consistent, formal writing
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mb: 0.5 }}>
                        • Higher for creative, varied writing
                      </Typography>
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block" }}>
                        • Works together with Temperature for fine-tuning
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton size="small" sx={{ ml: 1, color: "#e36250" }}>
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </InfoTooltip>
                <Typography variant="body2" sx={{ ml: "auto" }}>
                  {topP}
                </Typography>
              </Box>
              <Slider
                value={topP}
                onChange={(e, value) => {
                  // Store initial value on first change
                  if (fieldValueOnFocus.current === null) {
                    fieldValueOnFocus.current = { field: 'topP', value: topP };
                  }
                  setTopP(value);
                }}
                onChangeCommitted={(e, value) => {
                  // Check if value actually changed
                  if (fieldValueOnFocus.current && fieldValueOnFocus.current.value !== value && hasChanges) {
                    autoSave();
                  }
                  fieldValueOnFocus.current = null;
                }}
                min={0}
                max={1}
                step={0.1}
                valueLabelDisplay="auto"
              />
            </Box>
          </Box>
        </Popover>

        <AgentConflictDialog
          open={!!saveConflict?.dialogOpen}
          message={saveConflict?.message || AGENT_CONFLICT_FALLBACK}
          isDarkMode={isDarkMode}
          reloading={conflictReloading}
          onReload={handleConflictReload}
          onKeepEditing={handleConflictKeepEditing}
        />

        {/* Toast Notifications */}
        <Snackbar
          open={toast.open}
          autoHideDuration={6000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleToastClose}
            severity={toast.severity}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default AgentCreate;
